import { supportsNormalizedTemplate } from '../../helpers/patterns/patternCoordinates';
import React from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    DatePicker,
    Descriptions,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Slider,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    ArrowDownOutlined,
    ArrowLeftOutlined,
    ArrowUpOutlined,
    DeleteOutlined,
    ExpandOutlined,
    PlusOutlined,
    SaveOutlined,
    UndoOutlined,
    UploadOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
} from '@ant-design/icons';
import AreaMap from '../Map/AreaMap';
import PatternPointMapCard from '../PatternPointMapCard';
import ModuleConfigEditor from '../Configuration/ModuleConfigEditor';
import CommandExecutionGrid from './CommandExecutionGrid';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import {
    ACTION_OPTIONS,
    formatStatusLabel,
    MISSION_COMMAND_KIND_OPTIONS,
    MISSION_COMMAND_NAME_OPTIONS,
    MISSION_STATUS_OPTIONS,
    MISSION_TYPE_OPTIONS,
    OPERATION_ENVIRONMENT_OPTIONS,
    type OperationEnvironment,
    type AreaItem,
    type DeviceItem,
    type DeviceModuleItem,
    type DubinsCostMode,
    type MapPath,
    type MissionCommandDraft,
    type MissionCommandItem,
    type MissionImportPreview,
    type MissionEditorMode,
    type MissionFormValues,
    type MissionItem,
    type MissionNavigationMode,
    type LocalPose,
    type MissionPointDraft,
    type MissionPointItem,
    type MissionSavePayload,
    type ObstaclePolicyMode,
    type PatternItem,
    type PatternPointItem,
    type ScanMode,
} from './missionModels';
import { buildAreaPathMap, collectAreaSubtree } from './missionAreaUtils';
import { getAreasForOperationsMap, getGpsGeometryForOperationsMap } from './missionAreaPreviewGeometry';
import { getCommandDisplayName, resolveCommand, getFilteredCommands, getProtocolFilteredCommands } from './missionCommandCatalogue';
import { getMissionPointMarkerLabel } from './missionPointLabels';
import { projectPatternPointToGps } from './missionProjection';
import { normalizeMissionRouteConfig, resolveMissionPointDirections, resolveMissionPointDirectionsFromXY } from './missionRouting';
import { buildMissionPreviewPathsDubins, buildMissionPreviewPathsDubinsFromXY, type LocalMapPath, type LocalRouteControlPoint } from './missionRoutingDubins';
import { isPathInsideArea } from './missionAreaPathValidation';
import {
    createMissionPatternDraftKey,
    writeMissionPatternHandoff,
    type MissionPatternHandoff,
} from './missionPatternHandoff';

const { Text, Title } = Typography;

const getDeviceCapabilityKeys = (device?: DeviceItem | null): Set<string> => {
    const keys = new Set<string>();
    const addCapabilities = (capabilities?: DeviceItem['effectiveCapabilities'] | null) => {
        (capabilities || []).forEach((capability) => {
            if (capability.enabled === false) {
                return;
            }
            const key = capability.key || capability.definition?.key || capability.capabilityDefinition?.key;
            if (key) {
                keys.add(key);
            }
        });
    };
    addCapabilities(device?.deviceType?.capabilities);
    addCapabilities(device?.effectiveCapabilities);
    return keys;
};

const requiredCapabilitiesForEnvironment = (environment?: OperationEnvironment | null): string[] => {
    if (environment === 'scan') return ['slam_map_creation'];
    if (environment === 'indoor') return ['indoor_navigation', 'slam_map_creation'];
    if (environment === 'mixed') return ['indoor_navigation', 'slam_map_creation', 'apriltag_localization'];
    if (environment === 'outdoor') return ['gps_navigation'];
    return [];
};

const capabilityLabelByKey: Record<string, string> = {
    gps_navigation: 'GPS navigation',
    indoor_navigation: 'indoor navigation',
    slam_map_creation: 'SLAM map creation',
    apriltag_localization: 'AprilTag localization',
};

const formatCapabilityList = (keys: string[]): string => (
    keys.map((key) => capabilityLabelByKey[key] || key).join(', ')
);

const formatValidationErrors = (value: unknown): string | null => {
    if (!Array.isArray(value) || value.length === 0) {
        return null;
    }

    const messages = value
        .flatMap((item) => {
            if (!item || typeof item !== 'object') {
                return [];
            }

            const candidate = item as {
                property?: unknown;
                constraints?: Record<string, string> | null;
                children?: unknown[];
            };
            const constraintMessages = candidate.constraints
                ? Object.values(candidate.constraints)
                : [];
            const childMessages = formatValidationErrors(candidate.children);

            return [
                ...constraintMessages,
                ...(childMessages ? [childMessages] : []),
            ];
        })
        .map((message) => String(message).trim())
        .filter(Boolean);

    return messages.length > 0 ? messages.join(' ') : null;
};

const getErrorMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error)) {
        const responseData = error.response?.data;
        if (typeof responseData === 'string' && responseData.trim()) {
            return responseData.trim();
        }

        const validationMessage = formatValidationErrors(responseData);
        if (validationMessage) {
            return validationMessage;
        }

        return (
            (responseData as any)?.message
            || (responseData as any)?.error
            || error.message
            || fallback
        );
    }

    if (error instanceof Error) {
        return error.message;
    }

    return fallback;
};

const parseApiList = <T,>(payload: any): T[] => {
    if (Array.isArray(payload?.data)) {
        return payload.data as T[];
    }

    if (Array.isArray(payload)) {
        return payload as T[];
    }

    return [];
};

const calculatePathDistanceMeters = (coordinates: Array<{ lat: number; lng: number }>) => {
    let distanceMeters = 0;

    for (let index = 1; index < coordinates.length; index += 1) {
        const previous = coordinates[index - 1];
        const current = coordinates[index];
        const earthRadiusMeters = 6371000;
        const dLat = (current.lat - previous.lat) * Math.PI / 180;
        const dLng = (current.lng - previous.lng) * Math.PI / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(previous.lat * Math.PI / 180) * Math.cos(current.lat * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        distanceMeters += earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    return distanceMeters;
};

const maxStraightLineDeviationMeters = (coordinates: Array<{ lat: number; lng: number }>) => {
    if (coordinates.length < 3) return 0;
    const origin = coordinates[0];
    const meanLatRadians = coordinates.reduce((sum, point) => sum + point.lat, 0)
        / coordinates.length * Math.PI / 180;
    const toLocal = (point: { lat: number; lng: number }) => ({
        x: (point.lng - origin.lng) * Math.PI / 180 * EARTH_RADIUS_METERS * Math.cos(meanLatRadians),
        y: (point.lat - origin.lat) * Math.PI / 180 * EARTH_RADIUS_METERS,
    });
    const start = toLocal(coordinates[0]);
    const end = toLocal(coordinates[coordinates.length - 1]);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const length = Math.hypot(dx, dy);
    if (length < 0.05) return Number.POSITIVE_INFINITY;
    return coordinates
        .slice(1, -1)
        .reduce((maximum, point) => {
            const local = toLocal(point);
            const deviation = Math.abs(
                dy * local.x - dx * local.y + end.x * start.y - end.y * start.x
            ) / length;
            return Math.max(maximum, deviation);
        }, 0);
};

const calculateLocalPathDistanceMeters = (coordinates: Array<{ x: number; y: number }>) => {
    let distanceMeters = 0;
    for (let index = 1; index < coordinates.length; index += 1) {
        const previous = coordinates[index - 1];
        const current = coordinates[index];
        distanceMeters += Math.hypot(current.x - previous.x, current.y - previous.y);
    }
    return distanceMeters;
};

const computeGeometryCenter = (geom: any): { lat: number; lng: number } | null => {
    if (!geom?.coordinates) {
        return null;
    }

    const coords = geom.type === 'Polygon'
        ? geom.coordinates[0]
        : geom.type === 'MultiPolygon'
            ? geom.coordinates[0]?.[0]
            : null;

    if (!Array.isArray(coords) || coords.length === 0) {
        return null;
    }

    const validCoords = coords.filter((coordinate: any) => (
        Array.isArray(coordinate)
        && Number.isFinite(Number(coordinate[0]))
        && Number.isFinite(Number(coordinate[1]))
    ));

    if (!validCoords.length) {
        return null;
    }

    const sum = validCoords.reduce(
        (accumulator: { lng: number; lat: number }, coordinate: [number, number]) => ({
            lng: accumulator.lng + Number(coordinate[0]),
            lat: accumulator.lat + Number(coordinate[1]),
        }),
        { lng: 0, lat: 0 }
    );

    return {
        lng: sum.lng / validCoords.length,
        lat: sum.lat / validCoords.length,
    };
};

type WorkflowStep = 'area' | 'slam_map' | 'device' | 'modules' | 'pattern' | 'details';

type ModuleExecutionMode = 'series' | 'parallel';

type OscarMissionModuleSetting = {
    moduleKey: string;
    instanceKey?: string | null;
    enabled: boolean;
    label: string;
    moduleDefinitionId?: number | null;
    moduleDefinitionKey?: string | null;
    commandName?: string | null;
    commandSpecKey?: string | null;
    config: Record<string, any>;
};

type MissionRoutePreviewMode = 'local' | 'mapbox';

type RouteEditorSnapshot = {
    points: MissionPointDraft[];
    commands: MissionCommandDraft[];
    activePointKey: string | null;
    activeCommandKey: string | null;
};

type SlamPlacement = {
    lat: number;
    lng: number;
    bearingDegrees: number;
};

type AreaSelectionRow = AreaItem & {
    key: number;
    children?: AreaSelectionRow[];
    hierarchyDepth: number;
};

type PatternPreviewPoint = {
    id: number;
    pointIndex: number;
    x: number;
    y: number;
};

const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) {
        return null;
    }

    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const OBSTACLE_POLICY_OPTIONS: Array<{
    value: ObstaclePolicyMode;
    label: string;
    description: string;
}> = [
    {
        value: 'assist',
        label: 'Stop and resume when clear',
        description: 'Stop when the route is blocked, keep checking it, and continue automatically once it is clear.',
    },
    {
        value: 'avoid',
        label: 'Avoid automatically',
        description: 'Keep trying to reroute within the configured limits. Stop and report a failure if avoidance is unsuccessful.',
    },
    {
        value: 'stop',
        label: 'Stop immediately for operator',
        description: 'Do not attempt a detour. Stop immediately and wait for an operator to intervene and resume the mission.',
    },
];

const SCAN_MODE_OPTIONS: Array<{ value: ScanMode; label: string }> = [
    { value: 'autonomous', label: 'Autonomous exploration' },
    { value: 'manual', label: 'Manual drive' },
];

const NAVIGATION_MODE_OPTIONS: Array<{ value: MissionNavigationMode; label: string }> = [
    { value: 'route_following', label: 'Follow planned route' },
    { value: 'waypoint_goals', label: 'Waypoint pose goals only' },
];

const normalizeObstaclePolicyMode = (value: unknown): ObstaclePolicyMode => {
    const mode = String(value || '').trim().toLowerCase();
    if (mode === 'avoid' || mode === 'assist' || mode === 'stop') {
        return mode;
    }
    return 'assist';
};

const normalizeScanMode = (value: unknown): ScanMode => {
    const mode = String(value || '').trim().toLowerCase();
    return mode === 'manual' ? 'manual' : 'autonomous';
};

const EARTH_RADIUS_METERS = 6371000;
const DEG_TO_RAD = Math.PI / 180;

const localMetersToGps = (
    xMeters: number,
    yMeters: number,
    anchor: SlamPlacement
): { lat: number; lng: number } => {
    const bearing = anchor.bearingDegrees * DEG_TO_RAD;
    const northMeters = yMeters * Math.cos(bearing) - xMeters * Math.sin(bearing);
    const eastMeters = yMeters * Math.sin(bearing) + xMeters * Math.cos(bearing);

    return {
        lat: anchor.lat + (northMeters / EARTH_RADIUS_METERS) * (180 / Math.PI),
        lng: anchor.lng + (eastMeters / (EARTH_RADIUS_METERS * Math.cos(anchor.lat * DEG_TO_RAD))) * (180 / Math.PI),
    };
};

const gpsToLocalMeters = (
    lat: number,
    lng: number,
    anchor: SlamPlacement
): { x: number; y: number } => {
    const dlat = (lat - anchor.lat) * DEG_TO_RAD * EARTH_RADIUS_METERS;
    const dlng = (lng - anchor.lng) * DEG_TO_RAD * EARTH_RADIUS_METERS * Math.cos(anchor.lat * DEG_TO_RAD);
    const bearing = anchor.bearingDegrees * DEG_TO_RAD;
    return {
        y: dlat * Math.cos(bearing) + dlng * Math.sin(bearing),
        x: -dlat * Math.sin(bearing) + dlng * Math.cos(bearing),
    };
};

const readSlamPlacement = (area?: AreaItem | null): SlamPlacement | null => {
    const mapAnchor = area?.slamMap?.geoAnchor;
    if (mapAnchor) {
        const lat = toFiniteNumber(mapAnchor.lat);
        const lng = toFiniteNumber(mapAnchor.lng);
        const bearingDegrees = toFiniteNumber(mapAnchor.bearingDegrees);
        if (lat !== null && lng !== null && bearingDegrees !== null) {
            return { lat, lng, bearingDegrees };
        }
    }

    const saved = area?.configurations?.slamPlacement;
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
        const lat = toFiniteNumber((saved as Record<string, unknown>).lat);
        const lng = toFiniteNumber((saved as Record<string, unknown>).lng);
        const bearingDegrees = toFiniteNumber((saved as Record<string, unknown>).bearingDegrees);
        if (lat !== null && lng !== null && bearingDegrees !== null) {
            return { lat, lng, bearingDegrees };
        }
    }

    return null;
};

const extractLocalQuad = (geom: any): Array<{ x: number; y: number }> | null => {
    if (!geom) {
        return null;
    }
    const coords = geom.type === 'Polygon'
        ? geom.coordinates?.[0]
        : geom.type === 'MultiPolygon'
            ? geom.coordinates?.[0]?.[0]
            : null;
    if (!Array.isArray(coords) || coords.length < 4) {
        return null;
    }
    const first = coords[0];
    const last = coords[coords.length - 1];
    const ring = Array.isArray(first) && Array.isArray(last) && first[0] === last[0] && first[1] === last[1]
        ? coords.slice(0, -1)
        : coords;
    if (ring.length !== 4) {
        return null;
    }
    const quad = ring.map((point: any) => ({
        x: toFiniteNumber(point?.[0]) ?? NaN,
        y: toFiniteNumber(point?.[1]) ?? NaN,
    }));
    return quad.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y)) ? null : quad;
};

const extractGpsQuadAsLocal = (geom: any, placement: SlamPlacement | null): Array<{ x: number; y: number }> | null => {
    if (!placement) {
        return null;
    }
    const coords = geom?.type === 'Polygon'
        ? geom.coordinates?.[0]
        : geom?.type === 'MultiPolygon'
            ? geom.coordinates?.[0]?.[0]
            : null;
    if (!Array.isArray(coords) || coords.length < 4) {
        return null;
    }
    const first = coords[0];
    const last = coords[coords.length - 1];
    const ring = Array.isArray(first) && Array.isArray(last) && first[0] === last[0] && first[1] === last[1]
        ? coords.slice(0, -1)
        : coords;
    if (ring.length !== 4) {
        return null;
    }
    const localQuad = ring.map((point: any) => {
        const lng = toFiniteNumber(point?.[0]);
        const lat = toFiniteNumber(point?.[1]);
        return lat !== null && lng !== null ? gpsToLocalMeters(lat, lng, placement) : null;
    });
    return localQuad.some((point) => point === null) ? null : localQuad as Array<{ x: number; y: number }>;
};

const solveHomographySystem = (matrix: number[][], vector: number[]): number[] | null => {
    const size = vector.length;
    for (let column = 0; column < size; column += 1) {
        let pivotRow = column;
        let max = Math.abs(matrix[column][column]);
        for (let row = column + 1; row < size; row += 1) {
            const value = Math.abs(matrix[row][column]);
            if (value > max) {
                max = value;
                pivotRow = row;
            }
        }
        if (max === 0 || !Number.isFinite(max)) {
            return null;
        }
        if (pivotRow !== column) {
            [matrix[pivotRow], matrix[column]] = [matrix[column], matrix[pivotRow]];
            [vector[pivotRow], vector[column]] = [vector[column], vector[pivotRow]];
        }
        const pivot = matrix[column][column];
        for (let row = column + 1; row < size; row += 1) {
            const factor = matrix[row][column] / pivot;
            if (!Number.isFinite(factor)) {
                continue;
            }
            for (let index = column; index < size; index += 1) {
                matrix[row][index] -= factor * matrix[column][index];
            }
            vector[row] -= factor * vector[column];
        }
    }

    const result = new Array<number>(size);
    for (let row = size - 1; row >= 0; row -= 1) {
        let sum = vector[row];
        for (let column = row + 1; column < size; column += 1) {
            sum -= matrix[row][column] * result[column];
        }
        const denominator = matrix[row][row];
        if (denominator === 0 || !Number.isFinite(denominator)) {
            return null;
        }
        result[row] = sum / denominator;
    }
    return result;
};

const computeLocalHomography = (
    destination: Array<{ x: number; y: number }>
): number[] | null => {
    const source = [
        { x: 0, y: 0 },
        { x: 1, y: 0 },
        { x: 1, y: 1 },
        { x: 0, y: 1 },
    ];
    const matrix: number[][] = [];
    const vector: number[] = [];
    source.forEach(({ x, y }, index) => {
        const { x: u, y: v } = destination[index];
        matrix.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
        vector.push(u);
        matrix.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
        vector.push(v);
    });
    return solveHomographySystem(matrix, vector);
};

const applyLocalHomography = (matrix: number[], x: number, y: number): { x: number; y: number } | null => {
    const [h11, h12, h13, h21, h22, h23, h31, h32] = matrix;
    const denominator = h31 * x + h32 * y + 1;
    if (denominator === 0 || !Number.isFinite(denominator)) {
        return null;
    }
    const projectedX = (h11 * x + h12 * y + h13) / denominator;
    const projectedY = (h21 * x + h22 * y + h23) / denominator;
    return Number.isFinite(projectedX) && Number.isFinite(projectedY)
        ? { x: projectedX, y: projectedY }
        : null;
};

const normalizePatternPointForWarp = (
    point: PatternPointItem,
    pattern: PatternItem | null,
    area?: AreaItem | null
): { x: number; y: number } | null => {
    const xLocal = toFiniteNumber(point.xLocal);
    const yLocal = toFiniteNumber(point.yLocal);
    if (xLocal === null || yLocal === null) {
        return null;
    }
    if (xLocal >= 0 && xLocal <= 1 && yLocal >= 0 && yLocal <= 1) {
        return { x: xLocal, y: yLocal };
    }
    const widthMeters = toFiniteNumber(pattern?.definition?.boxWidth)
        ?? toFiniteNumber(area?.areaType?.templateDimensions?.widthMeters);
    const heightMeters = toFiniteNumber(pattern?.definition?.boxHeight)
        ?? toFiniteNumber(area?.areaType?.templateDimensions?.heightMeters);
    if (!widthMeters || !heightMeters || widthMeters <= 0 || heightMeters <= 0) {
        return null;
    }
    return {
        x: xLocal / widthMeters,
        y: yLocal / heightMeters,
    };
};

const resolvePatternPointLocalForMission = (
    point: PatternPointItem,
    pattern: PatternItem | null,
    area?: AreaItem | null,
    placement?: SlamPlacement | null
): { x: number; y: number; mode: 'area_warped' | 'gps_slam_fallback' } | null => {
    if (!area) {
        return null;
    }
    const normalized = normalizePatternPointForWarp(point, pattern, area);
    const explicitLocalQuad = extractLocalQuad(area.configurations?.localGeom);
    const fallbackLocalQuad = explicitLocalQuad ? null : extractGpsQuadAsLocal(area.geom, placement || null);
    const localQuad = explicitLocalQuad || fallbackLocalQuad;
    if (!normalized || !localQuad) {
        return null;
    }
    const matrix = computeLocalHomography(localQuad);
    const warped = matrix ? applyLocalHomography(matrix, normalized.x, normalized.y) : null;
    return warped ? { ...warped, mode: explicitLocalQuad ? 'area_warped' : 'gps_slam_fallback' } : null;
};

const localMetersToSlamPixel = (
    xMeters: number,
    yMeters: number,
    slamMap?: AreaItem['slamMap'] | null
): { x: number; y: number } | null => {
    if (!slamMap) {
        return null;
    }
    const metersPerPixel = toFiniteNumber(slamMap.metersPerPixel) ?? 0.05;
    const width = toFiniteNumber(slamMap.imageWidth) ?? 800;
    const height = toFiniteNumber(slamMap.imageHeight) ?? 600;
    const anchorPixelX = toFiniteNumber(slamMap.geoAnchor?.anchorPixelX) ?? width / 2;
    const anchorPixelY = toFiniteNumber(slamMap.geoAnchor?.anchorPixelY) ?? height / 2;

    if (!metersPerPixel || metersPerPixel <= 0) {
        return null;
    }

    return {
        x: anchorPixelX + xMeters / metersPerPixel,
        y: anchorPixelY - yMeters / metersPerPixel,
    };
};

const slamPixelToLocalMeters = (
    pixelX: number,
    pixelY: number,
    slamMap?: AreaItem['slamMap'] | null
): { x: number; y: number } | null => {
    if (!slamMap) {
        return null;
    }
    const metersPerPixel = toFiniteNumber(slamMap.metersPerPixel) ?? 0.05;
    const width = toFiniteNumber(slamMap.imageWidth) ?? 800;
    const height = toFiniteNumber(slamMap.imageHeight) ?? 600;
    const anchorPixelX = toFiniteNumber(slamMap.geoAnchor?.anchorPixelX) ?? width / 2;
    const anchorPixelY = toFiniteNumber(slamMap.geoAnchor?.anchorPixelY) ?? height / 2;

    if (!metersPerPixel || metersPerPixel <= 0) {
        return null;
    }

    return {
        x: (pixelX - anchorPixelX) * metersPerPixel,
        y: (anchorPixelY - pixelY) * metersPerPixel,
    };
};

const readDraftPointLocalCoordinates = (
    point: MissionPointDraft,
    placement: SlamPlacement | null
): { x: number; y: number } | null => {
    const poseX = toFiniteNumber(point.localPose?.x);
    const poseY = toFiniteNumber(point.localPose?.y);
    if (poseX !== null && poseY !== null) {
        return { x: poseX, y: poseY };
    }

    const local = point.params?.local;
    const x = toFiniteNumber(local?.x);
    const y = toFiniteNumber(local?.y);
    if (x !== null && y !== null) {
        return { x, y };
    }

    const lat = toFiniteNumber(point.lat);
    const lng = toFiniteNumber(point.lng);
    if (lat !== null && lng !== null && placement) {
        return gpsToLocalMeters(lat, lng, placement);
    }

    return null;
};

const buildLocalPose = (
    local: { x: number; y: number },
    existing: LocalPose | null | undefined,
    source: string,
    slamMapId?: number | null,
    extras: Record<string, any> = {}
): LocalPose => ({
    coordinateSystem: existing?.coordinateSystem || 'slam_local',
    frameId: existing?.frameId || 'map',
    ...(existing || {}),
    ...extras,
    x: local.x,
    y: local.y,
    source: existing?.source || source,
    slamMapId: existing?.slamMapId ?? slamMapId ?? null,
});

const buildPointWithLocalCoordinates = (
    point: MissionPointDraft,
    local: { x: number; y: number },
    placement: SlamPlacement | null,
    slamMapId?: number | null
): MissionPointDraft => {
    const gps = placement ? localMetersToGps(local.x, local.y, placement) : null;
    const localPose = buildLocalPose(local, point.localPose, 'slam_local', slamMapId);
    return {
        ...point,
        lat: gps?.lat ?? point.lat ?? null,
        lng: gps?.lng ?? point.lng ?? null,
        localPose,
        params: {
            ...(point.params || {}),
            local: {
                ...(point.params?.local || {}),
                ...localPose,
            },
        },
    };
};

const normalizePatternLocalCoordinate = (value: unknown, maxDimension: unknown): number | null => {
    const parsed = toFiniteNumber(value);
    if (parsed === null) {
        return null;
    }

    if (parsed >= 0 && parsed <= 1) {
        return parsed;
    }

    const dimension = toFiniteNumber(maxDimension);
    if (!dimension || dimension <= 0) {
        return null;
    }

    return Math.min(1, Math.max(0, parsed / dimension));
};

const buildAreaSelectionTree = (items: AreaItem[]): AreaSelectionRow[] => {
    const itemMap = new Map<number, AreaSelectionRow>();
    items.forEach((item) => {
        itemMap.set(item.id, {
            ...item,
            key: item.id,
            children: [],
            hierarchyDepth: 0,
        });
    });

    const roots: AreaSelectionRow[] = [];
    itemMap.forEach((item) => {
        const parentId = item.parentAreaId ?? null;
        if (parentId && itemMap.has(parentId)) {
            const parent = itemMap.get(parentId);
            if (parent) {
                parent.children = [...(parent.children || []), item];
            }
            return;
        }

        roots.push(item);
    });

    const assignDepth = (nodes: AreaSelectionRow[], depth: number): AreaSelectionRow[] => (
        [...nodes]
            .sort((left, right) => left.name.localeCompare(right.name))
            .map((node) => ({
                ...node,
                hierarchyDepth: depth,
                children: node.children && node.children.length > 0
                    ? assignDepth(node.children, depth + 1)
                    : undefined,
            }))
    );

    return assignDepth(roots, 0);
};

const findAreaSelectionPath = (
    areaId: number,
    nodes: AreaSelectionRow[],
    path: number[] = []
): number[] => {
    for (const node of nodes) {
        const nextPath = [...path, node.id];
        if (node.id === areaId) {
            return nextPath;
        }

        if (node.children && node.children.length > 0) {
            const found = findAreaSelectionPath(areaId, node.children, nextPath);
            if (found.length > 0) {
                return found;
            }
        }
    }

    return [];
};

const normalizeAreaSpaceType = (value: unknown): OperationEnvironment | null => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'mixed' || normalized === 'indoor' || normalized === 'outdoor') {
        return normalized;
    }
    return null;
};

const deriveOperationEnvironmentFromAreaHierarchy = (
    areaId: number | null | undefined,
    areas: AreaItem[],
    selectedAreaDetail?: AreaItem | null
): OperationEnvironment => {
    if (!areaId) {
        return 'outdoor';
    }

    const areasById = new Map<number, AreaItem>();
    areas.forEach((area) => {
        areasById.set(area.id, area);
    });
    if (selectedAreaDetail?.id) {
        areasById.set(selectedAreaDetail.id, {
            ...(areasById.get(selectedAreaDetail.id) || {}),
            ...selectedAreaDetail,
        });
    }

    let current: AreaItem | null | undefined = areasById.get(areaId);
    let currentId: number | null | undefined = areaId;
    const visited = new Set<number>();
    let hasIndoor = false;

    while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        current = areasById.get(currentId) || current;

        const spaceType = normalizeAreaSpaceType(current?.spaceType);
        if (spaceType === 'mixed') {
            return 'mixed';
        }
        if (spaceType === 'indoor') {
            hasIndoor = true;
        }

        const parent: AreaItem | null = current?.parentArea || null;
        const parentId: number | null = current?.parentAreaId ?? parent?.id ?? null;
        if (parent?.id && !areasById.has(parent.id)) {
            areasById.set(parent.id, parent);
        }

        currentId = parentId;
        current = parent || (currentId ? areasById.get(currentId) : null);
    }

    return hasIndoor ? 'indoor' : 'outdoor';
};

const buildPatternPreviewPoints = (
    points: PatternPointItem[],
    pattern: PatternItem | null,
    area: AreaItem | null
): PatternPreviewPoint[] => {
    if (!points.length) {
        return [];
    }

    const templateDimensions = area?.areaType?.templateDimensions;
    const localPreviewPoints = points
        .map((point) => {
            const x = normalizePatternLocalCoordinate(
                point.xLocal,
                pattern?.definition?.boxWidth ?? templateDimensions?.widthMeters
            );
            const y = normalizePatternLocalCoordinate(
                point.yLocal,
                pattern?.definition?.boxHeight ?? templateDimensions?.heightMeters
            );

            if (x === null || y === null) {
                return null;
            }

            return {
                id: point.id,
                pointIndex: point.pointIndex,
                x,
                y: 1 - y,
            };
        })
        .filter((point): point is PatternPreviewPoint => Boolean(point))
        .sort((left, right) => left.pointIndex - right.pointIndex);

    if (localPreviewPoints.length > 0) {
        return localPreviewPoints;
    }

    const geoPreviewPoints = points
        .map((point) => {
            const projected = projectPatternPointToGps(point, area?.geom, {
                boxWidth: pattern?.definition?.boxWidth,
                boxHeight: pattern?.definition?.boxHeight,
                templateDimensions,
            });

            if (!projected) {
                return null;
            }

            return {
                id: point.id,
                pointIndex: point.pointIndex,
                lat: projected.lat,
                lng: projected.lng,
            };
        })
        .filter((point): point is { id: number; pointIndex: number; lat: number; lng: number } => Boolean(point));

    if (!geoPreviewPoints.length) {
        return [];
    }

    const lngs = geoPreviewPoints.map((point) => point.lng);
    const lats = geoPreviewPoints.map((point) => point.lat);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const lngSpan = maxLng - minLng;
    const latSpan = maxLat - minLat;

    return geoPreviewPoints
        .map((point) => ({
            id: point.id,
            pointIndex: point.pointIndex,
            x: lngSpan > 0 ? (point.lng - minLng) / lngSpan : 0.5,
            y: latSpan > 0 ? 1 - ((point.lat - minLat) / latSpan) : 0.5,
        }))
        .sort((left, right) => left.pointIndex - right.pointIndex);
};

/** Extract the 4 GPS corners of an area quad in Mapbox ImageSource order:
 *  [topLeft, topRight, bottomRight, bottomLeft] as [lng, lat].
 *  Based on the UNIT_SQUARE → quad mapping in missionProjection.ts:
 *  quad[0]=(0,0)=bottom-left, quad[1]=(1,0)=bottom-right,
 *  quad[2]=(1,1)=top-right,   quad[3]=(0,1)=top-left of the image.
 */
const extractAreaQuadCorners = (
    geom: any
): [[number, number], [number, number], [number, number], [number, number]] | null => {
    if (!geom) return null;
    let coords: any = null;
    if (geom.type === 'Polygon') {
        coords = geom.coordinates?.[0];
    } else if (geom.type === 'MultiPolygon') {
        coords = geom.coordinates?.[0]?.[0];
    }
    if (!Array.isArray(coords) || coords.length < 4) return null;
    const first = coords[0];
    const last = coords[coords.length - 1];
    const isClosed = Array.isArray(first) && Array.isArray(last) && first[0] === last[0] && first[1] === last[1];
    const ring: any[] = isClosed ? coords.slice(0, -1) : coords;
    if (ring.length !== 4) return null;
    for (const pt of ring) {
        if (!Array.isArray(pt) || !Number.isFinite(pt[0]) || !Number.isFinite(pt[1])) return null;
    }
    return [
        [ring[3][0], ring[3][1]], // topLeft  (unit 0,1)
        [ring[2][0], ring[2][1]], // topRight (unit 1,1)
        [ring[1][0], ring[1][1]], // bottomRight (unit 1,0)
        [ring[0][0], ring[0][1]], // bottomLeft  (unit 0,0)
    ];
};

/** Compass bearing in degrees [0-360) from (lat1,lon1) to (lat2,lon2). */
const computeCompassBearing = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const dLon = toRad(lon2 - lon1);
    const lat1R = toRad(lat1);
    const lat2R = toRad(lat2);
    const y = Math.sin(dLon) * Math.cos(lat2R);
    const x = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLon);
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const MISSION_NAME_WORDS = [
    'Atlas',
    'Beacon',
    'Comet',
    'Harbor',
    'Juniper',
    'Keystone',
    'Nimbus',
    'Orchard',
    'Pioneer',
    'Summit',
    'Vector',
    'Willow',
];

const pickMissionNameWord = () => MISSION_NAME_WORDS[Math.floor(Math.random() * MISSION_NAME_WORDS.length)];

const pickMissionNameNumber = () => Math.floor(Math.random() * 900 + 100);

const compactMissionText = (value: string, maxLength: number) => (
    value.replace(/\s+/g, ' ').trim().slice(0, maxLength).trim()
);

const PatternTemplatePreview: React.FC<{
    templateUrl: string | null;
    points: PatternPreviewPoint[];
    compact?: boolean;
    expanded?: boolean;
    loading?: boolean;
    emptyText?: string;
}> = ({
    templateUrl,
    points,
    compact = false,
    expanded = false,
    loading = false,
    emptyText = 'No template image',
}) => {
    const frameClassName = `operations-pattern-template-frame${compact ? ' operations-pattern-template-frame-compact' : ''}${expanded ? ' operations-pattern-template-frame-expanded' : ''}`;
    const imageClassName = `operations-pattern-template-image${compact ? ' operations-pattern-template-image-compact' : ''}${expanded ? ' operations-pattern-template-image-expanded' : ''}`;
    const markerClassName = `operations-pattern-template-marker${compact ? ' operations-pattern-template-marker-compact' : ''}${expanded ? ' operations-pattern-template-marker-expanded' : ''}`;

    if (loading) {
        return (
            <div className={frameClassName}>
                <div className="operations-pattern-template-empty">
                    <Spin size="small" />
                </div>
            </div>
        );
    }

    if (!templateUrl) {
        return (
            <div className={frameClassName}>
                <div className="operations-pattern-template-empty">
                    <Text type="secondary">{emptyText}</Text>
                </div>
            </div>
        );
    }

    return (
        <div className={frameClassName}>
            <div className="operations-pattern-template-canvas">
                <img
                    src={templateUrl}
                    alt="Pattern template preview"
                    className={imageClassName}
                />
                {points.map((point) => (
                    <div
                        key={point.id}
                        className={markerClassName}
                        style={{
                            left: `${point.x * 100}%`,
                            top: `${point.y * 100}%`,
                        }}
                    >
                        {point.pointIndex}
                    </div>
                ))}
            </div>
        </div>
    );
};

type LocalRoutePreviewPoint = {
    id: string;
    x: number;
    y: number;
    pixelX: number;
    pixelY: number;
    label: string;
    selected: boolean;
    headingDegrees?: number | null;
    directionMode?: PointDirectionMode | null;
};

type LocalRoutePreviewPath = {
    id: string | number;
    points: Array<{ pixelX: number; pixelY: number }>;
    selected?: boolean;
    warning?: boolean;
};

const LocalRoutePreview: React.FC<{
    imageUrl: string;
    imageName?: string | null;
    imageWidth: number;
    imageHeight: number;
    points: LocalRoutePreviewPoint[];
    paths?: LocalRoutePreviewPath[];
    templateImageUrl?: string | null;
    templateQuadPixels?: Array<{ x: number; y: number }> | null;
    focusTemplate?: boolean;
    draggablePointIds?: Array<string | number>;
    onPointClick: (pointId: string | number) => void;
    onPointDragEnd?: (pointId: string | number, location: { pixelX: number; pixelY: number }) => void;
    onViewCenterChange?: (location: { pixelX: number; pixelY: number }) => void;
}> = ({
    imageUrl,
    imageName,
    imageWidth,
    imageHeight,
    points,
    paths = [],
    templateImageUrl,
    templateQuadPixels,
    focusTemplate = false,
    draggablePointIds = [],
    onPointClick,
    onPointDragEnd,
    onViewCenterChange,
}) => {
    const viewportRef = React.useRef<HTMLDivElement | null>(null);
    const templateCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const [scale, setScale] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = React.useState(false);
    const [dragPreview, setDragPreview] = React.useState<{ id: string; pixelX: number; pixelY: number } | null>(null);
    const panStartRef = React.useRef({ x: 0, y: 0, panX: 0, panY: 0 });
    const draggingPointRef = React.useRef<{ id: string; pointerId: number } | null>(null);
    const draggablePointIdSet = React.useMemo(
        () => new Set(draggablePointIds.map((value) => String(value))),
        [draggablePointIds]
    );
    const renderBounds = React.useMemo(() => {
        const candidates = [
            { x: 0, y: 0 },
            { x: imageWidth, y: imageHeight },
            ...(templateQuadPixels?.length === 4 ? templateQuadPixels : []),
            ...points.map((point) => ({ x: point.pixelX, y: point.pixelY })),
            ...paths.flatMap((path) => path.points.map((point) => ({ x: point.pixelX, y: point.pixelY }))),
        ];
        const bounds = candidates.reduce(
            (acc, point) => ({
                minX: Math.min(acc.minX, point.x),
                maxX: Math.max(acc.maxX, point.x),
                minY: Math.min(acc.minY, point.y),
                maxY: Math.max(acc.maxY, point.y),
            }),
            {
                minX: candidates[0].x,
                maxX: candidates[0].x,
                minY: candidates[0].y,
                maxY: candidates[0].y,
            }
        );
        return {
            ...bounds,
            width: Math.max(1, bounds.maxX - bounds.minX),
            height: Math.max(1, bounds.maxY - bounds.minY),
        };
    }, [imageHeight, imageWidth, paths, points, templateQuadPixels]);

    const fitToViewport = React.useCallback(() => {
        const viewport = viewportRef.current;
        if (!viewport || imageWidth <= 0 || imageHeight <= 0) {
            return;
        }
        const rect = viewport.getBoundingClientRect();
        const useTemplateFocus = focusTemplate && templateQuadPixels?.length === 4;
        const fitPoints = useTemplateFocus
            ? templateQuadPixels
            : points.length > 0
                ? points.map((point) => ({ x: point.pixelX, y: point.pixelY }))
                : [
                    { x: 0, y: 0 },
                    { x: imageWidth, y: imageHeight },
                ];
        const bounds = fitPoints.reduce(
            (acc, point) => ({
                minX: Math.min(acc.minX, point.x),
                maxX: Math.max(acc.maxX, point.x),
                minY: Math.min(acc.minY, point.y),
                maxY: Math.max(acc.maxY, point.y),
            }),
            {
                minX: fitPoints[0].x,
                maxX: fitPoints[0].x,
                minY: fitPoints[0].y,
                maxY: fitPoints[0].y,
            }
        );
        const boundsWidth = Math.max(1, bounds.maxX - bounds.minX);
        const boundsHeight = Math.max(1, bounds.maxY - bounds.minY);
        const padding = useTemplateFocus
            ? Math.max(12, Math.min(boundsWidth, boundsHeight) * 0.04)
            : Math.max(20, Math.min(boundsWidth, boundsHeight) * 0.08);
        const nextScale = Math.min(
            rect.width / (boundsWidth + padding * 2),
            rect.height / (boundsHeight + padding * 2)
        );
        const fittedScale = Math.max(0.05, Math.min(8, nextScale));
        setScale(fittedScale);
        setPan({
            x: (rect.width - boundsWidth * fittedScale) / 2 - bounds.minX * fittedScale,
            y: (rect.height - boundsHeight * fittedScale) / 2 - bounds.minY * fittedScale,
        });
    }, [focusTemplate, imageHeight, imageWidth, points, templateQuadPixels]);

    React.useEffect(() => {
        fitToViewport();
        const viewport = viewportRef.current;
        if (!viewport || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(() => fitToViewport());
        observer.observe(viewport);
        return () => observer.disconnect();
    }, [fitToViewport]);

    const zoomAtCenter = React.useCallback((factor: number) => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const rect = viewport.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        setScale((currentScale) => {
            const nextScale = Math.max(0.05, Math.min(8, currentScale * factor));
            const ratio = nextScale / currentScale;
            setPan((currentPan) => ({
                x: centerX - (centerX - currentPan.x) * ratio,
                y: centerY - (centerY - currentPan.y) * ratio,
            }));
            return nextScale;
        });
    }, []);

    React.useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return undefined;
        }
        const handleWheel = (event: WheelEvent) => {
            event.preventDefault();
            event.stopPropagation();
            const rect = viewport.getBoundingClientRect();
            const focusX = event.clientX - rect.left;
            const focusY = event.clientY - rect.top;
            const factor = event.deltaY < 0 ? 1.12 : 0.89;
            setScale((currentScale) => {
                const nextScale = Math.max(0.05, Math.min(8, currentScale * factor));
                const ratio = nextScale / currentScale;
                setPan((currentPan) => ({
                    x: focusX - (focusX - currentPan.x) * ratio,
                    y: focusY - (focusY - currentPan.y) * ratio,
                }));
                return nextScale;
            });
        };
        viewport.addEventListener('wheel', handleWheel, { passive: false });
        return () => viewport.removeEventListener('wheel', handleWheel);
    }, []);

    const clientPointToPixel = React.useCallback((clientX: number, clientY: number) => {
        const viewport = viewportRef.current;
        if (!viewport) {
            return null;
        }
        const rect = viewport.getBoundingClientRect();
        return {
            pixelX: (clientX - rect.left - pan.x) / scale,
            pixelY: (clientY - rect.top - pan.y) / scale,
        };
    }, [pan.x, pan.y, scale]);

    React.useEffect(() => {
        if (!onViewCenterChange) {
            return;
        }
        const viewport = viewportRef.current;
        if (!viewport) {
            return;
        }
        const rect = viewport.getBoundingClientRect();
        onViewCenterChange({
            pixelX: (rect.width / 2 - pan.x) / scale,
            pixelY: (rect.height / 2 - pan.y) / scale,
        });
    }, [onViewCenterChange, pan.x, pan.y, scale]);

    React.useEffect(() => {
        const canvas = templateCanvasRef.current;
        const quad = templateQuadPixels;
        if (!canvas || !templateImageUrl || !quad || quad.length !== 4) {
            const ctx = canvas?.getContext('2d');
            ctx?.clearRect(0, 0, renderBounds.width, renderBounds.height);
            return undefined;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return undefined;
        }
        let cancelled = false;
        const templateImage = new Image();
        templateImage.onload = () => {
            if (cancelled) {
                return;
            }
            canvas.width = renderBounds.width;
            canvas.height = renderBounds.height;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, renderBounds.width, renderBounds.height);
            ctx.translate(-renderBounds.minX, -renderBounds.minY);

            const imgW = templateImage.naturalWidth || templateImage.width;
            const imgH = templateImage.naturalHeight || templateImage.height;
            const [p0, p1, p2, p3] = quad;
            const subdivisions = 8;

            const lerpQuad = (u: number, v: number) => ({
                x: (1 - u) * (1 - v) * p0.x + u * (1 - v) * p1.x + u * v * p2.x + (1 - u) * v * p3.x,
                y: (1 - u) * (1 - v) * p0.y + u * (1 - v) * p1.y + u * v * p2.y + (1 - u) * v * p3.y,
            });

            const drawWarpTriangle = (
                sx0: number, sy0: number, sx1: number, sy1: number, sx2: number, sy2: number,
                dx0: number, dy0: number, dx1: number, dy1: number, dx2: number, dy2: number
            ) => {
                const det = sx0 * (sy1 - sy2) + sx1 * (sy2 - sy0) + sx2 * (sy0 - sy1);
                if (Math.abs(det) < 1e-10) return;
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(dx0, dy0);
                ctx.lineTo(dx1, dy1);
                ctx.lineTo(dx2, dy2);
                ctx.closePath();
                ctx.clip();

                const a = (dx0 * (sy1 - sy2) + dx1 * (sy2 - sy0) + dx2 * (sy0 - sy1)) / det;
                const b = (dy0 * (sy1 - sy2) + dy1 * (sy2 - sy0) + dy2 * (sy0 - sy1)) / det;
                const c = (dx0 * (sx2 - sx1) + dx1 * (sx0 - sx2) + dx2 * (sx1 - sx0)) / det;
                const d = (dy0 * (sx2 - sx1) + dy1 * (sx0 - sx2) + dy2 * (sx1 - sx0)) / det;
                const e = (dx0 * (sx1 * sy2 - sx2 * sy1) + dx1 * (sx2 * sy0 - sx0 * sy2) + dx2 * (sx0 * sy1 - sx1 * sy0)) / det;
                const f = (dy0 * (sx1 * sy2 - sx2 * sy1) + dy1 * (sx2 * sy0 - sx0 * sy2) + dy2 * (sx0 * sy1 - sx1 * sy0)) / det;

                ctx.transform(a, b, c, d, e, f);
                ctx.drawImage(templateImage, 0, 0);
                ctx.restore();
            };

            ctx.globalAlpha = 0.62;
            for (let j = 0; j < subdivisions; j += 1) {
                for (let i = 0; i < subdivisions; i += 1) {
                    const u0 = i / subdivisions;
                    const u1 = (i + 1) / subdivisions;
                    const v0 = j / subdivisions;
                    const v1 = (j + 1) / subdivisions;
                    const d00 = lerpQuad(u0, v0);
                    const d10 = lerpQuad(u1, v0);
                    const d11 = lerpQuad(u1, v1);
                    const d01 = lerpQuad(u0, v1);
                    const su0 = u0 * imgW;
                    const su1 = u1 * imgW;
                    const sv0 = v0 * imgH;
                    const sv1 = v1 * imgH;
                    drawWarpTriangle(su0, sv0, su1, sv0, su1, sv1, d00.x, d00.y, d10.x, d10.y, d11.x, d11.y);
                    drawWarpTriangle(su0, sv0, su1, sv1, su0, sv1, d00.x, d00.y, d11.x, d11.y, d01.x, d01.y);
                }
            }
            ctx.globalAlpha = 1;
        };
        templateImage.onerror = () => {
            if (!cancelled) {
                ctx.clearRect(0, 0, renderBounds.width, renderBounds.height);
            }
        };
        templateImage.src = templateImageUrl;
        return () => {
            cancelled = true;
        };
    }, [renderBounds.height, renderBounds.minX, renderBounds.minY, renderBounds.width, templateImageUrl, templateQuadPixels]);

    const handlePointerDown = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }
        event.currentTarget.setPointerCapture(event.pointerId);
        panStartRef.current = {
            x: event.clientX,
            y: event.clientY,
            panX: pan.x,
            panY: pan.y,
        };
        setIsPanning(true);
    }, [pan.x, pan.y]);

    const handlePointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const draggingPoint = draggingPointRef.current;
        if (draggingPoint) {
            const pixel = clientPointToPixel(event.clientX, event.clientY);
            if (pixel) {
                setDragPreview({
                    id: draggingPoint.id,
                    pixelX: pixel.pixelX,
                    pixelY: pixel.pixelY,
                });
            }
            return;
        }

        if (!isPanning) {
            return;
        }
        const start = panStartRef.current;
        setPan({
            x: start.panX + event.clientX - start.x,
            y: start.panY + event.clientY - start.y,
        });
    }, [clientPointToPixel, isPanning]);

    const endPan = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const draggingPoint = draggingPointRef.current;
        if (draggingPoint) {
            const pixel = clientPointToPixel(event.clientX, event.clientY);
            if (pixel) {
                onPointDragEnd?.(draggingPoint.id, pixel);
            }
            draggingPointRef.current = null;
            setDragPreview(null);
            setIsPanning(false);
            return;
        }

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setIsPanning(false);
    }, [clientPointToPixel, onPointDragEnd]);

    return (
        <div
            ref={viewportRef}
            onWheelCapture={(event) => {
                event.preventDefault();
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPan}
            onPointerCancel={endPan}
            style={{
                position: 'relative',
                width: '100%',
                height: 520,
                maxHeight: '62vh',
                minHeight: 360,
                background: '#17172a',
                borderRadius: 6,
                overflow: 'hidden',
                cursor: isPanning ? 'grabbing' : 'grab',
                touchAction: 'none',
                overscrollBehavior: 'contain',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: imageWidth,
                    height: imageHeight,
                    overflow: 'visible',
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                    transformOrigin: '0 0',
                }}
            >
                <img
                    src={imageUrl}
                    alt={imageName || 'SLAM map'}
                    draggable={false}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: imageWidth,
                        height: imageHeight,
                        display: 'block',
                        userSelect: 'none',
                        zIndex: 0,
                    }}
                />
                {templateImageUrl && templateQuadPixels?.length === 4 ? (
                    <canvas
                        ref={templateCanvasRef}
                        width={renderBounds.width}
                        height={renderBounds.height}
                        style={{
                            position: 'absolute',
                            left: renderBounds.minX,
                            top: renderBounds.minY,
                            width: renderBounds.width,
                            height: renderBounds.height,
                            pointerEvents: 'none',
                            zIndex: 1,
                        }}
                    />
                ) : null}
                <svg
                    viewBox={`${renderBounds.minX} ${renderBounds.minY} ${renderBounds.width} ${renderBounds.height}`}
                    preserveAspectRatio="xMidYMid meet"
                    style={{
                        position: 'absolute',
                        left: renderBounds.minX,
                        top: renderBounds.minY,
                        width: renderBounds.width,
                        height: renderBounds.height,
                        maxWidth: 'none',
                        maxHeight: 'none',
                        pointerEvents: 'none',
                        overflow: 'visible',
                        zIndex: 2,
                    }}
                >
                    {templateImageUrl && templateQuadPixels?.length === 4 ? (
                        <polygon
                            points={templateQuadPixels.map((point) => `${point.x},${point.y}`).join(' ')}
                            fill="none"
                            stroke="#f59e0b"
                            strokeWidth={Math.max(3, 6 / scale)}
                            strokeLinejoin="round"
                        />
                    ) : null}
                    {paths.length > 0 ? paths.map((path) => (
                        <polyline
                            key={path.id}
                            points={path.points.map((point) => `${point.pixelX},${point.pixelY}`).join(' ')}
                            fill="none"
                            stroke={path.warning ? '#ef4444' : '#1677ff'}
                            strokeWidth={Math.max(path.warning ? 3 : 2, 4 / scale)}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                        />
                    )) : points.length > 1 ? (
                        <polyline
                            points={points.map((point) => `${point.pixelX},${point.pixelY}`).join(' ')}
                            fill="none"
                            stroke="#1677ff"
                            strokeWidth={Math.max(2, 4 / scale)}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                        />
                    ) : null}
                </svg>
                {templateImageUrl && templateQuadPixels?.length === 4 ? (
                    <>
                        {templateQuadPixels.map((point, index) => (
                            <div
                                key={`template-corner-${index}`}
                                style={{
                                    position: 'absolute',
                                    left: point.x,
                                    top: point.y,
                                    transform: `translate(-50%, -50%) scale(${1 / scale})`,
                                    transformOrigin: 'center',
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    border: '3px solid #fff',
                                    background: '#f59e0b',
                                    color: '#111827',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 13,
                                    fontWeight: 800,
                                    pointerEvents: 'none',
                                    zIndex: 3,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
                                }}
                            >
                                {index + 1}
                            </div>
                        ))}
                    </>
                ) : null}
                {points.map((point) => {
                    const preview = dragPreview?.id === String(point.id) ? dragPreview : null;
                    const pixelX = preview?.pixelX ?? point.pixelX;
                    const pixelY = preview?.pixelY ?? point.pixelY;
                    const isDraggable = draggablePointIdSet.has(String(point.id));
                    const headingDegrees = Number(point.headingDegrees);
                    return (
                        <React.Fragment key={point.id}>
                            {Number.isFinite(headingDegrees) ? (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: pixelX,
                                        top: pixelY,
                                        width: 18,
                                        height: 40,
                                        transform: `translate(-50%, -100%) rotate(${headingDegrees}deg) scale(${1 / scale})`,
                                        transformOrigin: '50% 100%',
                                        pointerEvents: 'none',
                                        zIndex: 3,
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: 8,
                                            bottom: 9,
                                            width: 3,
                                            height: 24,
                                            borderRadius: 999,
                                            background: point.directionMode === 'manual' ? '#fa541c' : point.directionMode === 'pattern' ? '#722ed1' : '#1677ff',
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                                        }}
                                    />
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: 4,
                                            top: 0,
                                            width: 0,
                                            height: 0,
                                            borderLeft: '5px solid transparent',
                                            borderRight: '5px solid transparent',
                                            borderBottom: `10px solid ${point.directionMode === 'manual' ? '#fa541c' : point.directionMode === 'pattern' ? '#722ed1' : '#1677ff'}`,
                                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.35))',
                                        }}
                                    />
                                </div>
                            ) : null}
                            <button
                                type="button"
                                onPointerDown={(event) => {
                                    event.stopPropagation();
                                    if (!isDraggable || !onPointDragEnd) {
                                        return;
                                    }
                                    event.currentTarget.setPointerCapture(event.pointerId);
                                    draggingPointRef.current = { id: String(point.id), pointerId: event.pointerId };
                                    setDragPreview({ id: String(point.id), pixelX, pixelY });
                                }}
                                onClick={() => onPointClick(point.id)}
                                title={`${point.label} (${point.x.toFixed(2)}, ${point.y.toFixed(2)} m)`}
                                style={{
                                    position: 'absolute',
                                    left: pixelX,
                                    top: pixelY,
                                    transform: `translate(-50%, -50%) scale(${1 / scale})`,
                                    transformOrigin: 'center',
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    border: point.selected ? '3px solid #fff' : '2px solid #fff',
                                    background: point.selected ? '#fa541c' : '#1677ff',
                                    color: '#fff',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: isDraggable ? 'grab' : 'pointer',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                    zIndex: 4,
                                }}
                            >
                                {point.label}
                            </button>
                        </React.Fragment>
                    );
                })}
            </div>
            <Space
                size={4}
                onPointerDown={(event) => event.stopPropagation()}
                style={{
                    position: 'absolute',
                    right: 10,
                    top: 10,
                    zIndex: 2,
                    background: 'rgba(255,255,255,0.92)',
                    padding: 4,
                    borderRadius: 6,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                }}
            >
                <Button size="small" icon={<ZoomInOutlined />} onClick={() => zoomAtCenter(1.18)} />
                <Button size="small" icon={<ZoomOutOutlined />} onClick={() => zoomAtCenter(0.85)} />
                <Button size="small" icon={<ExpandOutlined />} onClick={fitToViewport} />
            </Space>
            <div
                style={{
                    position: 'absolute',
                    left: 10,
                    bottom: 10,
                    zIndex: 2,
                    padding: '3px 7px',
                    borderRadius: 4,
                    background: 'rgba(255,255,255,0.92)',
                    fontSize: 12,
                    color: '#475569',
                }}
            >
                {(scale * 100).toFixed(0)}%
            </div>
        </div>
    );
};

const buildDraftPointFromMissionPoint = (point: MissionPointItem): MissionPointDraft => ({
    key: `mission-point-${point.id}`,
    id: point.id,
    label: point.label || '',
    actionType: point.actionType,
    patternPointId: point.patternPointId || null,
    lat: point.resolvedLocation?.lat ?? null,
    lng: point.resolvedLocation?.lng ?? null,
    waitSeconds: point.waitSeconds ?? null,
    speedOverride: point.speedOverride ?? null,
    localPose: point.localPose || null,
    isRequired: point.isRequired,
    source: point.patternPointId ? 'pattern' : 'custom',
    params: point.params || {},
});

const buildDraftPointPayload = (point: MissionPointDraft) => ({
    label: point.label.trim() || null,
    actionType: point.actionType,
    patternPointId: point.patternPointId || null,
    geom:
        Number.isFinite(point.lat) && Number.isFinite(point.lng)
            ? {
                type: 'Point' as const,
                coordinates: [Number(point.lng), Number(point.lat)] as [number, number],
            }
            : null,
    localPose: point.localPose || null,
    waitSeconds: point.waitSeconds ?? null,
    speedOverride: point.speedOverride ?? null,
    params: point.params || {},
    isRequired: point.isRequired,
});

const normalizeCommandProtocol = (protocol?: string | null): string => {
    const normalized = String(protocol || 'oscar').trim().toLowerCase();
    return ['mavlink', 'ardupilot', 'custom'].includes(normalized) ? normalized : 'oscar';
};

const isMavlinkLikeProtocol = (protocol?: string | null): boolean => {
    const normalized = normalizeCommandProtocol(protocol);
    return normalized === 'mavlink' || normalized === 'ardupilot';
};

const getDefaultCommandForKind = (kind: string, protocol?: string | null) => {
    const proto = normalizeCommandProtocol(protocol);
    const protocolOption = getProtocolFilteredCommands(proto, { complexity: 'advanced' })
        .find((command) => command.kind === kind);
    if (protocolOption) {
        return { value: protocolOption.id, commandCode: protocolOption.mavlinkCode ?? null };
    }

    const fallbackOption = (MISSION_COMMAND_NAME_OPTIONS[kind] || MISSION_COMMAND_NAME_OPTIONS.custom)[0];
    const fallbackCommand = fallbackOption ? resolveCommand(fallbackOption.value) : null;
    return {
        value: fallbackOption?.value || 'custom',
        commandCode: fallbackCommand?.mavlinkCode ?? null,
    };
};

const mapActionTypeToGeneratedCommand = (actionType: string, commandProtocol?: string | null) => {
    const measureCommand = isMavlinkLikeProtocol(commandProtocol)
        ? { kind: 'nav', commandName: 'waypoint' }
        : { kind: 'nav', commandName: 'nav_pause' };
    const mapping: Record<string, { kind: string; commandName: string }> = {
        start: { kind: 'nav', commandName: 'start' },
        move: { kind: 'nav', commandName: 'waypoint' },
        measure: measureCommand,
        wait: { kind: 'condition', commandName: 'wait' },
        dock: { kind: 'nav', commandName: 'dock' },
        end: { kind: 'nav', commandName: 'end' },
        pass_through: { kind: 'nav', commandName: 'pass_through' },
        home: { kind: 'nav', commandName: 'return_home' },
        custom: { kind: 'custom', commandName: 'custom' },
    };

    return mapping[String(actionType || '').trim().toLowerCase()] || mapping.custom;
};

/** Convert pathDirection {x,y} (math convention: 0=East, CCW+) to compass degrees (0=North, CW+). */
const pathDirectionToCompassDegrees = (
    direction: { x: unknown; y: unknown } | null | undefined,
    bearingOffsetDegrees = 0
): number | null => {
    if (!direction) return null;
    const dx = Number(direction.x);
    const dy = Number(direction.y);
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1e-6) return null;
    const mathRad = Math.atan2(dy / len, dx / len);
    return ((90 - mathRad * 180 / Math.PI) + bearingOffsetDegrees + 360) % 360;
};

const buildGeneratedPrimaryCommand = (
    point: MissionPointDraft,
    commandProtocol?: string | null,
    headingBearingDegrees = 0
): MissionCommandDraft => {
    const generated = mapActionTypeToGeneratedCommand(point.actionType, commandProtocol);
    const commandDefinition = resolveCommand(generated.commandName);
    // For manually-added (custom) points, mark the primary command as 'manual'
    // so the sync function won't overwrite user edits.
    const isManualPoint = point.source === 'custom';
    return {
        key: `primary-command-${point.key}`,
        missionPointKey: point.key,
        label: '',
        kind: generated.kind,
        commandName: generated.commandName,
        commandCode: commandDefinition?.mavlinkCode ?? null,
        frame: null,
        autoContinue: true,
        altitude: null,
        headingDegrees: pathDirectionToCompassDegrees(point.params?.pathDirection, headingBearingDegrees),
        params: {},
        source: isManualPoint ? 'manual' : 'generated',
        isPrimary: true,
    };
};

const NO_MODULE_PATTERN_ACTION_SOURCE = 'generated_no_module_action';
const NO_MODULE_PATTERN_WAIT_SECONDS = 5;

const isGeneratedNoModulePatternActionCommand = (command: MissionCommandDraft): boolean => (
    command.source === NO_MODULE_PATTERN_ACTION_SOURCE
);

const shouldGenerateNoModulePatternActions = (
    point: MissionPointDraft,
    hasSelectedPattern: boolean,
    enabledModuleCount: number
): boolean => (
    hasSelectedPattern
    && enabledModuleCount === 0
    && point.actionType === 'measure'
    && (point.source === 'pattern' || Boolean(point.patternPointId))
);

const buildNoModulePatternActionCommands = (
    point: MissionPointDraft,
    existingSecondaryCommands: MissionCommandDraft[]
): MissionCommandDraft[] => {
    const baseGroup = 1 + existingSecondaryCommands.reduce(
        (max, command) => Math.max(max, command.executionGroup ?? 0),
        0
    );

    return [
        {
            key: `no-module-wait-${point.key}`,
            missionPointKey: point.key,
            label: 'Wait 5 seconds',
            kind: 'condition',
            commandName: 'wait',
            commandCode: 112,
            frame: null,
            autoContinue: true,
            altitude: null,
            params: { delaySeconds: NO_MODULE_PATTERN_WAIT_SECONDS },
            source: NO_MODULE_PATTERN_ACTION_SOURCE,
            isPrimary: false,
            executionGroup: baseGroup,
        },
        {
            key: `no-module-continue-${point.key}`,
            missionPointKey: point.key,
            label: 'Continue Navigation',
            kind: 'nav',
            commandName: 'nav_continue',
            commandCode: null,
            frame: null,
            autoContinue: true,
            altitude: null,
            params: {},
            source: NO_MODULE_PATTERN_ACTION_SOURCE,
            isPrimary: false,
            executionGroup: baseGroup + 1,
        },
    ];
};

const buildDraftCommandFromMissionCommand = (
    command: MissionCommandItem,
    pointKeyById: Map<number, string>,
    pointKeyByIndex: Map<number, string>
): MissionCommandDraft => ({
    key: `mission-command-${command.id}`,
    missionPointKey: (
        (command.missionPointId ? pointKeyById.get(command.missionPointId) : null)
        || (command.missionPointIndex ? pointKeyByIndex.get(command.missionPointIndex) : null)
        || null
    ),
    label: command.label || '',
    kind: command.kind,
    commandName: command.commandName,
    commandCode: command.commandCode ?? null,
    frame: command.frame ?? null,
    autoContinue: command.autoContinue,
    altitude: command.altitude ?? null,
    lat: command.resolvedLocation?.lat ?? null,
    lng: command.resolvedLocation?.lng ?? null,
    localPose: command.localPose || null,
    params: command.params || {},
    source: command.source || 'manual',
    isPrimary: Boolean(command.isPrimary),
    executionGroup: command.executionGroup ?? null,
});

const buildDraftCommandPayload = (
    command: MissionCommandDraft,
    pointIndexByKey: Map<string, number>
) => ({
    label: command.label.trim() || null,
    kind: command.kind,
    commandName: command.commandName.trim(),
    commandCode: command.commandCode ?? null,
    frame: command.frame ?? null,
    autoContinue: command.autoContinue,
    missionPointIndex: command.missionPointKey ? (pointIndexByKey.get(command.missionPointKey) ?? null) : null,
    geom:
        Number.isFinite(command.lat) && Number.isFinite(command.lng)
            ? {
                type: 'Point' as const,
                coordinates: [Number(command.lng), Number(command.lat)] as [number, number],
            }
            : null,
    localPose: command.localPose || null,
    altitude: command.altitude ?? null,
    headingDegrees: command.headingDegrees ?? null,
    params: command.params || {},
    source: command.source || null,
    isPrimary: command.isPrimary,
    executionGroup: command.executionGroup ?? null,
});

const isPlainObject = (value: unknown): value is Record<string, any> => (
    Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const cloneJsonObject = (value: unknown): Record<string, any> => (
    isPlainObject(value) ? JSON.parse(JSON.stringify(value)) : {}
);

const mergeJsonObjects = (...values: unknown[]): Record<string, any> => {
    const result: Record<string, any> = {};
    values.forEach((value) => {
        if (!isPlainObject(value)) {
            return;
        }
        Object.entries(value).forEach(([key, childValue]) => {
            result[key] = isPlainObject(childValue) && isPlainObject(result[key])
                ? mergeJsonObjects(result[key], childValue)
                : childValue;
        });
    });
    return result;
};

const getDeviceModuleKey = (module: DeviceModuleItem, index: number): string => (
    module.instanceKey
    || module.moduleDefinition?.key
    || (module.moduleDefinitionId ? String(module.moduleDefinitionId) : `module_${index}`)
);

const getDeviceModuleLabel = (module: DeviceModuleItem, moduleKey: string): string => (
    module.name
    || module.moduleDefinition?.name
    || moduleKey
);

const getDeviceModuleDefaultConfig = (module: DeviceModuleItem): Record<string, any> => (
    mergeJsonObjects(module.moduleDefinition?.defaultConfig, module.config)
);

const getDeviceModuleDefaultCommandSpecKey = (module: DeviceModuleItem): string | null => {
    const specs = Array.isArray(module.moduleDefinition?.commandSpecs)
        ? module.moduleDefinition?.commandSpecs || []
        : [];
    const firstSpec = specs.find((spec) => spec && typeof spec === 'object' && String(spec.key || '').trim());
    return firstSpec ? String(firstSpec.key).trim() : null;
};

const getDeviceModuleDefaultCommandName = (module: DeviceModuleItem, moduleKey: string): string => (
    getDeviceModuleDefaultCommandSpecKey(module)
    || module.moduleDefinition?.key
    || moduleKey
);

const buildOscarModuleSettings = (
    modules: DeviceModuleItem[],
    previous: Record<string, OscarMissionModuleSetting> = {}
): Record<string, OscarMissionModuleSetting> => {
    const next: Record<string, OscarMissionModuleSetting> = {};
    modules.forEach((module, index) => {
        const moduleKey = getDeviceModuleKey(module, index);
        const existing = previous[moduleKey];
        const commandSpecKey = getDeviceModuleDefaultCommandSpecKey(module);
        next[moduleKey] = {
            moduleKey,
            enabled: existing?.enabled ?? !['disabled', 'removed'].includes(String(module.status || '').toLowerCase()),
            label: getDeviceModuleLabel(module, moduleKey),
            instanceKey: module.instanceKey ?? null,
            moduleDefinitionId: module.moduleDefinitionId ?? module.moduleDefinition?.id ?? null,
            moduleDefinitionKey: module.moduleDefinition?.key ?? null,
            commandName: existing?.commandName ?? getDeviceModuleDefaultCommandName(module, moduleKey),
            commandSpecKey: existing?.commandSpecKey ?? commandSpecKey,
            config: cloneJsonObject(existing?.config ?? getDeviceModuleDefaultConfig(module)),
        };
    });
    return next;
};

const serializeOscarModuleDefaults = (
    executionMode: ModuleExecutionMode,
    settings: Record<string, OscarMissionModuleSetting>
) => ({
    executionMode,
    modules: Object.values(settings).map((setting) => ({
        moduleKey: setting.moduleKey,
        enabled: setting.enabled,
        label: setting.label,
        instanceKey: setting.instanceKey ?? null,
        moduleDefinitionId: setting.moduleDefinitionId ?? null,
        moduleDefinitionKey: setting.moduleDefinitionKey ?? null,
        commandName: setting.commandName ?? null,
        commandSpecKey: setting.commandSpecKey ?? null,
        config: cloneJsonObject(setting.config),
    })),
});

const normalizeOscarModuleDefaultsFromMeta = (value: unknown): {
    executionMode: ModuleExecutionMode;
    settings: Record<string, OscarMissionModuleSetting>;
} => {
    if (!isPlainObject(value)) {
        return { executionMode: 'parallel', settings: {} };
    }

    const executionMode = value.executionMode === 'parallel' ? 'parallel' : 'series';
    const rawModules = Array.isArray(value.modules)
        ? value.modules
        : Object.values(isPlainObject(value.modules) ? value.modules : {});
    const settings: Record<string, OscarMissionModuleSetting> = {};

    rawModules.forEach((raw, index) => {
        if (!isPlainObject(raw)) {
            return;
        }
        const moduleKey = String(raw.moduleKey || raw.moduleDefinitionKey || `module_${index}`).trim();
        if (!moduleKey) {
            return;
        }
        settings[moduleKey] = {
            moduleKey,
            instanceKey: typeof raw.instanceKey === 'string' ? raw.instanceKey : null,
            enabled: raw.enabled !== false,
            label: String(raw.label || moduleKey),
            moduleDefinitionId: Number.isFinite(Number(raw.moduleDefinitionId)) ? Number(raw.moduleDefinitionId) : null,
            moduleDefinitionKey: typeof raw.moduleDefinitionKey === 'string' ? raw.moduleDefinitionKey : null,
            commandName: typeof raw.commandName === 'string' ? raw.commandName : null,
            commandSpecKey: typeof raw.commandSpecKey === 'string' ? raw.commandSpecKey : null,
            config: cloneJsonObject(raw.config),
        };
    });

    return { executionMode, settings };
};

const isDeviceModuleCommand = (command: MissionCommandDraft): boolean => (
    command.source === 'device_module'
);

const isGeneratedNavContinueCommand = (command: MissionCommandDraft): boolean => (
    isDeviceModuleCommand(command) && command.commandName === 'nav_continue'
);

const buildModuleDoCommands = (
    point: MissionPointDraft,
    moduleSettings: OscarMissionModuleSetting[],
    existingSecondaryCommands: MissionCommandDraft[],
    executionMode: ModuleExecutionMode
): MissionCommandDraft[] => {
    if (point.actionType !== 'measure' || moduleSettings.length === 0) {
        return [];
    }

    // Compute max existing group so new commands start after existing ones
    const maxExistingGroup = existingSecondaryCommands.reduce(
        (max, c) => Math.max(max, c.executionGroup ?? 0),
        0
    );

    const generated: MissionCommandDraft[] = [];
    const parallelGroup = maxExistingGroup + 1;
    moduleSettings.forEach((setting, modIndex) => {
        const moduleKey = setting.moduleKey;
        const executionModuleKey = setting.moduleDefinitionKey || setting.moduleKey;
        const alreadyExists = existingSecondaryCommands.some(
            (cmd) =>
                cmd.missionPointKey === point.key &&
                cmd.source === 'device_module' &&
                (cmd.params?.instanceKey || cmd.params?.moduleKey) === (setting.instanceKey || moduleKey)
        );
        if (alreadyExists) {
            return;
        }

        generated.push({
            key: `module-do-${point.key}-${moduleKey}-${modIndex}`,
            missionPointKey: point.key,
            label: setting.label,
            kind: 'do',
            commandName: setting.commandName || setting.moduleDefinitionKey || moduleKey,
            commandCode: null,
            frame: null,
            autoContinue: true,
            altitude: null,
            params: {
                moduleKey: executionModuleKey,
                ...(setting.instanceKey ? { instanceKey: setting.instanceKey } : {}),
                ...(setting.commandSpecKey ? { commandSpecKey: setting.commandSpecKey } : {}),
                triggerMethod: 'auto',
                config: cloneJsonObject(setting.config),
                ...(setting.moduleDefinitionId ? { moduleDefinitionId: setting.moduleDefinitionId } : {}),
            },
            source: 'device_module',
            isPrimary: false,
            executionGroup: executionMode === 'parallel'
                ? parallelGroup
                : maxExistingGroup + 1 + generated.length,
        });
    });

    // Append nav_continue at the end if not already present
    const hasNavContinue = existingSecondaryCommands.some(
        (cmd) =>
            cmd.missionPointKey === point.key &&
            cmd.commandName === 'nav_continue' &&
            cmd.source === 'device_module'
    );
    if (!hasNavContinue) {
        generated.push({
            key: `nav-continue-${point.key}`,
            missionPointKey: point.key,
            label: 'Continue Navigation',
            kind: 'nav',
            commandName: 'nav_continue',
            commandCode: null,
            frame: null,
            autoContinue: true,
            altitude: null,
            params: {},
            source: 'device_module',
            isPrimary: false,
            executionGroup: executionMode === 'parallel'
                ? parallelGroup + (moduleSettings.length > 0 ? 1 : 0)
                : maxExistingGroup + 1 + generated.length,
        });
    }

    return generated;
};

const rebuildModuleCommandsForPoint = (
    point: MissionPointDraft,
    existingSecondaryCommands: MissionCommandDraft[],
    moduleSettings: OscarMissionModuleSetting[],
    executionMode: ModuleExecutionMode,
    resetConfigs: boolean,
    hasSelectedPattern = false
): MissionCommandDraft[] => {
    if (point.actionType !== 'measure') {
        return existingSecondaryCommands
            .filter((command) => !isDeviceModuleCommand(command))
            .filter((command) => !isGeneratedNoModulePatternActionCommand(command));
    }

    const preservedManual = existingSecondaryCommands
        .filter((command) => !isDeviceModuleCommand(command))
        .filter((command) => !isGeneratedNoModulePatternActionCommand(command));

    if (shouldGenerateNoModulePatternActions(point, hasSelectedPattern, moduleSettings.length)) {
        return [
            ...preservedManual,
            ...buildNoModulePatternActionCommands(point, preservedManual),
        ];
    }

    const generated: MissionCommandDraft[] = [];
    const moduleGroup = 1 + preservedManual.reduce((max, command) => Math.max(max, command.executionGroup ?? 0), 0);
    const existingByModuleKey = new Map<string, MissionCommandDraft>();
    existingSecondaryCommands
        .filter((command) => (
            isDeviceModuleCommand(command)
            && command.kind === 'do'
            && command.params?.moduleKey
        ))
        .forEach((command) => existingByModuleKey.set(String(command.params?.instanceKey || command.params?.moduleKey), command));

    moduleSettings.forEach((setting, index) => {
        const existing = existingByModuleKey.get(setting.instanceKey || setting.moduleKey);
        const executionModuleKey = setting.moduleDefinitionKey || setting.moduleKey;
        const config = !resetConfigs && isPlainObject(existing?.params?.config)
            ? cloneJsonObject(existing?.params?.config)
            : cloneJsonObject(setting.config);
        generated.push({
            ...(existing || {}),
            key: existing?.key || `module-do-${point.key}-${setting.moduleKey}-${index}`,
            missionPointKey: point.key,
            label: existing?.label || setting.label,
            kind: 'do',
            commandName: setting.commandName || existing?.commandName || setting.moduleDefinitionKey || setting.moduleKey,
            commandCode: null,
            frame: null,
            autoContinue: existing?.autoContinue ?? true,
            altitude: null,
            params: {
                ...(existing?.params || {}),
                moduleKey: executionModuleKey,
                ...(setting.instanceKey ? { instanceKey: setting.instanceKey } : {}),
                ...(setting.commandSpecKey ? { commandSpecKey: setting.commandSpecKey } : {}),
                triggerMethod: existing?.params?.triggerMethod || 'auto',
                config,
                ...(setting.moduleDefinitionId ? { moduleDefinitionId: setting.moduleDefinitionId } : {}),
            },
            source: 'device_module',
            isPrimary: false,
            executionGroup: executionMode === 'parallel' ? moduleGroup : moduleGroup + index,
        });
    });

    if (moduleSettings.length > 0) {
        const existingContinue = existingSecondaryCommands.find(isGeneratedNavContinueCommand);
        generated.push({
            ...(existingContinue || {}),
            key: existingContinue?.key || `nav-continue-${point.key}`,
            missionPointKey: point.key,
            label: existingContinue?.label || 'Continue Navigation',
            kind: 'nav',
            commandName: 'nav_continue',
            commandCode: null,
            frame: null,
            autoContinue: true,
            altitude: null,
            params: existingContinue?.params || {},
            source: 'device_module',
            isPrimary: false,
            executionGroup: executionMode === 'parallel'
                ? moduleGroup + 1
                : moduleGroup + moduleSettings.length,
        });
    }

    return [...preservedManual, ...generated];
};

const syncDraftCommandsWithPoints = (
    points: MissionPointDraft[],
    currentCommands: MissionCommandDraft[],
    moduleSettings: OscarMissionModuleSetting[] = [],
    executionMode: ModuleExecutionMode = 'parallel',
    commandProtocol?: string | null,
    headingBearingDegrees = 0,
    hasSelectedPattern = false
) => {
    const nextCommands: MissionCommandDraft[] = [];
    const pointKeySet = new Set(points.map((point) => point.key));
    const mavlinkMode = isMavlinkLikeProtocol(commandProtocol);

    points.forEach((point) => {
        const existingPrimary = currentCommands.find((command) => command.missionPointKey === point.key && command.isPrimary) || null;
        const generatedPrimary = buildGeneratedPrimaryCommand(point, commandProtocol, headingBearingDegrees);
        const primaryCommand = existingPrimary
            ? (
                existingPrimary.source === 'generated'
                    ? {
                        ...existingPrimary,
                        ...generatedPrimary,
                        key: existingPrimary.key,
                        params: existingPrimary.params || {},
                        source: 'generated',
                    }
                    : {
                        ...existingPrimary,
                        missionPointKey: point.key,
                    }
            )
            : generatedPrimary;

        nextCommands.push(primaryCommand);

        const existingSecondary = currentCommands
            .filter((command) => !command.isPrimary && command.missionPointKey === point.key)
            .filter((command) => !isGeneratedNoModulePatternActionCommand(command))
            .filter((command) => !(mavlinkMode && command.source === 'device_module'));

        existingSecondary.forEach((command) => {
            nextCommands.push({
                ...command,
                missionPointKey: point.key,
            });
        });

        const moduleCommands = mavlinkMode
            ? []
            : buildModuleDoCommands(point, moduleSettings, existingSecondary, executionMode);
        moduleCommands.forEach((cmd) => nextCommands.push(cmd));

        if (
            !mavlinkMode
            && shouldGenerateNoModulePatternActions(point, hasSelectedPattern, moduleSettings.length)
        ) {
            buildNoModulePatternActionCommands(point, existingSecondary)
                .forEach((cmd) => nextCommands.push(cmd));
        }
    });

    return nextCommands.filter((command) => (
        !command.missionPointKey || pointKeySet.has(command.missionPointKey)
    ));
};

const buildDraftPointsFromImportPreview = (preview: MissionImportPreview): MissionPointDraft[] => (
    preview.missionPoints.map((point, index) => {
        const coordinates = Array.isArray(point.geom?.coordinates) ? point.geom?.coordinates : null;
        return {
            key: `import-point-${Date.now()}-${index}`,
            label: point.label || '',
            actionType: point.actionType,
            patternPointId: point.patternPointId || null,
            lat: coordinates ? Number(coordinates[1]) : null,
            lng: coordinates ? Number(coordinates[0]) : null,
            localPose: point.localPose || null,
            waitSeconds: point.waitSeconds ?? null,
            speedOverride: point.speedOverride ?? null,
            isRequired: point.isRequired,
            source: point.patternPointId ? 'pattern' : 'custom',
            params: point.params || {},
        };
    })
);

const buildDraftCommandsFromImportPreview = (
    preview: MissionImportPreview,
    pointKeyByIndex: Map<number, string>
): MissionCommandDraft[] => (
    (preview.missionCommands || []).map((command, index) => {
        const coordinates = Array.isArray(command.geom?.coordinates) ? command.geom?.coordinates : null;
        return {
            key: `import-command-${Date.now()}-${index}`,
            missionPointKey: command.missionPointIndex ? (pointKeyByIndex.get(command.missionPointIndex) || null) : null,
            label: command.label || '',
            kind: command.kind,
            commandName: command.commandName,
            commandCode: command.commandCode ?? null,
            frame: command.frame ?? null,
            autoContinue: command.autoContinue,
            altitude: command.altitude ?? null,
            lat: coordinates ? Number(coordinates[1]) : null,
            lng: coordinates ? Number(coordinates[0]) : null,
            localPose: command.localPose || null,
            params: command.params || {},
            source: command.source || 'imported',
            isPrimary: command.isPrimary,
        };
    })
);

type DirectionVector = {
    x: number;
    y: number;
};

type PointDirectionMode = 'pattern' | 'auto' | 'manual';

const normalizeDirectionVector = (vector: DirectionVector | null | undefined): DirectionVector | null => {
    if (!vector) {
        return null;
    }

    const x = Number(vector.x);
    const y = Number(vector.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }

    const magnitude = Math.sqrt((x ** 2) + (y ** 2));
    if (magnitude <= 1e-6) {
        return null;
    }

    return {
        x: x / magnitude,
        y: y / magnitude,
    };
};

const readPointDirectionVector = (point?: MissionPointDraft | null): DirectionVector | null => normalizeDirectionVector(
    point?.params?.pathDirection
        ? {
            x: Number(point.params.pathDirection.x),
            y: Number(point.params.pathDirection.y),
        }
        : null
);

const normalizeHeadingDegrees = (value: number) => ((value % 360) + 360) % 360;

const directionVectorToHeadingDegrees = (vector?: DirectionVector | null): number | null => {
    const normalized = normalizeDirectionVector(vector || null);
    if (!normalized) {
        return null;
    }

    return normalizeHeadingDegrees((Math.atan2(normalized.x, normalized.y) * 180) / Math.PI);
};

const directionVectorToProjectedHeadingDegrees = (
    vector: DirectionVector | null,
    placement: SlamPlacement | null
): number | null => {
    const heading = directionVectorToHeadingDegrees(vector);
    if (heading === null) {
        return null;
    }
    return normalizeHeadingDegrees(heading + (placement?.bearingDegrees ?? 0));
};

const headingDegreesToDirectionVector = (headingDegrees: number): DirectionVector => {
    const radians = (normalizeHeadingDegrees(headingDegrees) * Math.PI) / 180;
    return {
        x: Math.sin(radians),
        y: Math.cos(radians),
    };
};

const getPointDirectionMode = (point?: MissionPointDraft | null): PointDirectionMode | null => {
    const normalized = String(point?.params?.pathDirectionMode || '').trim().toLowerCase();
    if (normalized === 'pattern' || normalized === 'auto' || normalized === 'manual') {
        return normalized;
    }

    return null;
};

const updatePointDirectionParams = (
    point: MissionPointDraft,
    mode: PointDirectionMode,
    direction?: DirectionVector | null
): MissionPointDraft => {
    const nextParams: Record<string, any> = {
        ...(point.params || {}),
        pathDirectionMode: mode,
    };

    if (mode === 'manual') {
        const normalized = normalizeDirectionVector(direction || null);
        if (normalized) {
            nextParams.pathDirection = {
                x: Number(normalized.x.toFixed(6)),
                y: Number(normalized.y.toFixed(6)),
            };
        } else {
            delete nextParams.pathDirection;
        }
    } else {
        delete nextParams.pathDirection;
    }

    return {
        ...point,
        params: nextParams,
    };
};

type MissionEditorViewProps = {
    mode: MissionEditorMode;
    mission: MissionItem | null;
    loading: boolean;
    saving: boolean;
    areas: AreaItem[];
    devices: DeviceItem[];
    referencesLoading: boolean;
    initialEnvironment?: OperationEnvironment;
    resumeAreaId?: number | null;
    missionPatternHandoff?: { draftKey: string; value: MissionPatternHandoff } | null;
    onMissionPatternResumeConsumed?: () => void;
    onCancel: () => void;
    onSave: (payload: MissionSavePayload, missionId?: number) => Promise<void>;
};

const MissionEditorView: React.FC<MissionEditorViewProps> = ({
    mode,
    mission,
    loading,
    saving,
    areas,
    devices,
    referencesLoading,
    initialEnvironment,
    resumeAreaId,
    missionPatternHandoff,
    onMissionPatternResumeConsumed,
    onCancel,
    onSave,
}) => {
    const navigate = useNavigate();
    const [form] = Form.useForm<MissionFormValues>();
    const [draftPoints, setDraftPoints] = React.useState<MissionPointDraft[]>([]);
    const [draftCommands, setDraftCommands] = React.useState<MissionCommandDraft[]>([]);
    const [patterns, setPatterns] = React.useState<PatternItem[]>([]);
    const [patternPoints, setPatternPoints] = React.useState<PatternPointItem[]>([]);
    const [patternsLoading, setPatternsLoading] = React.useState(false);
    const [patternPointsLoading, setPatternPointsLoading] = React.useState(false);
    const [selectedAreaDetail, setSelectedAreaDetail] = React.useState<AreaItem | null>(null);
    const [previewMapCenter, setPreviewMapCenter] = React.useState<{ lat: number; lng: number; zoom: number } | null>(null);
    const [localPreviewCenter, setLocalPreviewCenter] = React.useState<{ x: number; y: number } | null>(null);
    const [activePointKey, setActivePointKey] = React.useState<string | null>(null);
    const [activeCommandKey, setActiveCommandKey] = React.useState<string | null>(null);
    const [expandedWorkflowStep, setExpandedWorkflowStep] = React.useState<WorkflowStep>('area');
    const [confirmedEnvironment, setConfirmedEnvironment] = React.useState(false);
    const [expandedAreaRowKeys, setExpandedAreaRowKeys] = React.useState<React.Key[]>([]);
    const [confirmedAreaId, setConfirmedAreaId] = React.useState<number | null>(null);
    const [confirmedDeviceId, setConfirmedDeviceId] = React.useState<number | null>(null);
    const [moduleStepConfirmed, setModuleStepConfirmed] = React.useState(false);
    const [oscarModuleExecutionMode, setOscarModuleExecutionMode] = React.useState<ModuleExecutionMode>('parallel');
    const [oscarModuleSettings, setOscarModuleSettings] = React.useState<Record<string, OscarMissionModuleSetting>>({});
    const [patternSelectionConfirmed, setPatternSelectionConfirmed] = React.useState(false);
    const [confirmedPatternId, setConfirmedPatternId] = React.useState<number | null>(null);
    const [patternPreviewPointsById, setPatternPreviewPointsById] = React.useState<Map<number, PatternPointItem[]>>(new Map());
    const [patternPreviewLoading, setPatternPreviewLoading] = React.useState(false);
    const [patternPreviewVisible, setPatternPreviewVisible] = React.useState(false);
    const [patternPreviewPatternId, setPatternPreviewPatternId] = React.useState<number | null>(null);
    const [areaTemplateUrl, setAreaTemplateUrl] = React.useState<string | null>(null);
    const [areaTemplateLoading, setAreaTemplateLoading] = React.useState(false);
    const [arduPilotImportLoading, setArduPilotImportLoading] = React.useState(false);
    const [arduPilotPreview, setArduPilotPreview] = React.useState<MissionImportPreview | null>(null);
    const [arduPilotPreviewVisible, setArduPilotPreviewVisible] = React.useState(false);
    const [slamMapImageUrls, setSlamMapImageUrls] = React.useState<Record<number, string>>({});
    // Area-level SLAM map state (loaded after area is confirmed for indoor/mixed)
    const [areaSlamMaps, setAreaSlamMaps] = React.useState<import('./missionModels').SlamMapItem[]>([]);
    const [areaSlamMapsLoading, setAreaSlamMapsLoading] = React.useState(false);
    const [areaSlamMapsChecked, setAreaSlamMapsChecked] = React.useState(false);
    const [confirmedSlamMapIds, setConfirmedSlamMapIds] = React.useState<number[]>([]);
    const [slamMapStepConfirmed, setSlamMapStepConfirmed] = React.useState(false);
    const [isScanMission, setIsScanMission] = React.useState(false);
    const [scanMode, setScanMode] = React.useState<ScanMode>('autonomous');
    const [slamMapUploadVisible, setSlamMapUploadVisible] = React.useState(false);
    const [slamMapUploadSaving, setSlamMapUploadSaving] = React.useState(false);
    const [slamUploadName, setSlamUploadName] = React.useState('');
    const [slamUploadMapType, setSlamUploadMapType] = React.useState('slam_2d');
    const [slamUploadScale, setSlamUploadScale] = React.useState<number | null>(null);
    const [slamUploadFile, setSlamUploadFile] = React.useState<File | null>(null);
    const [indoorSpaceName, setIndoorSpaceName] = React.useState('');
    const [indoorSpaceCreating, setIndoorSpaceCreating] = React.useState(false);
    const [patternGuidedDirections, setPatternGuidedDirections] = React.useState(true);
    const [obstaclePolicyMode, setObstaclePolicyMode] = React.useState<ObstaclePolicyMode>('assist');
    const [showRoutePreviewTemplate, setShowRoutePreviewTemplate] = React.useState(true);
    const [routePreviewMode, setRoutePreviewMode] = React.useState<MissionRoutePreviewMode>('mapbox');
    const [costMode, setCostMode] = React.useState<DubinsCostMode>('balanced');
    const [sweepAngles, setSweepAngles] = React.useState(36);
    const [windowSize, setWindowSize] = React.useState(3);
    const routeEditorStateRef = React.useRef<RouteEditorSnapshot>({
        points: [],
        commands: [],
        activePointKey: null,
        activeCommandKey: null,
    });
    const routeUndoStackRef = React.useRef<RouteEditorSnapshot[]>([]);
    const [routeUndoDepth, setRouteUndoDepth] = React.useState(0);
    const arduPilotFileInputRef = React.useRef<HTMLInputElement | null>(null);
    const templateObjectUrlRef = React.useRef<string | null>(null);
    const missionNameWordRef = React.useRef(pickMissionNameWord());
    const missionNameNumberRef = React.useRef(pickMissionNameNumber());
    const resumedPatternResultRef = React.useRef<string | null>(null);
    const handoffSavedPatternId = missionPatternHandoff?.value.result?.status === 'saved'
        ? missionPatternHandoff.value.result.patternId
        : null;
    const isRestoringMissionPatternHandoff = Boolean(
        missionPatternHandoff
        && resumedPatternResultRef.current !== missionPatternHandoff.draftKey
    );

    React.useEffect(() => {
        routeEditorStateRef.current = {
            points: draftPoints,
            commands: draftCommands,
            activePointKey,
            activeCommandKey,
        };
    }, [activeCommandKey, activePointKey, draftCommands, draftPoints]);

    const clearRouteUndoHistory = React.useCallback(() => {
        routeUndoStackRef.current = [];
        setRouteUndoDepth(0);
    }, []);

    const captureRouteUndo = React.useCallback(() => {
        const current = routeEditorStateRef.current;
        routeUndoStackRef.current = [
            ...routeUndoStackRef.current.slice(-49),
            {
                points: current.points,
                commands: current.commands,
                activePointKey: current.activePointKey,
                activeCommandKey: current.activeCommandKey,
            },
        ];
        setRouteUndoDepth(routeUndoStackRef.current.length);
    }, []);

    const handleUndoRouteEdit = React.useCallback(() => {
        const previous = routeUndoStackRef.current.pop();
        if (!previous) {
            return;
        }

        setDraftPoints(previous.points);
        setDraftCommands(previous.commands);
        setActivePointKey(previous.activePointKey);
        setActiveCommandKey(previous.activeCommandKey);
        setRouteUndoDepth(routeUndoStackRef.current.length);
    }, []);

    const editorAreaId = Form.useWatch('areaId', form) as number | undefined;
    const editorDeviceId = Form.useWatch('deviceId', form) as number | undefined;
    const editorPatternId = Form.useWatch('patternId', form) as number | undefined;
    const editorMissionName = Form.useWatch('name', form) as string | undefined;
    const editorMissionDescription = Form.useWatch('description', form) as string | undefined;
    const editorMissionType = Form.useWatch('missionType', form) as string | undefined;
    const editorOperationEnvironment = (Form.useWatch('operationEnvironment', form) as OperationEnvironment | undefined) ?? 'outdoor';
    const editorNavigationMode = (Form.useWatch('navigationMode', form) as MissionNavigationMode | undefined) ?? 'route_following';
    const turnRadiusMeters = Form.useWatch('turnRadiusMeters', form) as number | null | undefined;
    const isExternalApiMission = mode === 'edit' && mission?.externalSource === 'external_api';

    const areaPathMap = React.useMemo(() => buildAreaPathMap(areas), [areas]);
    const areaSelectionRows = React.useMemo(() => buildAreaSelectionTree(areas), [areas]);

    React.useEffect(() => {
        if (areaSelectionRows.length === 0) {
            setExpandedAreaRowKeys([]);
            return;
        }

        if (!editorAreaId) {
            const firstAreaId = areaSelectionRows[0].id;
            form.setFieldsValue({ areaId: firstAreaId });
            setExpandedAreaRowKeys([String(firstAreaId)]);
            return;
        }

        const pathKeys = findAreaSelectionPath(editorAreaId, areaSelectionRows).map((id) => String(id));
        setExpandedAreaRowKeys(pathKeys.length > 0 ? pathKeys : [String(areaSelectionRows[0].id)]);
    }, [areaSelectionRows, editorAreaId, form]);

    const loadAreaDetail = React.useCallback(async (areaId: number) => {
        try {
            const response = await axios.get(ENDPOINTS.AREA_DETAIL(areaId), {
                withCredentials: true,
            });
            setSelectedAreaDetail(response.data?.data || null);
        } catch (error) {
            console.error('Failed to load area detail', error);
            setSelectedAreaDetail(null);
            message.error(getErrorMessage(error, 'Failed to load selected area'));
        }
    }, []);

    const loadPatternsForArea = React.useCallback(async (areaId: number) => {
        setPatternsLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.AREA_PATTERNS(areaId), {
                withCredentials: true,
            });
            const loadedPatterns = parseApiList<PatternItem>(response.data);
            setPatterns((current) => {
                const resumedPattern = handoffSavedPatternId
                    ? current.find((pattern) => pattern.id === handoffSavedPatternId)
                    : null;
                return resumedPattern && !loadedPatterns.some((pattern) => pattern.id === resumedPattern.id)
                    ? [...loadedPatterns, resumedPattern]
                    : loadedPatterns;
            });
        } catch (error) {
            console.error('Failed to load patterns for area', error);
            setPatterns([]);
            message.error(getErrorMessage(error, 'Failed to load patterns'));
        } finally {
            setPatternsLoading(false);
        }
    }, [handoffSavedPatternId]);

    const loadPatternPoints = React.useCallback(async (patternId: number) => {
        setPatternPointsLoading(true);
        try {
            const response = await axios.get(ENDPOINTS.PATTERN_POINTS(patternId), {
                withCredentials: true,
            });
            setPatternPoints(parseApiList<PatternPointItem>(response.data));
        } catch (error) {
            console.error('Failed to load pattern points', error);
            setPatternPoints([]);
            message.error(getErrorMessage(error, 'Failed to load pattern points'));
        } finally {
            setPatternPointsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        form.resetFields();
        clearRouteUndoHistory();
        setPatternPreviewVisible(false);
        setPatternPreviewPatternId(null);

        if (mode === 'create' && missionPatternHandoff) {
            const resumed = missionPatternHandoff.value.draft;
            const resumedValues = resumed.formValues || {};
            const scheduledStartAt = resumedValues.scheduledStartAt
                ? dayjs(resumedValues.scheduledStartAt)
                : null;
            form.setFieldsValue({
                ...resumedValues,
                patternId: handoffSavedPatternId,
                scheduledStartAt: scheduledStartAt?.isValid() ? scheduledStartAt : null,
            } as MissionFormValues);
            setDraftPoints(Array.isArray(resumed.draftPoints) ? resumed.draftPoints : []);
            setDraftCommands(Array.isArray(resumed.draftCommands) ? resumed.draftCommands : []);
            setActivePointKey(null);
            setActiveCommandKey(null);
            setExpandedWorkflowStep(handoffSavedPatternId ? 'details' : 'pattern');
            setConfirmedEnvironment(true);
            setConfirmedAreaId(resumed.confirmedAreaId ?? null);
            setConfirmedDeviceId(resumed.confirmedDeviceId ?? null);
            setModuleStepConfirmed(Boolean(resumed.moduleStepConfirmed));
            setOscarModuleExecutionMode(
                resumed.oscarModuleExecutionMode === 'series' ? 'series' : 'parallel'
            );
            setOscarModuleSettings(resumed.oscarModuleSettings || {});
            setPatternSelectionConfirmed(Boolean(handoffSavedPatternId));
            setConfirmedPatternId(handoffSavedPatternId);
            setConfirmedSlamMapIds(Array.isArray(resumed.confirmedSlamMapIds) ? resumed.confirmedSlamMapIds : []);
            setSlamMapStepConfirmed(Boolean(resumed.slamMapStepConfirmed));
            setPatternGuidedDirections(resumed.patternGuidedDirections !== false);
            setObstaclePolicyMode(normalizeObstaclePolicyMode(resumed.obstaclePolicyMode));
            setCostMode(
                resumed.costMode === 'shortest' || resumed.costMode === 'smoothest'
                    ? resumed.costMode
                    : 'balanced'
            );
            setSweepAngles(Number.isFinite(Number(resumed.sweepAngles)) ? Number(resumed.sweepAngles) : 36);
            setWindowSize(Number.isFinite(Number(resumed.windowSize)) ? Number(resumed.windowSize) : 3);
            setIsScanMission(false);
            return;
        }

        if (mode === 'edit' && mission) {
            const lockedOutgoingPointIndexes = new Set(
                (mission.missionLegs || [])
                    .filter((leg) => leg.constraints?.motionConstraint?.mode === 'steering_locked')
                    .map((leg) => leg.fromPointIndex)
                    .filter((index): index is number => Number.isInteger(index))
            );
            const nextDraftPoints = (mission.missionPoints || []).map((point, index) => {
                const draft = buildDraftPointFromMissionPoint(point);
                const normalizedDraft = isExternalApiMission && point.params?.externalWaypointIndex
                    ? { ...draft, actionType: 'measure' }
                    : draft;
                return lockedOutgoingPointIndexes.has(index + 1)
                    ? {
                        ...normalizedDraft,
                        params: {
                            ...(normalizedDraft.params || {}),
                            straightSteeringLocked: true,
                        },
                    }
                    : normalizedDraft;
            });
            const pointKeyById = new Map(
                nextDraftPoints
                    .filter((point) => point.id)
                    .map((point) => [Number(point.id), point.key])
            );
            const pointKeyByIndex = new Map(
                nextDraftPoints.map((point, index) => [index + 1, point.key])
            );
            setDraftPoints(nextDraftPoints);
            setDraftCommands(
                (mission.missionCommands || []).map((command) => (
                    buildDraftCommandFromMissionCommand(command, pointKeyById, pointKeyByIndex)
                ))
            );
            setActivePointKey(null);
            setActiveCommandKey(null);
            setExpandedWorkflowStep(
                resumeAreaId && resumeAreaId !== mission.areaId ? 'area' : 'details'
            );
            setConfirmedEnvironment(true);
            setIsScanMission(mission.missionType === 'slam_scan' || mission.operationEnvironment === 'scan');
            setConfirmedAreaId(mission.areaId ?? null);
            setConfirmedDeviceId(mission.deviceId);
            setConfirmedSlamMapIds(Array.from(new Set(
                nextDraftPoints
                    .map((point) => point.localPose?.slamMapId)
                    .filter((slamMapId): slamMapId is number => Number.isFinite(slamMapId))
            )));
            setSlamMapStepConfirmed(true);
            const savedMissionMeta = mission.missionMeta as Record<string, any> | null | undefined;
            const savedObstaclePolicy = isPlainObject(savedMissionMeta?.obstaclePolicy)
                ? savedMissionMeta?.obstaclePolicy
                : null;
            setObstaclePolicyMode(normalizeObstaclePolicyMode(savedObstaclePolicy?.mode));
            setScanMode(normalizeScanMode(savedMissionMeta?.scanMode));
            const savedModuleDefaults = normalizeOscarModuleDefaultsFromMeta(
                savedMissionMeta?.oscarModuleDefaults
            );
            setOscarModuleExecutionMode(savedModuleDefaults.executionMode);
            setOscarModuleSettings(savedModuleDefaults.settings);
            setModuleStepConfirmed(true);
            setPatternSelectionConfirmed(true);
            setConfirmedPatternId(mission.patternId || null);
            setPatternGuidedDirections(mission.routeConfig?.patternGuidedDirections !== false);
            form.setFieldsValue({
                name: mission.name,
                description: mission.description || '',
                missionType: mission.missionType || 'robot_run',
                operationEnvironment: (mission.operationEnvironment as OperationEnvironment) || 'outdoor',
                navigationMode: isExternalApiMission ? 'route_following' : mission.navigationMode || 'route_following',
                areaId: resumeAreaId || mission.areaId,
                deviceId: mission.deviceId,
                patternId: mission.patternId || null,
                turnRadiusMeters: mission.routeConfig?.turnRadiusMeters ?? normalizeMissionRouteConfig().turnRadiusMeters,
                status: mission.status,
                scheduledStartAt: mission.scheduledStartAt ? dayjs(mission.scheduledStartAt) : null,
            });
            return;
        }

        setDraftPoints([]);
        setDraftCommands([]);
        setActivePointKey(null);
        setActiveCommandKey(null);
        setExpandedWorkflowStep(initialEnvironment === 'scan' ? 'device' : 'area');
        setConfirmedAreaId(null);
        setConfirmedDeviceId(null);
        setModuleStepConfirmed(false);
        setOscarModuleExecutionMode('parallel');
        setOscarModuleSettings({});
        setPatternSelectionConfirmed(false);
        setConfirmedPatternId(null);
        setPatternGuidedDirections(true);
        setConfirmedSlamMapIds([]);
        setSlamMapStepConfirmed(false);
        setObstaclePolicyMode('assist');
        setScanMode('autonomous');
        setConfirmedEnvironment(true);
        missionNameWordRef.current = pickMissionNameWord();
        missionNameNumberRef.current = pickMissionNameNumber();
        form.setFieldsValue({
            missionType: 'robot_run',
            operationEnvironment: (initialEnvironment || 'outdoor') as OperationEnvironment,
            navigationMode: 'route_following',
            status: 'draft',
            patternId: null,
            turnRadiusMeters: normalizeMissionRouteConfig().turnRadiusMeters,
            scheduledStartAt: null,
        });

        if (initialEnvironment === 'scan' && mode === 'create') {
            setIsScanMission(true);
            setScanMode('autonomous');
            form.setFieldsValue({ missionType: 'slam_scan', operationEnvironment: 'scan' as OperationEnvironment });
            setPatternSelectionConfirmed(true);
            setConfirmedPatternId(null);
            setExpandedWorkflowStep('device');
        } else {
            setIsScanMission(false);
            setExpandedWorkflowStep('area');
        }
    }, [clearRouteUndoHistory, form, handoffSavedPatternId, initialEnvironment, isExternalApiMission, mission, missionPatternHandoff, mode, resumeAreaId]);

    React.useEffect(() => {
        if (!editorAreaId) {
            setPatterns([]);
            setPatternPoints([]);
            setSelectedAreaDetail(null);
            setPatternPreviewPointsById(new Map());
            setPatternPreviewLoading(false);
            return;
        }

        void loadPatternsForArea(editorAreaId);
        void loadAreaDetail(editorAreaId);
    }, [editorAreaId, loadAreaDetail, loadPatternsForArea]);

    React.useEffect(() => {
        if (!editorPatternId) {
            setPatternPoints([]);
            return;
        }

        const previewPoints = patternPreviewPointsById.get(editorPatternId);
        if (previewPoints) {
            setPatternPoints(previewPoints);
            return;
        }

        void loadPatternPoints(editorPatternId);
    }, [editorPatternId, loadPatternPoints, patternPreviewPointsById]);

    const selectedAreaForEditor = React.useMemo(() => {
        if (!editorAreaId) {
            return null;
        }

        const areaFromList = areas.find((area) => area.id === editorAreaId) || null;
        if (!areaFromList) {
            return selectedAreaDetail;
        }

        return selectedAreaDetail
            ? { ...areaFromList, ...selectedAreaDetail }
            : areaFromList;
    }, [areas, editorAreaId, selectedAreaDetail]);

    React.useEffect(() => {
        const areaTypeId = selectedAreaForEditor?.areaType?.id;
        if (!areaTypeId) {
            if (templateObjectUrlRef.current) {
                URL.revokeObjectURL(templateObjectUrlRef.current);
                templateObjectUrlRef.current = null;
            }
            setAreaTemplateUrl(null);
            setAreaTemplateLoading(false);
            return;
        }

        let cancelled = false;
        setAreaTemplateLoading(true);

        void axios.get(`${ENDPOINTS.AREA_TYPES_V2}/${areaTypeId}/template-image/download?t=${Date.now()}`, {
            withCredentials: true,
            responseType: 'blob',
        })
            .then((response) => {
                if (cancelled) {
                    return;
                }

                if (templateObjectUrlRef.current) {
                    URL.revokeObjectURL(templateObjectUrlRef.current);
                }

                const nextUrl = URL.createObjectURL(response.data as Blob);
                templateObjectUrlRef.current = nextUrl;
                setAreaTemplateUrl(nextUrl);
            })
            .catch((error) => {
                console.warn('Failed to load area template preview', error);
                if (templateObjectUrlRef.current) {
                    URL.revokeObjectURL(templateObjectUrlRef.current);
                    templateObjectUrlRef.current = null;
                }
                setAreaTemplateUrl(null);
            })
            .finally(() => {
                if (!cancelled) {
                    setAreaTemplateLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedAreaForEditor]);

    React.useEffect(() => () => {
        if (templateObjectUrlRef.current) {
            URL.revokeObjectURL(templateObjectUrlRef.current);
            templateObjectUrlRef.current = null;
        }
    }, []);

    const areasToDisplay = React.useMemo(() => {
        if (!editorAreaId) {
            return [];
        }

        return collectAreaSubtree(areas, editorAreaId, selectedAreaDetail);
    }, [areas, editorAreaId, selectedAreaDetail]);

    const operationMapAreas = React.useMemo(() => {
        if (!areasToDisplay.length) {
            return [];
        }

        return getAreasForOperationsMap(areasToDisplay, areas, selectedAreaForEditor);
    }, [areas, areasToDisplay, selectedAreaForEditor]);

    const operationMapGeometry = React.useMemo(
        () => getGpsGeometryForOperationsMap(selectedAreaForEditor, areas, selectedAreaForEditor),
        [areas, selectedAreaForEditor]
    );

    const importedDraftPreviewPoints = React.useMemo(() => (
        mission?.externalSource === 'external_api' && !mission.areaId
            ? draftPoints
                .map((point, index) => ({
                    id: point.key,
                    lat: Number(point.lat),
                    lng: Number(point.lng),
                    label: point.label || `W${index + 1}`,
                }))
                .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
            : []
    ), [draftPoints, mission?.areaId, mission?.externalSource]);

    const importedDraftPreviewPaths = React.useMemo(() => (
        importedDraftPreviewPoints.length > 1
            ? [{
                id: 'external-draft-waypoints',
                coordinates: importedDraftPreviewPoints.map((point) => ({ lat: point.lat, lng: point.lng })),
                selected: true,
                previewOnly: true,
            }]
            : []
    ), [importedDraftPreviewPoints]);

    const selectedPattern = React.useMemo(
        () => patterns.find((pattern) => pattern.id === editorPatternId) || null,
        [editorPatternId, patterns]
    );

    const selectedDevice = React.useMemo(
        () => devices.find((device) => device.id === editorDeviceId) || null,
        [devices, editorDeviceId]
    );
    const selectedDeviceForDisplay = selectedDevice || mission?.device || null;

    const deviceModules = React.useMemo(
        () => selectedDevice?.modules ?? [],
        [selectedDevice]
    );

    const commandProtocol = React.useMemo(
        () => normalizeCommandProtocol(selectedDevice?.deviceType?.commandProtocol),
        [selectedDevice]
    );
    const isMavlinkCommandMode = React.useMemo(
        () => isMavlinkLikeProtocol(commandProtocol),
        [commandProtocol]
    );
    const isOscarModuleMode = !isMavlinkCommandMode;

    React.useEffect(() => {
        if (!selectedDevice) {
            // During edit hydration the form value can arrive one render before
            // the device reference list. Keep the saved module confirmation so
            // the workflow does not become gated at the module step.
            if (mode === 'edit' && mission?.deviceId) {
                return;
            }
            if (!isRestoringMissionPatternHandoff) {
                setOscarModuleSettings({});
                setModuleStepConfirmed(false);
            }
            return;
        }

        if (isMavlinkCommandMode) {
            setOscarModuleSettings({});
            setModuleStepConfirmed(false);
            return;
        }

        setOscarModuleSettings((current) => buildOscarModuleSettings(deviceModules, current));
        if (mode === 'edit' && confirmedDeviceId === mission?.deviceId) {
            setModuleStepConfirmed(true);
        }
    }, [confirmedDeviceId, deviceModules, isMavlinkCommandMode, isRestoringMissionPatternHandoff, mission?.deviceId, mode, selectedDevice]);

    const enabledOscarModuleSettings = React.useMemo(() => (
        deviceModules
            .map((module, index) => oscarModuleSettings[getDeviceModuleKey(module, index)])
            .filter((setting): setting is OscarMissionModuleSetting => Boolean(setting?.enabled))
    ), [deviceModules, oscarModuleSettings]);

    const commandModuleConfigsByKey = React.useMemo(() => {
        const configs: Record<string, {
            moduleKey: string;
            instanceKey?: string | null;
            label: string;
            moduleDefinitionKey?: string | null;
            moduleDefinitionId?: number | null;
            status?: string | null;
            triggerMethod?: string | null;
            schema?: Record<string, any> | null;
            defaultConfig?: Record<string, any> | null;
            commandSpecs?: any[] | null;
        }> = {};
        deviceModules.forEach((module, index) => {
            const moduleKey = getDeviceModuleKey(module, index);
            const setting = oscarModuleSettings[moduleKey];
            configs[moduleKey] = {
                moduleKey,
                instanceKey: module.instanceKey || null,
                label: setting?.label || getDeviceModuleLabel(module, moduleKey),
                moduleDefinitionKey: setting?.moduleDefinitionKey || module.moduleDefinition?.key || null,
                moduleDefinitionId: setting?.moduleDefinitionId ?? module.moduleDefinitionId ?? module.moduleDefinition?.id ?? null,
                status: module.status || null,
                triggerMethod: module.moduleDefinition?.defaultTriggerMethod || null,
                schema: module.moduleDefinition?.configSchema || null,
                defaultConfig: setting?.config || getDeviceModuleDefaultConfig(module),
                commandSpecs: module.moduleDefinition?.commandSpecs || null,
            };
            if (module.moduleDefinition?.key && module.moduleDefinition.key !== moduleKey) {
                configs[module.moduleDefinition.key] = configs[moduleKey];
            }
        });
        return configs;
    }, [deviceModules, oscarModuleSettings]);

    const expectedCapabilityKeys = React.useMemo(
        () => requiredCapabilitiesForEnvironment(editorOperationEnvironment),
        [editorOperationEnvironment]
    );

    const selectedDeviceCapabilityKeys = React.useMemo(
        () => getDeviceCapabilityKeys(selectedDevice),
        [selectedDevice]
    );

    const selectedDeviceMissingCapabilities = React.useMemo(
        () => selectedDevice
            ? expectedCapabilityKeys.filter((key) => !selectedDeviceCapabilityKeys.has(key))
            : [],
        [expectedCapabilityKeys, selectedDevice, selectedDeviceCapabilityKeys]
    );

    const selectedDeviceMissingExpectedCapability = selectedDeviceMissingCapabilities.length > 0;

    const filteredDevicesForEnvironment = React.useMemo(
        () => devices.filter((device) => {
            const capabilityKeys = getDeviceCapabilityKeys(device);
            return expectedCapabilityKeys.every((key) => capabilityKeys.has(key));
        }),
        [devices, expectedCapabilityKeys]
    );

    const isIndoorOrMixedMission = editorOperationEnvironment === 'indoor' || editorOperationEnvironment === 'mixed';
    const selectedAreaSlamPlacement = React.useMemo(
        () => readSlamPlacement(selectedAreaDetail),
        [selectedAreaDetail]
    );
    const selectedAreaSlamMap = selectedAreaDetail?.slamMap || null;
    const canProjectLocalMissionToGps = Boolean(selectedAreaSlamPlacement);

    React.useEffect(() => {
        if (isIndoorOrMixedMission) {
            setRoutePreviewMode('local');
        } else {
            setRoutePreviewMode('mapbox');
        }
    }, [isIndoorOrMixedMission]);

    React.useEffect(() => {
        if (!patterns.length) {
            setPatternPreviewPointsById(new Map());
            setPatternPreviewLoading(false);
            return;
        }

        let cancelled = false;
        setPatternPreviewLoading(true);

        void Promise.all(
            patterns.map(async (pattern) => {
                try {
                    const response = await axios.get(ENDPOINTS.PATTERN_POINTS(pattern.id), {
                        withCredentials: true,
                    });
                    return [pattern.id, parseApiList<PatternPointItem>(response.data)] as const;
                } catch (error) {
                    console.warn(`Failed to load preview points for pattern ${pattern.id}`, error);
                    return [pattern.id, [] as PatternPointItem[]] as const;
                }
            })
        )
            .then((entries) => {
                if (cancelled) {
                    return;
                }

                setPatternPreviewPointsById(new Map(entries));
            })
            .finally(() => {
                if (!cancelled) {
                    setPatternPreviewLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [patterns]);

    // Fetch a signed image URL for a SLAM map (cached)
    const loadSlamMapImageUrl = React.useCallback(async (slamMapId: number) => {
        if (slamMapImageUrls[slamMapId]) return;
        try {
            const res = await axios.get(ENDPOINTS.SLAM_MAP_IMAGE_URL(slamMapId), { withCredentials: true });
            const url = res.data?.data?.url;
            if (url) setSlamMapImageUrls((prev) => ({ ...prev, [slamMapId]: url }));
        } catch {
            // silently ignore
        }
    }, [slamMapImageUrls]);

    // Resolve SLAM maps for the confirmed area (indoor/mixed only). Mission creation
    // uses only the selected area's own map, not parent or company-wide maps.
    React.useEffect(() => {
        if (!confirmedAreaId || !isIndoorOrMixedMission) {
            setAreaSlamMaps([]);
            setAreaSlamMapsChecked(false);
            setConfirmedSlamMapIds([]);
            return;
        }
        setAreaSlamMapsLoading(true);
        setAreaSlamMapsChecked(false);
        const ownMap = selectedAreaDetail?.id === confirmedAreaId && selectedAreaDetail.slamMap
            ? selectedAreaDetail.slamMap
            : null;
        const nextMaps = ownMap && ownMap.id ? [ownMap] : [];
        setAreaSlamMaps(nextMaps);
        setAreaSlamMapsChecked(true);
        setAreaSlamMapsLoading(false);
        setConfirmedSlamMapIds(nextMaps.map((map) => map.id));
        nextMaps.forEach((map) => {
            if (map.imageStoragePath && !slamMapImageUrls[map.id]) {
                void loadSlamMapImageUrl(map.id);
            }
        });
    }, [confirmedAreaId, isIndoorOrMixedMission, loadSlamMapImageUrl, selectedAreaDetail, slamMapImageUrls]);

    // Auto-fill speed and turn radius from vehicle capabilities when device changes
    React.useEffect(() => {
        const caps = selectedDevice?.deviceType?.vehicleCapabilities;
        if (!caps) {
            return;
        }

        const updates: Partial<MissionFormValues> = {};

        const cruiseSpeed = Number(caps.defaultCruiseSpeed);
        if (Number.isFinite(cruiseSpeed) && cruiseSpeed > 0) {
            updates.defaultSpeedMps = cruiseSpeed;
        }

        const turnRadius = Number(caps.defaultTurnRadiusMeters);
        if (Number.isFinite(turnRadius) && turnRadius >= 0) {
            updates.turnRadiusMeters = turnRadius;
        }

        if (Object.keys(updates).length > 0) {
            form.setFieldsValue(updates);
        }
    }, [selectedDevice, form]);

    // Sync draft commands whenever points or device modules change
    React.useEffect(() => {
        setDraftCommands((current) => syncDraftCommandsWithPoints(
            draftPoints,
            current,
            enabledOscarModuleSettings,
            oscarModuleExecutionMode,
            commandProtocol,
            isIndoorOrMixedMission ? (selectedAreaSlamPlacement?.bearingDegrees ?? 0) : 0,
            Boolean(editorPatternId)
        ));
    }, [commandProtocol, draftPoints, editorPatternId, enabledOscarModuleSettings, isIndoorOrMixedMission, oscarModuleExecutionMode, selectedAreaSlamPlacement]);

    const selectedAreaLabel = React.useMemo(
        () => selectedAreaForEditor
            ? areaPathMap.get(selectedAreaForEditor.id) || selectedAreaForEditor.name
            : null,
        [areaPathMap, selectedAreaForEditor]
    );

    const skipsAreaSelection = editorOperationEnvironment === 'scan';
    const isAreaConfirmed = skipsAreaSelection || Boolean(editorAreaId && confirmedAreaId === editorAreaId);
    const areaHasDefinedGeometry = Boolean(selectedAreaForEditor?.geom?.coordinates);
    const needsSlamMapStep = (editorOperationEnvironment === 'indoor' || editorOperationEnvironment === 'mixed') && !areaHasDefinedGeometry;
    // For indoor/mixed, device step only unlocks after SLAM map step is confirmed
    // For scan missions, area is not required so device step only needs device selection
    const isDeviceConfirmed = Boolean(
        editorDeviceId && confirmedDeviceId === editorDeviceId
        && (skipsAreaSelection || isAreaConfirmed)
        && (!needsSlamMapStep || slamMapStepConfirmed)
    );
    // For slam_scan missions, pattern step is skipped
    const isSlamScan = isScanMission || editorMissionType === 'slam_scan';
    const isPatternSelectionConfirmed = isSlamScan
        ? true
        : (patternSelectionConfirmed && confirmedPatternId === (editorPatternId ?? null));
    const needsModuleStep = isDeviceConfirmed && isOscarModuleMode && !isSlamScan;
    const isModuleConfigurationConfirmed = !needsModuleStep || moduleStepConfirmed;
    const canShowMissionDetails = isAreaConfirmed && isDeviceConfirmed && isModuleConfigurationConfirmed && isPatternSelectionConfirmed;
    const showMissionDraftSections = canShowMissionDetails
        && (mode === 'edit' || expandedWorkflowStep === 'details');
    const robotStepNumber = skipsAreaSelection ? 1 : needsSlamMapStep ? 3 : 2;
    const robotStepTitle = `${robotStepNumber}. ${isExternalApiMission ? 'Selected robot' : 'Select robot'}`;
    const moduleStepNumber = skipsAreaSelection
        ? 2
        : needsSlamMapStep
            ? 4
            : 3;
    const patternStepNumber = moduleStepNumber + (needsModuleStep ? 1 : 0);
    const missionDetailsStepNumber = skipsAreaSelection
        ? (needsModuleStep ? 3 : 2)
        : needsSlamMapStep
            ? (isSlamScan ? 4 : (needsModuleStep ? 6 : 5))
            : (isSlamScan ? 3 : (needsModuleStep ? 5 : 4));
    const moduleStepTitle = `${moduleStepNumber}. Configure modules`;
    const patternStepTitle = `${patternStepNumber}. Select pattern`;
    const missionDetailsStepTitle = `${missionDetailsStepNumber}. Mission details`;
    const scanInstructionsStepTitle = `${missionDetailsStepNumber + 1}. Scanning instructions`;
    const workflowChangeLabel = mode === 'edit' ? 'Edit' : 'Change';
    const showScanMissionInstructions = showMissionDraftSections && isSlamScan;

    const generatedMissionMetadata = React.useMemo(() => {
        const environmentLabel = OPERATION_ENVIRONMENT_OPTIONS
            .find((option) => option.value === editorOperationEnvironment)?.label
            || 'Outdoor';
        const shortEnvironmentLabel = environmentLabel.replace(/\s*\([^)]*\)/g, '').trim();
        const areaLabel = skipsAreaSelection
            ? (scanMode === 'manual' ? 'Manual SLAM scan' : 'Autonomous exploration')
            : selectedAreaLabel || 'Selected area';
        const robotLabel = selectedDevice?.label || 'Selected robot';
        const routeLabel = isSlamScan
            ? 'SLAM scan'
            : selectedPattern?.name || 'Manual route';
        const randomSuffix = `${missionNameWordRef.current} ${missionNameNumberRef.current}`;

        return {
            name: compactMissionText(`${shortEnvironmentLabel} - ${areaLabel} ${randomSuffix}`, 150),
            description: compactMissionText(
                `${shortEnvironmentLabel} mission for ${areaLabel}, assigned to ${robotLabel}, using ${routeLabel}.`,
                500
            ),
        };
    }, [
        editorOperationEnvironment,
        isSlamScan,
        scanMode,
        selectedAreaLabel,
        selectedDevice,
        selectedPattern,
        skipsAreaSelection,
    ]);

    React.useEffect(() => {
        if (mode !== 'create') {
            return;
        }

        form.setFieldsValue(generatedMissionMetadata);
    }, [form, generatedMissionMetadata, mode]);

    const patternPreviewPattern = React.useMemo(
        () => patterns.find((pattern) => pattern.id === patternPreviewPatternId) || null,
        [patternPreviewPatternId, patterns]
    );

    const patternPreviewGraphicById = React.useMemo(() => {
        const previews = new Map<number, PatternPreviewPoint[]>();
        patterns.forEach((pattern) => {
            previews.set(
                pattern.id,
                buildPatternPreviewPoints(
                    patternPreviewPointsById.get(pattern.id) || [],
                    pattern,
                    selectedAreaForEditor
                )
            );
        });
        return previews;
    }, [patternPreviewPointsById, patterns, selectedAreaForEditor]);

    // Template image corners from area quad geometry for Mapbox ImageSource overlay
    const areaTemplateCorners = React.useMemo(
        () => extractAreaQuadCorners(operationMapGeometry),
        [operationMapGeometry]
    );

    // Pre-compute GPS-projected points (with index + label) and map bearing per pattern
    const patternGpsPointsById = React.useMemo(() => {
        type GpsPoint = { id: number; gpsLat: number; gpsLon: number; pointIndex: number; label: string | null };
        const result = new Map<number, { points: GpsPoint[]; bearing: number }>();
        patterns.forEach((pattern) => {
            const rawPoints = patternPreviewPointsById.get(pattern.id) || [];
            const sorted = [...rawPoints].sort((a, b) => a.pointIndex - b.pointIndex);
            const projected = sorted
                .map((point) => {
                    const localPoint = isIndoorOrMixedMission
                        ? resolvePatternPointLocalForMission(point, pattern, selectedAreaForEditor, selectedAreaSlamPlacement)
                        : null;
                    const gps = isIndoorOrMixedMission
                        ? (
                            localPoint && selectedAreaSlamPlacement
                                ? localMetersToGps(localPoint.x, localPoint.y, selectedAreaSlamPlacement)
                                : null
                        )
                        : projectPatternPointToGps(
                            point,
                            operationMapGeometry,
                            {
                                boxWidth: pattern.definition?.boxWidth,
                                boxHeight: pattern.definition?.boxHeight,
                                templateDimensions: selectedAreaForEditor?.areaType?.templateDimensions,
                            }
                        );
                    if (!gps) return null;
                    return {
                        id: point.id,
                        gpsLat: gps.lat,
                        gpsLon: gps.lng,
                        pointIndex: point.pointIndex,
                        label: point.label ?? null,
                    };
                })
                .filter((p): p is GpsPoint => p !== null);

            // Compute bearing from first to second point to align row W→E
            let bearing = 0;
            if (projected.length >= 2) {
                const compassBearing = computeCompassBearing(
                    projected[0].gpsLat, projected[0].gpsLon,
                    projected[1].gpsLat, projected[1].gpsLon
                );
                // Rotate map so first segment appears horizontal (W→E): bearing = θ - 90
                bearing = ((compassBearing - 90) % 360 + 360) % 360;
            }

            result.set(pattern.id, { points: projected, bearing });
        });
        return result;
    }, [isIndoorOrMixedMission, operationMapGeometry, patterns, patternPreviewPointsById, selectedAreaForEditor, selectedAreaSlamPlacement]);

    const resolvedDraftPoints = React.useMemo(
        () => {
            const directionInputPoints = patternGuidedDirections
                ? draftPoints
                : draftPoints.map((point) => (
                    (point.patternPointId || point.source === 'pattern')
                    && getPointDirectionMode(point) !== 'manual'
                        ? updatePointDirectionParams(point, 'auto', null)
                        : point
                ));
            const resolved = isIndoorOrMixedMission
                ? resolveMissionPointDirectionsFromXY(
                    directionInputPoints,
                    (point) => readDraftPointLocalCoordinates(point, selectedAreaSlamPlacement)
                )
                : resolveMissionPointDirections(directionInputPoints);
            return resolved;
        },
        [draftPoints, isIndoorOrMixedMission, patternGuidedDirections, selectedAreaSlamPlacement]
    );

    const editorPreviewPoints = React.useMemo(
        () => resolvedDraftPoints
            .map((point, index) => {
                const direction = readPointDirectionVector(point);
                return {
                    id: point.key,
                    lat: point.lat ?? NaN,
                    lng: point.lng ?? NaN,
                    label: getMissionPointMarkerLabel(resolvedDraftPoints, point, index),
                    selected: point.key === activePointKey,
                    headingDegrees: isIndoorOrMixedMission
                        ? directionVectorToProjectedHeadingDegrees(direction, selectedAreaSlamPlacement)
                        : directionVectorToHeadingDegrees(direction),
                    directionMode: getPointDirectionMode(point),
                };
            })
            .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng)),
        [activePointKey, isIndoorOrMixedMission, resolvedDraftPoints, selectedAreaSlamPlacement]
    );

    const localRouteControlPoints = React.useMemo<LocalRouteControlPoint[]>(() => (
        resolvedDraftPoints.map((point) => {
            const local = readDraftPointLocalCoordinates(point, selectedAreaSlamPlacement);
            return {
                x: local?.x ?? null,
                y: local?.y ?? null,
                source: point.source || (point.patternPointId ? 'pattern' : 'custom'),
                patternPointId: point.patternPointId ?? null,
                params: point.params || {},
            };
        })
    ), [resolvedDraftPoints, selectedAreaSlamPlacement]);

    const localEditorPreviewPaths = React.useMemo<LocalMapPath[]>(() => {
        if (!isIndoorOrMixedMission) {
            return [];
        }
        if (editorNavigationMode === 'waypoint_goals') {
            return localRouteControlPoints.slice(0, -1).flatMap((point, index) => {
                const next = localRouteControlPoints[index + 1];
                if (
                    !Number.isFinite(point.x) || !Number.isFinite(point.y)
                    || !Number.isFinite(next.x) || !Number.isFinite(next.y)
                ) {
                    return [];
                }
                return [{
                    id: `waypoint-order-${index + 1}`,
                    coordinates: [
                        { x: Number(point.x), y: Number(point.y) },
                        { x: Number(next.x), y: Number(next.y) },
                    ],
                }];
            });
        }
        const routeCfg = { turnRadiusMeters: turnRadiusMeters ?? undefined };
        return buildMissionPreviewPathsDubinsFromXY(localRouteControlPoints, routeCfg, {
            costMode,
            sweepAngles,
            windowSize,
        });
    }, [costMode, editorNavigationMode, isIndoorOrMixedMission, localRouteControlPoints, sweepAngles, turnRadiusMeters, windowSize]);

    const editorPreviewPaths = React.useMemo<MapPath[]>(() => {
        if (isIndoorOrMixedMission) {
            if (!selectedAreaSlamPlacement) {
                return [];
            }
            return localEditorPreviewPaths.map((path) => ({
                id: path.id,
                selected: path.selected,
                warning: path.warning,
                previewOnly: editorNavigationMode === 'waypoint_goals',
                coordinates: path.coordinates.map((point) => localMetersToGps(point.x, point.y, selectedAreaSlamPlacement)),
            }));
        }
        if (editorNavigationMode === 'waypoint_goals') {
            return resolvedDraftPoints.slice(0, -1).flatMap((point, index) => {
                const next = resolvedDraftPoints[index + 1];
                if (
                    !Number.isFinite(point.lat) || !Number.isFinite(point.lng)
                    || !Number.isFinite(next.lat) || !Number.isFinite(next.lng)
                ) {
                    return [];
                }
                return [{
                    id: `waypoint-order-${index + 1}`,
                    previewOnly: true,
                    coordinates: [
                        { lat: Number(point.lat), lng: Number(point.lng) },
                        { lat: Number(next.lat), lng: Number(next.lng) },
                    ],
                }];
            });
        }
        const routeCfg = { turnRadiusMeters: turnRadiusMeters ?? undefined };
        return buildMissionPreviewPathsDubins(resolvedDraftPoints, routeCfg, {
            costMode,
            sweepAngles,
            windowSize,
        });
    }, [costMode, editorNavigationMode, isIndoorOrMixedMission, localEditorPreviewPaths, resolvedDraftPoints, selectedAreaSlamPlacement, sweepAngles, turnRadiusMeters, windowSize]);

    const invalidAreaPathIndexes = React.useMemo(() => editorPreviewPaths.flatMap((path, index) => (
        isPathInsideArea(
            path.coordinates.map((coordinate) => [coordinate.lng, coordinate.lat] as [number, number]),
            operationMapGeometry
        ) ? [] : [index]
    )), [editorPreviewPaths, operationMapGeometry]);
    const invalidAreaPathIndexSet = React.useMemo(
        () => new Set(invalidAreaPathIndexes),
        [invalidAreaPathIndexes]
    );
    const areaValidatedEditorPreviewPaths = React.useMemo(() => editorPreviewPaths.map((path, index) => ({
        ...path,
        warning: Boolean(path.warning) || invalidAreaPathIndexSet.has(index),
    })), [editorPreviewPaths, invalidAreaPathIndexSet]);

    const localRoutePreviewPoints = React.useMemo<LocalRoutePreviewPoint[]>(() => (
        resolvedDraftPoints
            .map((point, index): LocalRoutePreviewPoint | null => {
                const local = readDraftPointLocalCoordinates(point, selectedAreaSlamPlacement);
                if (!local || !selectedAreaSlamMap) {
                    return null;
                }
                const pixel = localMetersToSlamPixel(local.x, local.y, selectedAreaSlamMap);
                const width = toFiniteNumber(selectedAreaSlamMap.imageWidth) ?? 800;
                const height = toFiniteNumber(selectedAreaSlamMap.imageHeight) ?? 600;
                if (!pixel || !width || !height) {
                    return null;
                }
                return {
                    id: point.key,
                    x: local.x,
                    y: local.y,
                    pixelX: pixel.x,
                    pixelY: pixel.y,
                    label: getMissionPointMarkerLabel(resolvedDraftPoints, point, index),
                    selected: point.key === activePointKey,
                    headingDegrees: directionVectorToHeadingDegrees(readPointDirectionVector(point)),
                    directionMode: getPointDirectionMode(point),
                };
            })
            .filter((point): point is LocalRoutePreviewPoint => point !== null)
    ), [activePointKey, resolvedDraftPoints, selectedAreaSlamMap, selectedAreaSlamPlacement]);

    const localRoutePreviewPaths = React.useMemo<LocalRoutePreviewPath[]>(() => {
        if (!selectedAreaSlamMap) {
            return [];
        }
        return localEditorPreviewPaths
            .map((path) => {
                const points = path.coordinates
                    .map((point) => localMetersToSlamPixel(point.x, point.y, selectedAreaSlamMap))
                    .filter((point): point is { x: number; y: number } => Boolean(point))
                    .map((point) => ({ pixelX: point.x, pixelY: point.y }));
                return {
                    id: path.id,
                    points,
                    selected: path.selected,
                    warning: path.warning,
                };
            })
            .filter((path) => path.points.length >= 2);
    }, [localEditorPreviewPaths, selectedAreaSlamMap]);
    const areaValidatedLocalRoutePreviewPaths = React.useMemo(() => localRoutePreviewPaths.map((path, index) => ({
        ...path,
        warning: Boolean(path.warning) || invalidAreaPathIndexSet.has(index),
    })), [invalidAreaPathIndexSet, localRoutePreviewPaths]);

    const localRouteTemplateQuadPixels = React.useMemo(() => {
        if (!selectedAreaSlamMap) {
            return null;
        }
        const localQuad = extractLocalQuad(selectedAreaForEditor?.configurations?.localGeom)
            || extractGpsQuadAsLocal(selectedAreaForEditor?.geom, selectedAreaSlamPlacement);
        if (!localQuad) {
            return null;
        }
        const pixels = localQuad
            .map((point) => localMetersToSlamPixel(point.x, point.y, selectedAreaSlamMap))
            .filter((point): point is { x: number; y: number } => Boolean(point));
        return pixels.length === 4 ? pixels : null;
    }, [selectedAreaForEditor?.configurations?.localGeom, selectedAreaForEditor?.geom, selectedAreaSlamMap, selectedAreaSlamPlacement]);

    const localRoutePreviewImageUrl = selectedAreaSlamMap?.id
        ? (slamMapImageUrls[selectedAreaSlamMap.id] || (selectedAreaSlamMap as any).imageUrl || (selectedAreaSlamMap as any).previewUrl || null)
        : null;
    const handleMoveDraftPoint = React.useCallback((index: number, direction: -1 | 1) => {
        captureRouteUndo();
        setDraftPoints((current) => {
            const nextIndex = index + direction;
            if (nextIndex < 0 || nextIndex >= current.length) {
                return current;
            }

            const clone = [...current];
            const [moved] = clone.splice(index, 1);
            clone.splice(nextIndex, 0, moved);
            return clone;
        });
    }, [captureRouteUndo]);

    const handleDraftPointChange = React.useCallback((index: number, updates: Partial<MissionPointDraft>) => {
        captureRouteUndo();
        setDraftPoints((current) => current.map((point, pointIndex) => (
            pointIndex === index ? { ...point, ...updates } : point
        )));
    }, [captureRouteUndo]);

    const handleDraftPointLocationChange = React.useCallback((pointKey: string, location: { lat: number; lng: number }) => {
        captureRouteUndo();
        setDraftPoints((current) => current.map((point) => (
            point.key === pointKey
                ? isIndoorOrMixedMission && selectedAreaSlamPlacement
                    ? buildPointWithLocalCoordinates(
                        { ...point, lat: location.lat, lng: location.lng },
                        gpsToLocalMeters(location.lat, location.lng, selectedAreaSlamPlacement),
                        selectedAreaSlamPlacement,
                        selectedAreaSlamMap?.id ?? null
                    )
                    : { ...point, lat: location.lat, lng: location.lng }
                : point
        )));
    }, [captureRouteUndo, isIndoorOrMixedMission, selectedAreaSlamMap?.id, selectedAreaSlamPlacement]);

    const handleDraftPointLocalLocationChange = React.useCallback((pointKey: string, local: { x: number; y: number }) => {
        captureRouteUndo();
        setDraftPoints((current) => current.map((point) => (
            point.key === pointKey
                ? buildPointWithLocalCoordinates(point, local, selectedAreaSlamPlacement, selectedAreaSlamMap?.id ?? null)
                : point
        )));
    }, [captureRouteUndo, selectedAreaSlamMap?.id, selectedAreaSlamPlacement]);

    const handleDraftPointDirectionModeChange = React.useCallback((index: number, mode: PointDirectionMode) => {
        captureRouteUndo();
        setDraftPoints((current) => current.map((point, pointIndex) => {
            if (pointIndex !== index) {
                return point;
            }

            if (mode === 'manual') {
                const resolvedPoint = resolvedDraftPoints[pointIndex];
                const fallbackDirection = readPointDirectionVector(resolvedPoint) || headingDegreesToDirectionVector(0);
                return updatePointDirectionParams(point, 'manual', fallbackDirection);
            }

            return updatePointDirectionParams(point, mode, null);
        }));
    }, [captureRouteUndo, resolvedDraftPoints]);

    const handleDraftPointHeadingChange = React.useCallback((index: number, headingDegrees: number | null) => {
        if (!Number.isFinite(Number(headingDegrees))) {
            return;
        }

        captureRouteUndo();
        setDraftPoints((current) => current.map((point, pointIndex) => (
            pointIndex === index
                ? updatePointDirectionParams(point, 'manual', headingDegreesToDirectionVector(Number(headingDegrees)))
                : point
        )));
    }, [captureRouteUndo]);


    const handleRemoveDraftPoint = React.useCallback((index: number) => {
        captureRouteUndo();
        const removedKey = draftPoints[index]?.key;
        const nextPoints = draftPoints.filter((_, pointIndex) => pointIndex !== index);
        setDraftPoints(nextPoints);

        if (removedKey && removedKey === activePointKey) {
            const fallbackPoint = nextPoints[Math.min(index, nextPoints.length - 1)];
            setActivePointKey(fallbackPoint?.key ?? null);
        }
    }, [activePointKey, captureRouteUndo, draftPoints]);

    const handleDraftCommandChange = React.useCallback((commandKey: string, updates: Partial<MissionCommandDraft>) => {
        captureRouteUndo();
        setDraftCommands((current) => current.map((command) => (
            command.key === commandKey
                ? {
                    ...command,
                    ...updates,
                    source: updates.source || command.source || 'manual',
                }
                : command
        )));
    }, [captureRouteUndo]);

    const handleAddCommandForActivePoint = React.useCallback((kind: string) => {
        if (!activePointKey) {
            return;
        }

        // Compute max existing executionGroup for this point's commands so the
        // new command gets its own sequential row (not parallel to the primary).
        const existingPointCommands = draftCommands.filter((c) => c.missionPointKey === activePointKey);
        const maxGroup = existingPointCommands.reduce(
            (max, c) => Math.max(max, c.executionGroup ?? 0),
            0
        );

        const defaultOption = getDefaultCommandForKind(kind, commandProtocol);
        const nextCommand: MissionCommandDraft = {
            key: `draft-command-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            missionPointKey: activePointKey,
            label: '',
            kind,
            commandName: defaultOption.value,
            commandCode: defaultOption.commandCode,
            frame: null,
            autoContinue: true,
            altitude: null,
            params: {},
            source: 'manual',
            isPrimary: false,
            executionGroup: maxGroup + 1,
        };

        captureRouteUndo();
        setDraftCommands((current) => {
            const pointCommands = current.filter((command) => command.missionPointKey === activePointKey);
            const insertAfterLastForPoint = pointCommands.length > 0
                ? current.reduce((lastIndex, command, index) => (
                    command.missionPointKey === activePointKey ? index : lastIndex
                ), -1)
                : -1;
            if (insertAfterLastForPoint < 0) {
                return [...current, nextCommand];
            }

            const clone = [...current];
            clone.splice(insertAfterLastForPoint + 1, 0, nextCommand);
            return clone;
        });
        setActiveCommandKey(nextCommand.key);
    }, [activePointKey, captureRouteUndo, commandProtocol, draftCommands]);

    const handleRemoveDraftCommand = React.useCallback((commandKey: string) => {
        const target = draftCommands.find((command) => command.key === commandKey);
        if (!target || target.isPrimary) {
            return;
        }

        captureRouteUndo();
        setDraftCommands((current) => {
            const next = current.filter((command) => command.key !== commandKey);
            const fallback = next.find((command) => command.missionPointKey === target.missionPointKey) || null;
            setActiveCommandKey(fallback?.key ?? null);
            return next;
        });
    }, [captureRouteUndo, draftCommands]);

    /** Replace all commands for the active point with a new set (used by DnD in CommandExecutionGrid). */
    const handlePointCommandsChange = React.useCallback((updatedPointCommands: MissionCommandDraft[]) => {
        captureRouteUndo();
        setDraftCommands((current) => {
            const otherCommands = current.filter((c) => c.missionPointKey !== activePointKey);
            return [...otherCommands, ...updatedPointCommands];
        });
    }, [activePointKey, captureRouteUndo]);

    /** Add a command that runs in parallel with an existing execution group step. */
    const handleAddCommandToGroup = React.useCallback((kind: string, targetGroup: number) => {
        if (!activePointKey) return;

        const defaultOption = getDefaultCommandForKind(kind, commandProtocol);
        const cmdKey = `draft-command-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const nextCommand: MissionCommandDraft = {
            key: cmdKey,
            missionPointKey: activePointKey,
            label: '',
            kind,
            commandName: defaultOption.value,
            commandCode: defaultOption.commandCode,
            frame: null,
            autoContinue: true,
            altitude: null,
            params: {},
            source: 'manual',
            isPrimary: false,
            executionGroup: targetGroup,
        };

        captureRouteUndo();
        setDraftCommands((current) => {
            const lastPointCmdIdx = current.reduce(
                (i, c, idx) => (c.missionPointKey === activePointKey ? idx : i),
                -1
            );
            if (lastPointCmdIdx < 0) return [...current, nextCommand];
            return [
                ...current.slice(0, lastPointCmdIdx + 1),
                nextCommand,
                ...current.slice(lastPointCmdIdx + 1),
            ];
        });

        setActiveCommandKey(cmdKey);
    }, [activePointKey, captureRouteUndo, commandProtocol]);

    const recalculateOscarModuleCommands = React.useCallback((resetConfigs: boolean) => {
        if (isMavlinkCommandMode) {
            return;
        }

        setDraftCommands((current) => {
            const nextCommands: MissionCommandDraft[] = [];
            draftPoints.forEach((point) => {
                const existingPrimary = current.find((command) => command.missionPointKey === point.key && command.isPrimary) || null;
                const generatedPrimary = buildGeneratedPrimaryCommand(
                    point,
                    commandProtocol,
                    isIndoorOrMixedMission ? (selectedAreaSlamPlacement?.bearingDegrees ?? 0) : 0
                );
                nextCommands.push(existingPrimary ? { ...existingPrimary, ...generatedPrimary, key: existingPrimary.key } : generatedPrimary);

                const existingSecondary = current.filter((command) => !command.isPrimary && command.missionPointKey === point.key);
                rebuildModuleCommandsForPoint(
                    point,
                    existingSecondary,
                    enabledOscarModuleSettings,
                    oscarModuleExecutionMode,
                    resetConfigs,
                    Boolean(editorPatternId)
                ).forEach((command) => nextCommands.push(command));
            });
            return nextCommands;
        });

        message.success(resetConfigs ? 'Module commands reset from defaults' : 'Module commands recalculated');
    }, [commandProtocol, draftPoints, editorPatternId, enabledOscarModuleSettings, isIndoorOrMixedMission, isMavlinkCommandMode, oscarModuleExecutionMode, selectedAreaSlamPlacement]);

    const handleToggleOscarModule = React.useCallback((moduleKey: string, enabled: boolean) => {
        setOscarModuleSettings((current) => ({
            ...current,
            [moduleKey]: {
                ...(current[moduleKey] || { moduleKey, label: moduleKey, config: {} }),
                enabled,
            },
        }));
        setModuleStepConfirmed(false);
    }, []);

    const handleOscarModuleConfigChange = React.useCallback((moduleKey: string, config: Record<string, any>) => {
        setOscarModuleSettings((current) => ({
            ...current,
            [moduleKey]: {
                ...(current[moduleKey] || { moduleKey, label: moduleKey, enabled: true }),
                config: cloneJsonObject(config),
            },
        }));
        setModuleStepConfirmed(false);
    }, []);

    const handleConfirmOscarModuleSettings = React.useCallback(() => {
        recalculateOscarModuleCommands(false);
        setModuleStepConfirmed(true);
        setExpandedWorkflowStep('pattern');
    }, [recalculateOscarModuleCommands]);

    const localDefaultPointPlacement = React.useMemo(() => {
        if (!isIndoorOrMixedMission) {
            return null;
        }
        if (routePreviewMode === 'local' && localPreviewCenter) {
            return localPreviewCenter;
        }
        if (routePreviewMode === 'mapbox' && previewMapCenter && selectedAreaSlamPlacement) {
            return gpsToLocalMeters(previewMapCenter.lat, previewMapCenter.lng, selectedAreaSlamPlacement);
        }
        if (localPreviewCenter) {
            return localPreviewCenter;
        }
        if (previewMapCenter && selectedAreaSlamPlacement) {
            return gpsToLocalMeters(previewMapCenter.lat, previewMapCenter.lng, selectedAreaSlamPlacement);
        }

        const localQuad = extractLocalQuad(selectedAreaForEditor?.configurations?.localGeom);
        if (localQuad?.length) {
            return {
                x: localQuad.reduce((sum, point) => sum + point.x, 0) / localQuad.length,
                y: localQuad.reduce((sum, point) => sum + point.y, 0) / localQuad.length,
            };
        }

        if (selectedAreaSlamMap) {
            const width = toFiniteNumber(selectedAreaSlamMap.imageWidth) ?? 800;
            const height = toFiniteNumber(selectedAreaSlamMap.imageHeight) ?? 600;
            return slamPixelToLocalMeters(width / 2, height / 2, selectedAreaSlamMap);
        }

        return null;
    }, [isIndoorOrMixedMission, localPreviewCenter, previewMapCenter, routePreviewMode, selectedAreaForEditor?.configurations?.localGeom, selectedAreaSlamMap, selectedAreaSlamPlacement]);

    const nextPointPlacement = React.useMemo(() => {
        if (isIndoorOrMixedMission && localDefaultPointPlacement && selectedAreaSlamPlacement) {
            return localMetersToGps(localDefaultPointPlacement.x, localDefaultPointPlacement.y, selectedAreaSlamPlacement);
        }

        return previewMapCenter
            ? { lat: previewMapCenter.lat, lng: previewMapCenter.lng }
            : computeGeometryCenter(operationMapGeometry);
    }, [isIndoorOrMixedMission, localDefaultPointPlacement, operationMapGeometry, previewMapCenter, selectedAreaSlamPlacement]);


    const activePointIndex = React.useMemo(
        () => draftPoints.findIndex((point) => point.key === activePointKey),
        [activePointKey, draftPoints]
    );

    const activePoint = activePointIndex >= 0 ? draftPoints[activePointIndex] : null;
    const resolvedActivePoint = activePointIndex >= 0 ? resolvedDraftPoints[activePointIndex] : null;
    const activePointLocal = activePoint ? readDraftPointLocalCoordinates(activePoint, selectedAreaSlamPlacement) : null;
    const activePointIsPattern = Boolean(activePoint?.patternPointId || activePoint?.source === 'pattern');
    const activePointDirectionMode = activePoint
        ? (getPointDirectionMode(resolvedActivePoint) || (activePointIsPattern ? 'pattern' : 'auto'))
        : null;
    const activePointHeadingDegrees = directionVectorToHeadingDegrees(readPointDirectionVector(resolvedActivePoint));
    const activePointDirectionOptions = React.useMemo(
        () => (
            activePointIsPattern
                ? patternGuidedDirections
                    ? [
                        { value: 'pattern', label: 'Pattern corridor' },
                        { value: 'manual', label: 'Manual heading' },
                    ]
                    : [
                        { value: 'auto', label: 'Auto from path' },
                        { value: 'manual', label: 'Manual heading' },
                    ]
                : [
                    { value: 'auto', label: 'Auto from geometry' },
                    { value: 'manual', label: 'Manual heading' },
                ]
        ),
        [activePointIsPattern, patternGuidedDirections]
    );

    const activePointCommands = React.useMemo(
        () => draftCommands.filter((command) => command.missionPointKey === activePointKey),
        [activePointKey, draftCommands]
    );

    React.useEffect(() => {
        if (!activePointKey) {
            setActiveCommandKey(null);
            return;
        }

        const firstCommand = draftCommands.find((command) => command.missionPointKey === activePointKey) || null;
        if (!firstCommand) {
            setActiveCommandKey(null);
            return;
        }

        if (activeCommandKey && draftCommands.some((command) => command.key === activeCommandKey && command.missionPointKey === activePointKey)) {
            return;
        }

        setActiveCommandKey(firstCommand.key);
    }, [activeCommandKey, activePointKey, draftCommands]);

    const activeCommandIndex = React.useMemo(
        () => draftCommands.findIndex((command) => command.key === activeCommandKey),
        [activeCommandKey, draftCommands]
    );
    const activeCommand = activeCommandIndex >= 0 ? draftCommands[activeCommandIndex] : null;

    const draftPointSummary = React.useMemo(() => {
        const mappedCount = draftPoints.filter((point) => (
            isIndoorOrMixedMission
                ? Number.isFinite(toFiniteNumber(point.params?.local?.x)) && Number.isFinite(toFiniteNumber(point.params?.local?.y))
                : Number.isFinite(point.lat) && Number.isFinite(point.lng)
        )).length;
        const patternCount = draftPoints.filter((point) => Boolean(point.patternPointId)).length;
        const customCount = draftPoints.length - patternCount;
        const requiredCount = draftPoints.filter((point) => point.isRequired).length;
        const commandCount = draftCommands.length;

        return {
            total: draftPoints.length,
            mappedCount,
            missingCount: draftPoints.length - mappedCount,
            patternCount,
            customCount,
            requiredCount,
            commandCount,
        };
    }, [draftCommands.length, draftPoints, isIndoorOrMixedMission]);

    const routeFallbackWarningCount = React.useMemo(() => (
        isIndoorOrMixedMission
            ? localEditorPreviewPaths.filter((path) => path.warning).length
            : editorPreviewPaths.filter((path) => path.warning).length
    ), [editorPreviewPaths, isIndoorOrMixedMission, localEditorPreviewPaths]);
    const hasStartPoint = React.useMemo(
        () => draftPoints.some((point) => point.actionType === 'start'),
        [draftPoints]
    );
    const hasEndPoint = React.useMemo(
        () => draftPoints.some((point) => point.actionType === 'end'),
        [draftPoints]
    );
    const hasBlockingRouteValidation = !isSlamScan && (
        invalidAreaPathIndexes.length > 0
        || !hasStartPoint
        || !hasEndPoint
    );

    const handleAddCustomPoint = React.useCallback((actionType: string) => {
        const placement = nextPointPlacement;
        const localPlacement = isIndoorOrMixedMission ? localDefaultPointPlacement : null;
        const pointKey = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const resolvedActionType = (
            actionType === 'custom' && editorNavigationMode === 'waypoint_goals'
        )
            ? 'measure'
            : actionType;

        captureRouteUndo();
        setDraftPoints((current) => {
            const next = [...current];
            const basePoint: MissionPointDraft = {
                key: pointKey,
                label: actionType === 'start' ? 'Start' : actionType === 'end' ? 'End' : '',
                actionType: resolvedActionType,
                patternPointId: null,
                lat: placement?.lat ?? null,
                lng: placement?.lng ?? null,
                waitSeconds: null,
                speedOverride: null,
                isRequired: true,
                source: 'custom',
                params: {},
            };
            const newPoint = localPlacement
                ? buildPointWithLocalCoordinates(basePoint, localPlacement, selectedAreaSlamPlacement, selectedAreaSlamMap?.id ?? null)
                : basePoint;

            if (actionType === 'start') {
                const firstPatternIndex = next.findIndex((point) => Boolean(point.patternPointId));
                const insertIndex = firstPatternIndex >= 0 ? firstPatternIndex : 0;
                next.splice(insertIndex, 0, newPoint);
                return next;
            }

            if (actionType === 'end') {
                const patternIndices = next
                    .map((point, index) => (point.patternPointId ? index : -1))
                    .filter((index) => index >= 0);
                const insertIndex = patternIndices.length > 0
                    ? patternIndices[patternIndices.length - 1] + 1
                    : next.length;
                next.splice(insertIndex, 0, newPoint);
                return next;
            }

            next.push(newPoint);
            return next;
        });

        setActivePointKey(pointKey);
    }, [captureRouteUndo, editorNavigationMode, isIndoorOrMixedMission, localDefaultPointPlacement, nextPointPlacement, selectedAreaSlamMap?.id, selectedAreaSlamPlacement]);

    const handleInsertCheckpointRelative = React.useCallback((referenceIndex: number, position: 'before' | 'after') => {
        const referencePoint = draftPoints[referenceIndex];
        if (!referencePoint) {
            return;
        }
        const pointKey = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        captureRouteUndo();
        setDraftPoints((current) => {
            const next = [...current];
            const insertIndex = position === 'before' ? referenceIndex : referenceIndex + 1;
            const previousNeighbor = insertIndex > 0 ? current[insertIndex - 1] : null;
            const nextNeighbor = insertIndex < current.length ? current[insertIndex] : null;
            const previousLocal = previousNeighbor
                ? readDraftPointLocalCoordinates(previousNeighbor, selectedAreaSlamPlacement)
                : null;
            const nextLocal = nextNeighbor
                ? readDraftPointLocalCoordinates(nextNeighbor, selectedAreaSlamPlacement)
                : null;
            const referenceLocal = readDraftPointLocalCoordinates(referencePoint, selectedAreaSlamPlacement);
            const localPlacement = isIndoorOrMixedMission
                ? (
                    previousLocal && nextLocal
                        ? {
                            x: (previousLocal.x + nextLocal.x) / 2,
                            y: (previousLocal.y + nextLocal.y) / 2,
                        }
                        : previousLocal
                            || nextLocal
                            || referenceLocal
                            || localDefaultPointPlacement
                )
                : null;
            const previousLocation = (
                previousNeighbor
                && Number.isFinite(previousNeighbor.lat)
                && Number.isFinite(previousNeighbor.lng)
            )
                ? {
                    lat: Number(previousNeighbor.lat),
                    lng: Number(previousNeighbor.lng),
                }
                : null;
            const nextLocation = (
                nextNeighbor
                && Number.isFinite(nextNeighbor.lat)
                && Number.isFinite(nextNeighbor.lng)
            )
                ? {
                    lat: Number(nextNeighbor.lat),
                    lng: Number(nextNeighbor.lng),
                }
                : null;
            const placement = previousLocation && nextLocation
                ? {
                    lat: (previousLocation.lat + nextLocation.lat) / 2,
                    lng: (previousLocation.lng + nextLocation.lng) / 2,
                }
                : localPlacement && selectedAreaSlamPlacement
                    ? localMetersToGps(localPlacement.x, localPlacement.y, selectedAreaSlamPlacement)
                : previousLocation
                    || nextLocation
                    || nextPointPlacement;

            const basePoint: MissionPointDraft = {
                key: pointKey,
                label: '',
                actionType: editorNavigationMode === 'waypoint_goals' ? 'measure' : 'custom',
                patternPointId: null,
                lat: placement?.lat ?? null,
                lng: placement?.lng ?? null,
                waitSeconds: null,
                speedOverride: null,
                isRequired: true,
                source: 'custom',
                params: {},
            };
            next.splice(
                insertIndex,
                0,
                localPlacement
                    ? buildPointWithLocalCoordinates(basePoint, localPlacement, selectedAreaSlamPlacement, selectedAreaSlamMap?.id ?? null)
                    : basePoint
            );
            return next;
        });

        setActivePointKey(pointKey);
    }, [captureRouteUndo, draftPoints, editorNavigationMode, isIndoorOrMixedMission, localDefaultPointPlacement, nextPointPlacement, selectedAreaSlamMap?.id, selectedAreaSlamPlacement]);

    const handleMapPointClick = React.useCallback((pointId: string | number) => {
        const key = String(pointId);
        if (draftPoints.some((point) => point.key === key)) {
            setActivePointKey(key);
        }
    }, [draftPoints]);

    const applyImportedPatternPoints = React.useCallback((
        pointsToImport: PatternPointItem[],
        patternForImport: PatternItem | null,
        options: { showFeedback?: boolean; asCustom?: boolean } = {}
    ) => {
        const shouldShowFeedback = options.showFeedback !== false;
        const asCustom = options.asCustom === true;
        let projectedCount = 0;
        let unresolvedCount = 0;
        let missingGpsProjectionCount = 0;

        const importedPoints = [...pointsToImport]
            .sort((a, b) => a.pointIndex - b.pointIndex)
            .map((point) => {
                const localPoint = isIndoorOrMixedMission
                    ? resolvePatternPointLocalForMission(point, patternForImport, selectedAreaForEditor, selectedAreaSlamPlacement)
                    : null;
                const localProjectedLocation = localPoint && selectedAreaSlamPlacement
                    ? localMetersToGps(localPoint.x, localPoint.y, selectedAreaSlamPlacement)
                    : null;
                const projectedLocation = isIndoorOrMixedMission
                    ? localProjectedLocation
                    : projectPatternPointToGps(
                        point,
                        operationMapGeometry,
                        {
                            boxWidth: patternForImport?.definition?.boxWidth,
                            boxHeight: patternForImport?.definition?.boxHeight,
                            templateDimensions: selectedAreaForEditor?.areaType?.templateDimensions,
                        }
                    );

                if (isIndoorOrMixedMission) {
                    if (localPoint) {
                        projectedCount += 1;
                        if (!localProjectedLocation) {
                            missingGpsProjectionCount += 1;
                        }
                    } else {
                        unresolvedCount += 1;
                    }
                } else if (
                    point.gpsLat === null
                    || point.gpsLat === undefined
                    || point.gpsLon === null
                    || point.gpsLon === undefined
                ) {
                    if (projectedLocation) {
                        projectedCount += 1;
                    } else {
                        unresolvedCount += 1;
                    }
                }

                const localPose = localPoint
                    ? buildLocalPose(
                        localPoint,
                        null,
                        asCustom ? 'mission_only_route' : 'pattern_point',
                        selectedAreaSlamMap?.id ?? null,
                        asCustom ? {} : {
                            patternPointId: point.id,
                            patternPointIndex: point.pointIndex,
                            projectionMode: localPoint.mode,
                        }
                    )
                    : null;

                return {
                    key: asCustom ? `mission-only-point-${point.pointIndex}` : `pattern-point-${point.id}`,
                    label: point.label || '',
                    actionType: 'measure',
                    patternPointId: asCustom ? null : point.id,
                    lat: isIndoorOrMixedMission
                        ? (projectedLocation?.lat ?? null)
                        : (point.gpsLat ?? projectedLocation?.lat ?? null),
                    lng: isIndoorOrMixedMission
                        ? (projectedLocation?.lng ?? null)
                        : (point.gpsLon ?? projectedLocation?.lng ?? null),
                    localPose,
                    waitSeconds: null,
                    speedOverride: null,
                    isRequired: true,
                    source: asCustom ? 'custom' as const : 'pattern' as const,
                    params: {
                        source: asCustom ? 'mission_only_route' : 'pattern',
                        ...(asCustom ? {} : { patternPointIndex: point.pointIndex }),
                        ...(localPose ? {
                            local: {
                                ...localPose,
                            },
                        } : {}),
                    },
                };
            });

        captureRouteUndo();
        setDraftPoints(importedPoints);
        setActivePointKey(importedPoints[0]?.key ?? null);

        if (!shouldShowFeedback) {
            return;
        }

        if (unresolvedCount > 0) {
            message.warning(
                isIndoorOrMixedMission
                    ? `Imported ${importedPoints.length} pattern points. ${projectedCount} were resolved from local coordinates, ${unresolvedCount} could not be resolved because the area local geometry or SLAM placement is missing.`
                    : `Imported ${importedPoints.length} pattern points. ${projectedCount} local points were projected, ${unresolvedCount} could not be mapped to GPS.`
            );
            return;
        }

        if (isIndoorOrMixedMission && missingGpsProjectionCount > 0) {
            message.warning(
                `Imported ${importedPoints.length} pattern points in local coordinates. ${missingGpsProjectionCount} do not have GPS coordinates yet because the SLAM placement is missing.`
            );
            return;
        }

        if (projectedCount > 0) {
            message.success(`Imported ${importedPoints.length} pattern points and projected ${projectedCount} local points onto the selected area`);
            return;
        }

        message.success(`Imported ${importedPoints.length} pattern points`);
    }, [captureRouteUndo, isIndoorOrMixedMission, operationMapGeometry, selectedAreaForEditor, selectedAreaSlamMap?.id, selectedAreaSlamPlacement]);

    const clearActivePoint = React.useCallback(() => {
        setActivePointKey(null);
        setActiveCommandKey(null);
    }, []);

    const applyArduPilotPreview = React.useCallback((preview: MissionImportPreview) => {
        const nextDraftPoints = buildDraftPointsFromImportPreview(preview);
        const pointKeyByIndex = new Map(
            nextDraftPoints.map((point, index) => [index + 1, point.key])
        );
        const importedCommands = buildDraftCommandsFromImportPreview(preview, pointKeyByIndex);
        const nextDraftCommands = syncDraftCommandsWithPoints(nextDraftPoints, importedCommands);

        captureRouteUndo();
        setDraftPoints(nextDraftPoints);
        setDraftCommands(nextDraftCommands);
        setActivePointKey(nextDraftPoints[0]?.key ?? null);
        setActiveCommandKey(null);
        setArduPilotPreviewVisible(false);

        const currentName = String(form.getFieldValue('name') || '').trim();
        if (!currentName && preview.missionName) {
            form.setFieldsValue({ name: preview.missionName });
        }

        message.success(`Imported ${preview.summary.pointCount} points and ${preview.summary.commandCount} commands from ${preview.format === 'qgc_plan' ? '.plan' : 'WPL'} file`);
    }, [captureRouteUndo, form]);

    const handleArduPilotFileSelected = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) {
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            setArduPilotImportLoading(true);
            const response = await axios.post(ENDPOINTS.MISSION_IMPORT_ARDUPILOT_PREVIEW, formData, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setArduPilotPreview(response.data?.data || null);
            setArduPilotPreviewVisible(true);
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to preview ArduPilot mission file'));
        } finally {
            setArduPilotImportLoading(false);
        }
    }, []);

    const openArduPilotImportPicker = React.useCallback(() => {
        if (arduPilotImportLoading) {
            return;
        }

        arduPilotFileInputRef.current?.click();
    }, [arduPilotImportLoading]);

    const handleImportPatternPoints = React.useCallback(() => {
        if (!editorPatternId) {
            message.warning('Select a pattern before importing points');
            return;
        }

        if (patternPoints.length === 0) {
            message.warning('This pattern does not have any points yet');
            return;
        }

        if (draftPoints.length > 0) {
            const proceed = window.confirm('Importing pattern points will replace the current mission point order in the editor. Continue?');
            if (!proceed) {
                return;
            }
        }

        applyImportedPatternPoints(patternPoints, selectedPattern);
    }, [applyImportedPatternPoints, draftPoints.length, editorPatternId, patternPoints, selectedPattern]);

    const handleAreaChange = React.useCallback((value: number) => {
        if (value !== editorAreaId) {
            const preserveImportedDraft = mode === 'edit'
                && mission?.externalSource === 'external_api'
                && !mission.areaId;
            form.setFieldsValue({
                areaId: value,
                deviceId: preserveImportedDraft ? mission.deviceId : undefined,
                patternId: null,
            });
            setPatternPoints([]);
            setPatternPreviewPointsById(new Map());
            setSelectedAreaDetail(null);
            setPreviewMapCenter(null);
            setConfirmedAreaId(null);
            setConfirmedDeviceId(preserveImportedDraft ? mission.deviceId : null);
            setPatternSelectionConfirmed(preserveImportedDraft);
            setConfirmedPatternId(null);
            setExpandedWorkflowStep('area');
            setPatternPreviewVisible(false);
            setPatternPreviewPatternId(null);
        }
    }, [editorAreaId, form, mission, mode]);

    const handleAreaRowClick = React.useCallback((record: AreaSelectionRow) => {
        const recordKey = String(record.id);
        const pathKeys = findAreaSelectionPath(record.id, areaSelectionRows).map((id) => String(id));

        setExpandedAreaRowKeys((current) => {
            if (editorAreaId === record.id && current.includes(recordKey)) {
                return pathKeys.slice(0, -1);
            }

            return pathKeys.length > 0 ? pathKeys : [recordKey];
        });

        handleAreaChange(record.id);
    }, [areaSelectionRows, editorAreaId, handleAreaChange]);

    const handleConfirmArea = React.useCallback(() => {
        if (!editorAreaId) {
            message.warning('Select an area to continue');
            return;
        }
        const nextEnvironment = deriveOperationEnvironmentFromAreaHierarchy(editorAreaId, areas, selectedAreaDetail);
        form.setFieldsValue({ operationEnvironment: nextEnvironment });
        setConfirmedAreaId(editorAreaId);
        // For indoor/mixed: skip SLAM map step if area already has a defined geometry;
        // otherwise go to SLAM map step first. For outdoor: go straight to robot.
        const isIndoorOrMixed = nextEnvironment === 'indoor' || nextEnvironment === 'mixed';
        const areaGeomDefined = Boolean(selectedAreaDetail?.geom?.coordinates);
        const nextStep: WorkflowStep = isIndoorOrMixed && !areaGeomDefined
            ? 'slam_map'
            : 'device';
        // Auto-confirm SLAM map step when skipping it for areas with defined geometry
        if (nextStep === 'device' && isIndoorOrMixed) {
            setSlamMapStepConfirmed(true);
        }
        setExpandedWorkflowStep(nextStep);
    }, [areas, editorAreaId, form, selectedAreaDetail]);

    const handleCreateIndoorArea = React.useCallback(async () => {
        // If user already picked an existing area from the dropdown, just confirm it
        if (editorAreaId && !indoorSpaceName.trim()) {
            setConfirmedAreaId(editorAreaId);
            setSlamMapStepConfirmed(false);
            setConfirmedSlamMapIds([]);
            setExpandedWorkflowStep('slam_map');
            return;
        }
        if (!indoorSpaceName.trim()) {
            message.warning('Enter a name for the indoor space or select an existing area');
            return;
        }
        setIndoorSpaceCreating(true);
        try {
            const companyId = areas[0]?.companyId ?? null;
            if (!companyId) {
                throw new Error('Could not determine company. Please refresh and try again.');
            }
            const res = await axios.post(ENDPOINTS.CREATE_AREA_V2, {
                name: indoorSpaceName.trim(),
                spaceType: 'indoor',
                geom: null,
                companyId,
            }, { withCredentials: true });
            const newArea = res.data?.data;
            if (!newArea?.id) throw new Error('Failed to create indoor space');
            form.setFieldsValue({ areaId: newArea.id });
            setConfirmedAreaId(newArea.id);
            setSlamMapStepConfirmed(false);
            setConfirmedSlamMapIds([]);
            setExpandedWorkflowStep('slam_map');
            message.success(`Indoor space "${indoorSpaceName.trim()}" created`);
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to create indoor space'));
        } finally {
            setIndoorSpaceCreating(false);
        }
    }, [editorAreaId, form, indoorSpaceName]);

    const handleConfirmSlamMaps = React.useCallback(() => {
        setSlamMapStepConfirmed(true);
        setIsScanMission(false);
        setExpandedWorkflowStep('device');
    }, []);

    const handleCreateScanMission = React.useCallback(() => {
        setSlamMapStepConfirmed(true);
        setIsScanMission(true);
        setScanMode('autonomous');
        form.setFieldsValue({ missionType: 'slam_scan' });
        setExpandedWorkflowStep('device');
    }, [form]);

    const handleInlineUploadSlamMap = React.useCallback(async (
        name: string,
        mapType: string,
        metersPerPixel: number | null,
        imageFile: File | null
    ) => {
        if (!confirmedAreaId) return;
        setSlamMapUploadSaving(true);
        try {
            // 1. Create the SLAM map record
            const createRes = await axios.post(ENDPOINTS.SLAM_MAPS, {
                name,
                mapType,
                metersPerPixel,
                areaId: confirmedAreaId,
            }, { withCredentials: true });
            const newMap = createRes.data?.data;
            if (!newMap?.id) throw new Error('Failed to create SLAM map');

            // 2. Upload image if provided
            let savedMap = newMap;
            if (imageFile) {
                const formData = new FormData();
                formData.append('image', imageFile);
                const uploadRes = await axios.post(ENDPOINTS.SLAM_MAP_IMAGE_UPLOAD(newMap.id), formData, {
                    withCredentials: true,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                savedMap = uploadRes.data?.data || savedMap;
            }

            await axios.post(`${ENDPOINTS.SLAM_MAP_DETAIL(newMap.id)}/assign-area`, {
                areaId: confirmedAreaId,
            }, { withCredentials: true });

            const detailRes = await axios.get(ENDPOINTS.SLAM_MAP_DETAIL(newMap.id), { withCredentials: true });
            savedMap = detailRes.data?.data || savedMap;
            setSelectedAreaDetail((current) => current && current.id === confirmedAreaId
                ? {
                    ...current,
                    slamMapId: savedMap.id,
                    slamMap: savedMap,
                }
                : current);
            setAreaSlamMaps([savedMap]);
            setAreaSlamMapsChecked(true);
            setConfirmedSlamMapIds([savedMap.id]);

            setSlamMapUploadVisible(false);
            message.success(`SLAM map "${name}" uploaded successfully`);
        } catch (error) {
            message.error(getErrorMessage(error, 'Failed to upload SLAM map'));
        } finally {
            setSlamMapUploadSaving(false);
        }
    }, [confirmedAreaId]);

    const handleSelectDevice = React.useCallback((deviceId: number) => {
        const nextDevice = devices.find((device) => device.id === deviceId) || null;
        const expectedKeys = requiredCapabilitiesForEnvironment(editorOperationEnvironment);
        const capabilityKeys = getDeviceCapabilityKeys(nextDevice);
        const missingCapabilities = expectedKeys.filter((key) => !capabilityKeys.has(key));

        if (missingCapabilities.length > 0) {
            message.warning(`${nextDevice?.label || 'Selected robot'} is missing: ${formatCapabilityList(missingCapabilities)}.`);
            return;
        }

        if (deviceId !== editorDeviceId) {
            form.setFieldsValue({
                deviceId,
                patternId: null,
            });
            setPatternPoints([]);
            setPatternSelectionConfirmed(false);
            setConfirmedPatternId(null);
            setModuleStepConfirmed(false);
        } else {
            form.setFieldsValue({ deviceId });
        }

        setConfirmedDeviceId(deviceId);
        const nextProtocol = normalizeCommandProtocol(nextDevice?.deviceType?.commandProtocol);
        const nextNeedsModuleStep = !isMavlinkLikeProtocol(nextProtocol) && !isSlamScan;
        setExpandedWorkflowStep(isSlamScan ? 'details' : (nextNeedsModuleStep ? 'modules' : 'pattern'));
    }, [devices, editorDeviceId, editorOperationEnvironment, form, isSlamScan]);

    const handleOpenPatternPreview = React.useCallback((patternId: number) => {
        setPatternPreviewPatternId(patternId);
        setPatternPreviewVisible(true);
    }, []);

    const handleCreateArea = React.useCallback(() => {
        if (mode !== 'edit' || !mission?.id) {
            message.warning('Save the mission draft before creating an area');
            return;
        }

        const returnParams = new URLSearchParams({
            resumeMissionId: String(mission.id),
        });
        const createAreaParams = new URLSearchParams({
            returnTo: `/operations?${returnParams.toString()}`,
        });
        navigate(`/areas/new?${createAreaParams.toString()}`);
    }, [mission?.id, mode, navigate]);

    const handleCreateManualRoute = React.useCallback(() => {
        if (!editorAreaId || !selectedAreaForEditor) {
            message.warning('Select and confirm an area before creating a manual route');
            return;
        }

        const areaTypeId = selectedAreaForEditor.areaType?.id;
        if (isIndoorOrMixedMission && (!areaTypeId || !supportsNormalizedTemplate(selectedAreaForEditor.areaType))) {
            message.warning('This local route editor requires a standard four-corner normalized template. A reference image alone cannot define normalized points.');
            return;
        }

        const rawValues = form.getFieldsValue(true) as Record<string, any>;
        const scheduledStartAt = rawValues.scheduledStartAt;
        const formValues = {
            ...rawValues,
            scheduledStartAt: scheduledStartAt && typeof scheduledStartAt.toISOString === 'function'
                ? scheduledStartAt.toISOString()
                : scheduledStartAt ?? null,
        };
        const draftKey = createMissionPatternDraftKey();
        const stored = writeMissionPatternHandoff(draftKey, {
            version: 1,
            createdAt: new Date().toISOString(),
            companyId: selectedAreaForEditor.companyId ?? null,
            draft: {
                formValues,
                draftPoints,
                draftCommands,
                confirmedAreaId,
                confirmedDeviceId,
                moduleStepConfirmed,
                oscarModuleExecutionMode,
                oscarModuleSettings,
                confirmedSlamMapIds,
                slamMapStepConfirmed,
                patternGuidedDirections,
                obstaclePolicyMode,
                costMode,
                sweepAngles,
                windowSize,
            },
        });

        if (!stored) {
            message.error('The mission draft could not be preserved. Please try again.');
            return;
        }

        const params = new URLSearchParams({ missionDraftKey: draftKey });
        if (isIndoorOrMixedMission) {
            params.set('areaTypeId', String(areaTypeId));
            navigate(`/patterns/create/template?${params.toString()}`);
            return;
        }

        params.set('areaId', String(editorAreaId));
        navigate(`/patterns/create/area?${params.toString()}`);
    }, [
        confirmedAreaId,
        confirmedDeviceId,
        confirmedSlamMapIds,
        costMode,
        draftCommands,
        draftPoints,
        editorAreaId,
        form,
        isIndoorOrMixedMission,
        moduleStepConfirmed,
        navigate,
        obstaclePolicyMode,
        oscarModuleExecutionMode,
        oscarModuleSettings,
        patternGuidedDirections,
        selectedAreaForEditor,
        slamMapStepConfirmed,
        sweepAngles,
        windowSize,
    ]);

    const handleConfirmPatternSelection = React.useCallback((
        pattern: PatternItem | null,
        loadedPatternPoints?: PatternPointItem[]
    ) => {
        const nextPatternId = pattern?.id ?? null;
        const nextPatternPoints = pattern
            ? (loadedPatternPoints ?? patternPreviewPointsById.get(pattern.id) ?? [])
            : [];

        form.setFieldsValue({ patternId: nextPatternId });
        setPatternPoints(nextPatternPoints);
        setPatternSelectionConfirmed(true);
        setConfirmedPatternId(nextPatternId);
        setExpandedWorkflowStep('details');
        setPatternPreviewVisible(false);
        setPatternPreviewPatternId(null);

        // Always clear previous draft so old pattern points don't linger
        setDraftPoints([]);
        setDraftCommands([]);
        setActivePointKey(null);
        setActiveCommandKey(null);

        // Auto-import the new pattern's points if available
        if (pattern && nextPatternPoints.length > 0) {
            applyImportedPatternPoints(nextPatternPoints, pattern, { showFeedback: false });
        }
    }, [applyImportedPatternPoints, form, patternPreviewPointsById]);

    React.useEffect(() => {
        if (!missionPatternHandoff || resumedPatternResultRef.current === missionPatternHandoff.draftKey) {
            return;
        }

        const result = missionPatternHandoff.value.result;
        if (!result || result.status === 'cancelled') {
            resumedPatternResultRef.current = missionPatternHandoff.draftKey;
            setExpandedWorkflowStep('pattern');
            onMissionPatternResumeConsumed?.();
            return;
        }

        if (result.status === 'saved') {
            resumedPatternResultRef.current = missionPatternHandoff.draftKey;
            const cachedPattern = patterns.find((pattern) => pattern.id === result.patternId) || null;
            const cachedPoints = patternPreviewPointsById.get(result.patternId);

            void Promise.all([
                cachedPattern
                    ? Promise.resolve(cachedPattern)
                    : axios.get(`${ENDPOINTS.PATTERNS}/${result.patternId}`, { withCredentials: true })
                        .then((response) => response.data?.data || response.data),
                cachedPoints
                    ? Promise.resolve(cachedPoints)
                    : axios.get(ENDPOINTS.PATTERN_POINTS(result.patternId), { withCredentials: true })
                        .then((response) => parseApiList<PatternPointItem>(response.data)),
            ])
                .then(([savedPattern, savedPoints]) => {
                    if (!savedPattern?.id) {
                        throw new Error('The saved pattern could not be loaded');
                    }

                    const resumedDraft = missionPatternHandoff.value.draft;
                    setConfirmedEnvironment(true);
                    setConfirmedAreaId(resumedDraft.confirmedAreaId ?? null);
                    setConfirmedDeviceId(resumedDraft.confirmedDeviceId ?? null);
                    setModuleStepConfirmed(Boolean(resumedDraft.moduleStepConfirmed));
                    setOscarModuleExecutionMode(
                        resumedDraft.oscarModuleExecutionMode === 'series' ? 'series' : 'parallel'
                    );
                    setOscarModuleSettings(resumedDraft.oscarModuleSettings || {});
                    setConfirmedSlamMapIds(
                        Array.isArray(resumedDraft.confirmedSlamMapIds) ? resumedDraft.confirmedSlamMapIds : []
                    );
                    setSlamMapStepConfirmed(Boolean(resumedDraft.slamMapStepConfirmed));
                    setPatterns((current) => (
                        current.some((pattern) => pattern.id === savedPattern.id)
                            ? current
                            : [...current, savedPattern]
                    ));
                    setPatternPreviewPointsById((current) => {
                        const next = new Map(current);
                        next.set(savedPattern.id, savedPoints);
                        return next;
                    });
                    handleConfirmPatternSelection(savedPattern, savedPoints);
                    onMissionPatternResumeConsumed?.();
                    message.success(`Pattern "${savedPattern.name}" selected for this mission`);
                })
                .catch((error) => {
                    console.error('Failed to select the newly saved pattern', error);
                    form.setFieldsValue({ patternId: null });
                    setPatternSelectionConfirmed(false);
                    setConfirmedPatternId(null);
                    setExpandedWorkflowStep('pattern');
                    onMissionPatternResumeConsumed?.();
                    message.error(getErrorMessage(error, 'The pattern was saved, but could not be selected automatically'));
                });
            return;
        }

        if (!selectedAreaForEditor) {
            return;
        }

        const missionOnlyPoints: PatternPointItem[] = result.points.map((point, index) => ({
            id: -(index + 1),
            pointIndex: point.pointIndex || index + 1,
            label: point.label || null,
            gpsLat: point.gpsLat ?? null,
            gpsLon: point.gpsLon ?? null,
            gpsAlt: point.gpsAlt ?? null,
            xLocal: point.xLocal ?? null,
            yLocal: point.yLocal ?? null,
        }));
        const transientPattern: PatternItem = {
            id: -1,
            name: 'Mission-only route',
            patternType: 'custom',
            coordSystem: result.coordSystem,
            definition: result.definition || {},
        };

        resumedPatternResultRef.current = missionPatternHandoff.draftKey;
        form.setFieldsValue({ patternId: null });
        setPatternPoints([]);
        setPatternSelectionConfirmed(true);
        setConfirmedPatternId(null);
        setDraftCommands([]);
        setActiveCommandKey(null);
        applyImportedPatternPoints(missionOnlyPoints, transientPattern, {
            showFeedback: false,
            asCustom: true,
        });
        setExpandedWorkflowStep('details');
        onMissionPatternResumeConsumed?.();
        message.success('Manual route added to this mission without saving a reusable pattern');
    }, [
        applyImportedPatternPoints,
        form,
        handleConfirmPatternSelection,
        missionPatternHandoff,
        onMissionPatternResumeConsumed,
        patternPreviewPointsById,
        patterns,
        selectedAreaForEditor,
    ]);

    const handleSave = React.useCallback(async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue(true) as MissionFormValues;
            if (!isSlamScan && (!hasStartPoint || !hasEndPoint)) {
                const missingPoints = [
                    !hasStartPoint ? 'START' : null,
                    !hasEndPoint ? 'END' : null,
                ].filter(Boolean).join(' and ');
                message.error(`Add a ${missingPoints} point before saving the mission.`);
                setExpandedWorkflowStep('details');
                return;
            }
            if (!isSlamScan && invalidAreaPathIndexes.length > 0) {
                message.error('The route leaves the selected area. Move the affected points until every red path stays inside the boundary.');
                setExpandedWorkflowStep('details');
                return;
            }
            if (selectedDeviceMissingExpectedCapability) {
                message.warning(`Selected robot is missing: ${formatCapabilityList(selectedDeviceMissingCapabilities)}.`);
                setExpandedWorkflowStep('device');
                return;
            }
            const pointIndexByKey = new Map(
                resolvedDraftPoints.map((point, index) => [point.key, index + 1])
            );
            const resolvedPointByKey = new Map(
                resolvedDraftPoints.map((point) => [point.key, point])
            );
            const headingBearingDegrees = isIndoorOrMixedMission ? (selectedAreaSlamPlacement?.bearingDegrees ?? 0) : 0;
            const lockedLegFlags = resolvedDraftPoints
                .slice(0, -1)
                .map((point) => point.params?.straightSteeringLocked === true);
            const lockedSectionIds: Array<string | null> = lockedLegFlags.map(() => null);
            let lockedRunStart: number | null = null;
            for (let index = 0; index <= lockedLegFlags.length; index += 1) {
                const locked = lockedLegFlags[index] === true;
                if (locked && lockedRunStart === null) lockedRunStart = index;
                if (!locked && lockedRunStart !== null) {
                    const sectionId = `straight-${lockedRunStart + 1}-${index + 1}`;
                    for (let legIndex = lockedRunStart; legIndex < index; legIndex += 1) {
                        lockedSectionIds[legIndex] = sectionId;
                    }
                    lockedRunStart = null;
                }
            }
            // Build missionLegs from the FE-computed preview paths (FE is authoritative)
            const clientLegs: MissionSavePayload['missionLegs'] = isSlamScan
                ? []
                : (
                isIndoorOrMixedMission && selectedAreaSlamPlacement
                    ? localEditorPreviewPaths.map((path) => ({
                        id: path.id,
                        selected: path.selected,
                        warning: path.warning,
                        coordinates: path.coordinates.map((point) => localMetersToGps(point.x, point.y, selectedAreaSlamPlacement)),
                        distanceMeters: calculateLocalPathDistanceMeters(path.coordinates),
                    }))
                    : editorPreviewPaths.map((path) => ({
                        ...path,
                        distanceMeters: calculatePathDistanceMeters(path.coordinates),
                    }))
            ).map((path, index) => {
                const sectionId = lockedSectionIds[index];
                const pathCoordinates = sectionId && path.coordinates.length >= 2
                    ? [path.coordinates[0], path.coordinates[path.coordinates.length - 1]]
                    : path.coordinates;
                return {
                    fromPointIndex: index + 1,
                    toPointIndex: index + 2,
                    seq: index + 1,
                    routingMode: sectionId ? 'direct' : 'dubins',
                    pathCoordinates,
                    distanceMeters: Number((
                        sectionId
                            ? calculatePathDistanceMeters(pathCoordinates)
                            : path.distanceMeters ?? 0
                    ).toFixed(2)),
                    constraints: sectionId ? {
                        motionConstraint: {
                            mode: 'steering_locked',
                            steeringAngleDegrees: 0,
                            sectionId,
                            entryHeadingToleranceDegrees: 1,
                            entryApproachDistanceMeters: 2,
                            maxCrossTrackMeters: 1.0,
                            obstacleBehavior: 'stop',
                        },
                    } : null,
                };
            });
            const straightSectionIds = Array.from(new Set(
                clientLegs
                    .map((leg) => leg.constraints?.motionConstraint?.sectionId)
                    .filter((sectionId): sectionId is string => Boolean(sectionId))
            ));
            for (const sectionId of straightSectionIds) {
                const sectionLegs = clientLegs.filter(
                    (leg) => leg.constraints?.motionConstraint?.sectionId === sectionId
                );
                const sectionCoordinates = sectionLegs.length > 0
                    ? [
                        sectionLegs[0].pathCoordinates[0],
                        ...sectionLegs.map((leg) => leg.pathCoordinates[leg.pathCoordinates.length - 1]),
                    ]
                    : [];
                const deviation = maxStraightLineDeviationMeters(sectionCoordinates);
                if (deviation > 0.5) {
                    message.error(
                        `Straight section ${sectionId} bends by ${deviation.toFixed(2)} m. Align its waypoints within 0.50 m before saving.`
                    );
                    return;
                }
            }

            const existingMissionMeta: Record<string, any> = isPlainObject(mission?.missionMeta) ? (mission!.missionMeta as Record<string, any>) : {};
            const existingObstaclePolicy = isPlainObject(existingMissionMeta.obstaclePolicy)
                ? existingMissionMeta.obstaclePolicy
                : {};
            const payload: MissionSavePayload = {
                name: values.name,
                description: values.description || null,
                missionType: values.missionType || 'robot_run',
                operationEnvironment: values.operationEnvironment || 'outdoor',
                navigationMode: values.navigationMode || 'route_following',
                missionMeta: {
                    ...existingMissionMeta,
                    scanMode: isSlamScan ? scanMode : existingMissionMeta.scanMode,
                    oscarModuleDefaults: isOscarModuleMode
                        ? serializeOscarModuleDefaults(oscarModuleExecutionMode, oscarModuleSettings)
                        : null,
                    obstaclePolicy: {
                        ...existingObstaclePolicy,
                        mode: obstaclePolicyMode,
                        softDiversionM: toFiniteNumber(existingObstaclePolicy.softDiversionM) ?? 5,
                        hardDiversionM: toFiniteNumber(existingObstaclePolicy.hardDiversionM) ?? 10,
                    },
                },
                slamMapIds: confirmedSlamMapIds.length > 0 ? confirmedSlamMapIds : undefined,
                areaId: skipsAreaSelection ? (values.areaId ?? null) : values.areaId,
                deviceId: values.deviceId,
                patternId: values.patternId || null,
                routeConfig: isSlamScan
                    ? null
                    : normalizeMissionRouteConfig({
                        turnRadiusMeters: values.turnRadiusMeters ?? undefined,
                        routingMode: 'dubins',
                        patternGuidedDirections,
                        costMode,
                        sweepAngles,
                        windowSize,
                    }),
                defaultSpeedMps: typeof values.defaultSpeedMps === 'number' ? values.defaultSpeedMps : null,
                status: values.status,
                scheduledStartAt: values.scheduledStartAt ? values.scheduledStartAt.toISOString() : null,
                missionPoints: isSlamScan ? [] : resolvedDraftPoints.map(buildDraftPointPayload),
                missionCommands: isSlamScan ? [] : draftCommands
                    .filter((command) => command.commandName.trim())
                    .map((command) => {
                        const resolvedPoint = command.missionPointKey
                            ? resolvedPointByKey.get(command.missionPointKey)
                            : null;
                        const commandForPayload = command.isPrimary && resolvedPoint
                            ? {
                                ...command,
                                headingDegrees: pathDirectionToCompassDegrees(
                                    resolvedPoint.params?.pathDirection,
                                    headingBearingDegrees
                                ),
                            }
                            : command;
                        return buildDraftCommandPayload(commandForPayload, pointIndexByKey);
                    }),
                missionLegs: isSlamScan || values.navigationMode === 'waypoint_goals'
                    ? []
                    : (clientLegs.length > 0 ? clientLegs : undefined),
            };

            await onSave(payload, mission?.id);
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) {
                const firstError = (error as { errorFields?: Array<{ errors?: string[] }> }).errorFields?.[0]?.errors?.[0];
                message.warning(firstError || 'Complete the required mission fields before saving');
                return;
            }

            message.error(getErrorMessage(error, 'Failed to save mission'));
        }
    }, [costMode, draftCommands, editorPreviewPaths, form, hasEndPoint, hasStartPoint, invalidAreaPathIndexes.length, isIndoorOrMixedMission, isOscarModuleMode, isSlamScan, localEditorPreviewPaths, mission?.id, mission?.missionMeta, obstaclePolicyMode, onSave, oscarModuleExecutionMode, oscarModuleSettings, patternGuidedDirections, resolvedDraftPoints, scanMode, selectedAreaSlamPlacement, selectedDeviceMissingCapabilities, selectedDeviceMissingExpectedCapability, skipsAreaSelection, sweepAngles, windowSize]);

    const areaSelectionColumns = React.useMemo<ColumnsType<AreaSelectionRow>>(() => [
        {
            title: 'Area',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div
                    className="area-name-cell"
                    style={{ paddingLeft: (record.hierarchyDepth || 0) * 16 }}
                >
                    {(record.hierarchyDepth || 0) > 0 ? <span className="area-depth-marker" /> : null}
                    <span>{text}</span>
                </div>
            ),
        },
        {
            title: 'Type',
            key: 'type',
            width: 220,
            render: (_, record) => record.areaType?.name || '—',
        },
        {
            title: 'Path',
            key: 'path',
            render: (_, record) => areaPathMap.get(record.id) || record.name,
        },
    ], [areaPathMap]);

    const patternPreviewPoints = React.useMemo(
        () => (
            patternPreviewPatternId
                ? (patternPreviewPointsById.get(patternPreviewPatternId) || [])
                : []
        ),
        [patternPreviewPatternId, patternPreviewPointsById]
    );

    const patternPreviewGraphicPoints = React.useMemo(
        () => buildPatternPreviewPoints(patternPreviewPoints, patternPreviewPattern, selectedAreaForEditor),
        [patternPreviewPattern, patternPreviewPoints, selectedAreaForEditor]
    );

    const draftPointColumns = React.useMemo<ColumnsType<MissionPointDraft>>(() => [
        {
            title: 'Marker',
            key: 'marker',
            width: 86,
            render: (_, point, index) => (
                <Tag color={point.key === activePointKey ? 'blue' : 'default'}>
                    {getMissionPointMarkerLabel(draftPoints, point, index)}
                </Tag>
            ),
        },
        {
            title: 'Order',
            key: 'order',
            width: 72,
            render: (_, __, index) => (
                <Typography.Text strong>{index + 1}</Typography.Text>
            ),
        },
        {
            title: 'Label',
            key: 'label',
            width: 170,
            render: (_, point, index) => (
                <Typography.Text>{point.label?.trim() || `Step ${index + 1}`}</Typography.Text>
            ),
        },
        {
            title: 'Action',
            key: 'actionType',
            width: 130,
            render: (_, point) => (
                <Tag>{ACTION_OPTIONS.find((option) => option.value === point.actionType)?.label || point.actionType}</Tag>
            ),
        },
        {
            title: 'Source',
            key: 'source',
            width: 150,
            render: (_, point) => point.patternPointId ? (
                <Typography.Text type="secondary">Pattern #{point.patternPointId}</Typography.Text>
            ) : (
                <Typography.Text type="secondary">Custom</Typography.Text>
            ),
        },
        {
            title: 'Location',
            key: 'location',
            width: 220,
            render: (_, point) => (
                Number.isFinite(point.lat) && Number.isFinite(point.lng) ? (
                    <div>
                        <Typography.Text>{Number(point.lat).toFixed(6)}</Typography.Text>
                        <br />
                        <Typography.Text type="secondary">{Number(point.lng).toFixed(6)}</Typography.Text>
                    </div>
                ) : (
                    <Typography.Text type="secondary">Not placed yet</Typography.Text>
                )
            ),
        },
        {
            title: 'Required',
            key: 'isRequired',
            width: 110,
            render: (_, point) => (
                <Tag color={point.isRequired ? 'green' : 'default'}>
                    {point.isRequired ? 'Yes' : 'No'}
                </Tag>
            ),
        },
        {
            title: 'Wait (s)',
            key: 'waitSeconds',
            width: 96,
            render: (_, point) => (
                <Typography.Text>{point.waitSeconds ?? '-'}</Typography.Text>
            ),
        },
        {
            title: 'Speed',
            key: 'speedOverride',
            width: 90,
            render: (_, point) => (
                <Typography.Text>{point.speedOverride ?? '-'}</Typography.Text>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 134,
            render: (_, __, index) => (
                <Space size={4}>
                    <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleMoveDraftPoint(index, -1);
                        }}
                        disabled={index === 0}
                    />
                    <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleMoveDraftPoint(index, 1);
                        }}
                        disabled={index === draftPoints.length - 1}
                    />
                    <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveDraftPoint(index);
                        }}
                    />
                </Space>
            ),
        },
    ], [activePointKey, draftPoints, handleMoveDraftPoint, handleRemoveDraftPoint]);

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div className="operations-header">
                <div>
                    <Button
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={onCancel}
                        className="operations-back-button"
                    >
                        Back to operations
                    </Button>
                    <Title level={3} className="operations-title">
                        {mode === 'edit' ? 'Edit mission' : 'New mission'}
                    </Title>
                    <Text className="operations-subtitle">
                        Configure the robot, field, optional pattern, and mission steps in a full-page editor.
                    </Text>
                </div>
                <Space wrap className="operations-editor-actions">
                    <Button onClick={onCancel}>Cancel</Button>
                </Space>
            </div>

            <Spin spinning={loading}>
                <Form<MissionFormValues> form={form} layout="vertical">
                    {mode === 'edit' && mission?.externalSource === 'external_api' && !mission.areaId ? (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Complete this imported mission draft"
                            description={`The API supplied ${draftPoints.length} waypoint${draftPoints.length === 1 ? '' : 's'} and selected the robot. Choose an area, then add START and END points before saving.`}
                        />
                    ) : null}
                    {(!showMissionDraftSections || isSlamScan) ? (
                        <>
                            <Form.Item name="name" hidden rules={[{ required: true, message: 'Enter a mission name' }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="description" hidden>
                                <Input />
                            </Form.Item>
                            <Form.Item name="missionType" hidden rules={[{ required: true }]}>
                                <Select options={MISSION_TYPE_OPTIONS} />
                            </Form.Item>
                            <Form.Item name="status" hidden rules={[{ required: true }]}>
                                <Select options={MISSION_STATUS_OPTIONS} />
                            </Form.Item>
                            <Form.Item name="scheduledStartAt" hidden>
                                <DatePicker showTime style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="turnRadiusMeters" hidden>
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="defaultSpeedMps" hidden>
                                <InputNumber style={{ width: '100%' }} />
                            </Form.Item>
                        </>
                    ) : null}
                    <Form.Item
                        name="areaId"
                        hidden
                        rules={skipsAreaSelection ? [] : [{ required: true, message: 'Select an area' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="deviceId" hidden rules={[{ required: true, message: 'Select a robot' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="patternId" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item name="operationEnvironment" hidden>
                        <Input />
                    </Form.Item>

                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {confirmedEnvironment && !skipsAreaSelection ? (
                        <Card
                            className="operations-editor-card"
                            title="1. Select area"
                            extra={isAreaConfirmed ? (
                                <Button type="link" onClick={() => setExpandedWorkflowStep('area')}>
                                    {workflowChangeLabel}
                                </Button>
                            ) : (
                                <Space size="small" wrap>
                                    {mode === 'edit' && mission?.id ? (
                                        <Button icon={<PlusOutlined />} onClick={handleCreateArea}>
                                            Create area
                                        </Button>
                                    ) : null}
                                    <Text type="secondary">
                                        {selectedAreaLabel || 'Choose an area'}
                                    </Text>
                                    <Button
                                        type="primary"
                                        onClick={handleConfirmArea}
                                        disabled={!editorAreaId}
                                    >
                                        Confirm area
                                    </Button>
                                </Space>
                            )}
                        >
                            {expandedWorkflowStep === 'area' || !isAreaConfirmed ? (
                                initialEnvironment === 'indoor' && editorOperationEnvironment === 'indoor' ? (
                                    /* Pure indoor: no GPS polygon needed — create a named placeholder area */
                                    <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 560 }}>
                                        <Alert
                                            type="info"
                                            showIcon
                                            message="Indoor spaces don't need a GPS boundary"
                                            description="Give this indoor space a name and we'll create a placeholder area for it. The SLAM map produced by the scanning mission will define its actual coordinate system. Alternatively, pick an existing area if one was already created for this venue."
                                        />
                                        <div>
                                            <Text className="operations-editor-field-label">Indoor space name *</Text>
                                            <Input
                                                value={indoorSpaceName}
                                                onChange={(e) => setIndoorSpaceName(e.target.value)}
                                                placeholder="e.g. Warehouse B — Ground Floor"
                                                style={{ marginTop: 6 }}
                                                onPressEnter={() => void handleCreateIndoorArea()}
                                            />
                                        </div>
                                        <div>
                                            <Text className="operations-editor-field-label" style={{ display: 'block', marginBottom: 6 }}>
                                                — or pick an existing area —
                                            </Text>
                                            <Select
                                                showSearch
                                                allowClear
                                                placeholder="Search existing areas…"
                                                style={{ width: '100%' }}
                                                value={editorAreaId ?? undefined}
                                                filterOption={(input, option) =>
                                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={areas.map((a) => ({
                                                    value: a.id,
                                                    label: areaPathMap.get(a.id) || a.name,
                                                }))}
                                                onChange={(value) => {
                                                    if (value) {
                                                        form.setFieldsValue({ areaId: value });
                                                        setIndoorSpaceName('');
                                                    } else {
                                                        form.setFieldsValue({ areaId: undefined });
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="operations-workflow-selection-meta">
                                            <div />
                                            <Button
                                                type="primary"
                                                loading={indoorSpaceCreating}
                                                disabled={!indoorSpaceName.trim() && !editorAreaId}
                                                onClick={() => void handleCreateIndoorArea()}
                                            >
                                                {editorAreaId && !indoorSpaceName.trim()
                                                    ? 'Use this area & continue'
                                                    : 'Create indoor space & continue'}
                                            </Button>
                                        </div>
                                    </Space>
                                ) : (
                                <Row gutter={[20, 20]} className="operations-workflow-split">
                                    <Col xs={24} xl={11}>
                                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                            <Table<AreaSelectionRow>
                                                rowKey={(record) => String(record.id)}
                                                size="small"
                                                loading={referencesLoading}
                                                className="operations-table areas-table operations-area-selection-table"
                                                columns={areaSelectionColumns}
                                                dataSource={areaSelectionRows}
                                                pagination={false}
                                                expandable={{
                                                    childrenColumnName: 'children',
                                                    expandedRowKeys: expandedAreaRowKeys,
                                                    showExpandColumn: false,
                                                    onExpand: (expanded, record) => {
                                                        const recordKey = String(record.id);
                                                        if (expanded) {
                                                            setExpandedAreaRowKeys((current) => (
                                                                current.includes(recordKey)
                                                                    ? current
                                                                    : [...current, recordKey]
                                                            ));
                                                            return;
                                                        }

                                                        setExpandedAreaRowKeys((current) => current.filter((key) => key !== recordKey));
                                                    },
                                                }}
                                                rowClassName={(record) => {
                                                    const classes = [];
                                                    if (record.id === editorAreaId) {
                                                        classes.push('operations-selected-row', 'area-row-selected');
                                                    }
                                                    if ((record.hierarchyDepth || 0) > 0) {
                                                        classes.push('area-row-child');
                                                    }
                                                    return classes.join(' ');
                                                }}
                                                onRow={(record) => ({
                                                    onClick: () => handleAreaRowClick(record),
                                                })}
                                                locale={{
                                                    emptyText: <Empty description="No areas available" />,
                                                }}
                                            />

                                            <div className="operations-workflow-selection-meta">
                                                <div>
                                                    <Text strong>{selectedAreaLabel || 'Choose an area from the list'}</Text>
                                                    <br />
                                                    <Text type="secondary">
                                                        {selectedAreaForEditor?.areaType?.name || 'Select an area to preview its footprint and continue.'}
                                                    </Text>
                                                </div>
                                                <Button
                                                    type="primary"
                                                    onClick={handleConfirmArea}
                                                    disabled={!editorAreaId}
                                                >
                                                    Confirm area
                                                </Button>
                                            </div>
                                        </Space>
                                    </Col>

                                    <Col xs={24} xl={13}>
                                        <div className="operations-map-wrapper operations-workflow-split-map">
                                            <AreaMap
                                                mapKey={editorAreaId || 'mission-area-picker'}
                                                areaGeometry={operationMapAreas.length > 0 ? null : operationMapGeometry}
                                                areaName={selectedAreaLabel}
                                                allAreas={operationMapAreas}
                                                points={importedDraftPreviewPoints}
                                                paths={importedDraftPreviewPaths}
                                                alwaysShowMap
                                                emptyOverlayMessage={importedDraftPreviewPoints.length > 0
                                                    ? 'Select an area containing the imported waypoints.'
                                                    : 'Select an area to preview its footprint.'}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                )  /* close inner (outdoor/mixed) branch */
                            ) : (
                                <div className="operations-workflow-confirmed">
                                    <div>
                                        <Text strong>{selectedAreaLabel}</Text>
                                        <br />
                                        <Text type="secondary">{selectedAreaForEditor?.areaType?.name || 'Area confirmed'}</Text>
                                    </div>
                                    <Tag color="blue">Confirmed</Tag>
                                </div>
                            )}
                        </Card>
                        ) : null}

                        {/* Step 2: SLAM map — only shown for indoor/mixed after area confirmed */}
                        {isAreaConfirmed && needsSlamMapStep ? (
                            <Card
                                className="operations-editor-card"
                                title="2. SLAM map"
                                extra={slamMapStepConfirmed ? (
                                    <Button type="link" onClick={() => {
                                        if (mode !== 'edit') {
                                            setSlamMapStepConfirmed(false);
                                            setConfirmedSlamMapIds([]);
                                        }
                                        setExpandedWorkflowStep('slam_map');
                                    }}>
                                        {workflowChangeLabel}
                                    </Button>
                                ) : null}
                            >
                                {(expandedWorkflowStep === 'slam_map' || !slamMapStepConfirmed) ? (
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        {areaSlamMapsLoading ? (
                                            <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                                <Spin />
                                                <div style={{ marginTop: 8 }}>
                                                    <Text type="secondary">Checking for SLAM maps for this area…</Text>
                                                </div>
                                            </div>
                                        ) : areaSlamMapsChecked && areaSlamMaps.length === 0 ? (
                                            // Branch B: no maps for this area
                                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                                <Alert
                                                    type="info"
                                                    showIcon
                                                    message="No SLAM maps for this area"
                                                    description={`No SLAM maps have been uploaded for "${selectedAreaLabel || 'this area'}" yet. Upload an existing map image, or create a scanning mission to map the area with the robot first.`}
                                                />
                                                <Space wrap>
                                                    <Button
                                                        icon={<UploadOutlined />}
                                                        onClick={() => {
                                                            setSlamUploadName('');
                                                            setSlamUploadMapType('slam_2d');
                                                            setSlamUploadScale(null);
                                                            setSlamUploadFile(null);
                                                            setSlamMapUploadVisible(true);
                                                        }}
                                                    >
                                                        Upload SLAM map
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        onClick={handleCreateScanMission}
                                                    >
                                                        Create map scanning mission
                                                    </Button>
                                                </Space>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    In a scanning mission the operator in the field chooses whether to drive the robot manually or let it explore autonomously. Once the map is created, upload it here and link it to this area.
                                                </Text>
                                            </Space>
                                        ) : areaSlamMaps.length > 0 ? (
                                            // Branch A: maps available
                                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                                <Text className="operations-editor-help-text">
                                                    Select the SLAM map(s) this mission will use for indoor navigation. Maps are linked to this area — you can select multiple for multi-floor facilities.
                                                </Text>
                                                <Row gutter={[12, 12]}>
                                                    {areaSlamMaps.map((slamMap) => {
                                                        const isSelected = confirmedSlamMapIds.includes(slamMap.id);
                                                        return (
                                                            <Col xs={24} sm={12} lg={8} key={slamMap.id}>
                                                                <button
                                                                    type="button"
                                                                    className={`operations-workflow-select-card${isSelected ? ' operations-workflow-select-card-active' : ''}`}
                                                                    style={{ width: '100%', textAlign: 'left' }}
                                                                    onClick={() => {
                                                                        setConfirmedSlamMapIds((prev) =>
                                                                            isSelected
                                                                                ? prev.filter((id) => id !== slamMap.id)
                                                                                : [...prev, slamMap.id]
                                                                        );
                                                                        if (slamMap.imageStoragePath && !slamMapImageUrls[slamMap.id]) {
                                                                            void loadSlamMapImageUrl(slamMap.id);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="operations-workflow-select-card-header">
                                                                        <Text strong ellipsis>{slamMap.name}</Text>
                                                                        {isSelected && <Tag color="blue">✓ Selected</Tag>}
                                                                    </div>
                                                                    <div className="operations-workflow-select-card-meta">
                                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                                            {slamMap.mapType} · {slamMap.metersPerPixel != null ? `${slamMap.metersPerPixel} m/px` : 'No scale set'}
                                                                        </Text>
                                                                        {slamMap.geoAnchor ? <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>Geo-anchored</Tag> : null}
                                                                        {slamMap.imageStoragePath && slamMapImageUrls[slamMap.id] ? (
                                                                            <img
                                                                                src={slamMapImageUrls[slamMap.id]}
                                                                                alt={slamMap.name}
                                                                                style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain', marginTop: 4, borderRadius: 4 }}
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                </button>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                                <div className="operations-workflow-selection-meta">
                                                    <Space wrap>
                                                        <Button
                                                            icon={<UploadOutlined />}
                                                            size="small"
                                                            onClick={() => {
                                                                setSlamUploadName('');
                                                                setSlamUploadMapType('slam_2d');
                                                                setSlamUploadScale(null);
                                                                setSlamUploadFile(null);
                                                                setSlamMapUploadVisible(true);
                                                            }}
                                                        >
                                                            Upload another map
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={handleCreateScanMission}
                                                        >
                                                            Re-scan area
                                                        </Button>
                                                    </Space>
                                                    <Button
                                                        type="primary"
                                                        disabled={confirmedSlamMapIds.length === 0}
                                                        onClick={handleConfirmSlamMaps}
                                                    >
                                                        Confirm map selection ({confirmedSlamMapIds.length})
                                                    </Button>
                                                </div>
                                            </Space>
                                        ) : null}
                                    </Space>
                                ) : (
                                    <div className="operations-workflow-confirmed">
                                        <div>
                                            {isScanMission ? (
                                                <>
                                                    <Text strong>Map scanning mission</Text>
                                                    <br />
                                                    <Text type="secondary">The operator will map the area with the robot before running the actual mission</Text>
                                                </>
                                            ) : (
                                                <>
                                                    <Text strong>{confirmedSlamMapIds.length} SLAM map{confirmedSlamMapIds.length !== 1 ? 's' : ''} selected</Text>
                                                    <br />
                                                    <Text type="secondary">
                                                        {areaSlamMaps.filter((m) => confirmedSlamMapIds.includes(m.id)).map((m) => m.name).join(', ')}
                                                    </Text>
                                                </>
                                            )}
                                        </div>
                                        <Tag color={isScanMission ? 'orange' : 'blue'}>
                                            {isScanMission ? 'Scan mission' : 'Confirmed'}
                                        </Tag>
                                    </div>
                                )}
                            </Card>
                        ) : null}

                        {isAreaConfirmed && (!needsSlamMapStep || slamMapStepConfirmed) ? (
                            <Card
                                className="operations-editor-card"
                                title={robotStepTitle}
                                extra={isDeviceConfirmed && !isExternalApiMission ? (
                                    <Button type="link" onClick={() => setExpandedWorkflowStep('device')}>
                                        {workflowChangeLabel}
                                    </Button>
                                ) : null}
                            >
                                {isExternalApiMission ? (
                                    <div className="operations-workflow-confirmed">
                                        <Text strong>{selectedDeviceForDisplay?.label || `Robot #${mission?.deviceId}`}</Text>
                                        <Tag color="geekblue">Selected by API</Tag>
                                    </div>
                                ) : expandedWorkflowStep === 'device' || !isDeviceConfirmed ? (
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <Text className="operations-editor-help-text">
                                            {skipsAreaSelection
                                                ? 'Pick the robot that will run this scan mission.'
                                                : 'Pick the robot for this mission. As soon as you choose one, the pattern step opens.'}
                                        </Text>
                                        {selectedDeviceMissingExpectedCapability ? (
                                            <Alert
                                                type="error"
                                                showIcon
                                                message={`${selectedDevice?.label || 'Selected robot'} is missing required capabilities.`}
                                                description={`Required for this environment: ${formatCapabilityList(expectedCapabilityKeys)}.`}
                                            />
                                        ) : null}
                                        {filteredDevicesForEnvironment.length > 0 ? (
                                            <Row gutter={[16, 16]}>
                                                {filteredDevicesForEnvironment.map((device) => {
                                                    const isSelected = editorDeviceId === device.id;
                                                    const capabilityKeys = getDeviceCapabilityKeys(device);
                                                    const missingCapabilities = expectedCapabilityKeys.filter((key) => !capabilityKeys.has(key));
                                                    return (
                                                        <Col xs={24} md={12} key={device.id}>
                                                            <button
                                                                type="button"
                                                                className={`operations-workflow-select-card${isSelected ? ' operations-workflow-select-card-active' : ''}`}
                                                                onClick={() => handleSelectDevice(device.id)}
                                                            >
                                                                <div className="operations-workflow-select-card-header">
                                                                    <div className="operations-workflow-select-card-identity">
                                                                        <Text strong title={device.label}>{device.label}</Text>
                                                                        <div>
                                                                            <Text type="secondary" title={device.deviceType?.name || 'No device type'}>
                                                                                {device.deviceType?.name || 'No device type'}
                                                                            </Text>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="operations-workflow-select-card-meta">
                                                                    <Text type="secondary">
                                                                        Modules: {device.modules?.length || 0}
                                                                    </Text>
                                                                    <Text type={missingCapabilities.length > 0 ? 'danger' : 'secondary'}>
                                                                        {expectedCapabilityKeys.length > 0
                                                                            ? missingCapabilities.length > 0
                                                                                ? `Missing ${formatCapabilityList(missingCapabilities)}`
                                                                                : `Has ${formatCapabilityList(expectedCapabilityKeys)}`
                                                                            : `${capabilityKeys.size} capabilities`}
                                                                    </Text>
                                                                </div>
                                                            </button>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        ) : (
                                            <Empty
                                                description={expectedCapabilityKeys.length > 0
                                                    ? `No robots match: ${formatCapabilityList(expectedCapabilityKeys)}`
                                                    : 'No robots available'}
                                            />
                                        )}
                                    </Space>
                                ) : (
                                    <div className="operations-workflow-confirmed">
                                        <div>
                                            <Text strong>{selectedDevice?.label}</Text>
                                            <br />
                                            <Text type="secondary">
                                                {selectedDevice?.deviceType?.name || 'Robot confirmed'}
                                            </Text>
                                        </div>
                                        <Tag color="geekblue">Confirmed</Tag>
                                    </div>
                                )}
                            </Card>
                        ) : null}

                        {needsModuleStep ? (
                            <Card
                                className="operations-editor-card"
                                title={moduleStepTitle}
                                extra={moduleStepConfirmed ? (
                                    <Button type="link" onClick={() => setExpandedWorkflowStep('modules')}>
                                        {workflowChangeLabel}
                                    </Button>
                                ) : null}
                            >
                                {expandedWorkflowStep === 'modules' || !moduleStepConfirmed ? (
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <div className="operations-module-execution-card">
                                            <Space direction="vertical" size={4}>
                                                <Space wrap size={8}>
                                                    <Text strong>Module execution order</Text>
                                                    <Tag color={oscarModuleExecutionMode === 'parallel' ? 'green' : 'gold'}>
                                                        {oscarModuleExecutionMode === 'parallel' ? 'Parallel' : 'Series'}
                                                    </Tag>
                                                </Space>
                                                <Text type="secondary">
                                                    {enabledOscarModuleSettings.length} enabled module{enabledOscarModuleSettings.length === 1 ? '' : 's'} for each measure point
                                                </Text>
                                            </Space>
                                            <Row gutter={[12, 12]} className="operations-module-execution-help">
                                                <Col xs={24} md={12}>
                                                    <button
                                                        type="button"
                                                        className={oscarModuleExecutionMode === 'parallel' ? 'operations-module-mode-card-active' : 'operations-module-mode-card'}
                                                        aria-pressed={oscarModuleExecutionMode === 'parallel'}
                                                        onClick={() => {
                                                            setOscarModuleExecutionMode('parallel');
                                                            setModuleStepConfirmed(false);
                                                        }}
                                                    >
                                                        <Space direction="vertical" size={6}>
                                                            <Text strong>Parallel</Text>
                                                            <Text type="secondary">
                                                                All enabled modules trigger together at the same mission point. This is the default and keeps collection time shorter.
                                                            </Text>
                                                        </Space>
                                                    </button>
                                                </Col>
                                                <Col xs={24} md={12}>
                                                    <button
                                                        type="button"
                                                        className={oscarModuleExecutionMode === 'series' ? 'operations-module-mode-card-active' : 'operations-module-mode-card'}
                                                        aria-pressed={oscarModuleExecutionMode === 'series'}
                                                        onClick={() => {
                                                            setOscarModuleExecutionMode('series');
                                                            setModuleStepConfirmed(false);
                                                        }}
                                                    >
                                                        <Space direction="vertical" size={6}>
                                                            <Text strong>Series</Text>
                                                            <Text type="secondary">
                                                                Modules trigger one after another at the same mission point. Use this when one sensor must finish before the next starts.
                                                            </Text>
                                                        </Space>
                                                    </button>
                                                </Col>
                                            </Row>
                                        </div>

                                        {editorPatternId && enabledOscarModuleSettings.length === 0 ? (
                                            <Alert
                                                type="info"
                                                showIcon
                                                message="No modules enabled"
                                                description="Pattern measure points will stop, wait 5 seconds, then continue."
                                            />
                                        ) : null}

                                        {deviceModules.length === 0 ? (
                                            <Empty description="No modules installed on this robot" />
                                        ) : (
                                            <Collapse
                                                bordered={false}
                                                items={deviceModules.map((module, index) => {
                                                    const moduleKey = getDeviceModuleKey(module, index);
                                                    const setting = oscarModuleSettings[moduleKey] || {
                                                        moduleKey,
                                                        instanceKey: module.instanceKey ?? null,
                                                        enabled: true,
                                                        label: getDeviceModuleLabel(module, moduleKey),
                                                        moduleDefinitionId: module.moduleDefinitionId ?? module.moduleDefinition?.id ?? null,
                                                        moduleDefinitionKey: module.moduleDefinition?.key ?? null,
                                                        commandName: getDeviceModuleDefaultCommandName(module, moduleKey),
                                                        commandSpecKey: getDeviceModuleDefaultCommandSpecKey(module),
                                                        config: getDeviceModuleDefaultConfig(module),
                                                    };
                                                    return {
                                                        key: moduleKey,
                                                        label: (
                                                            <Space>
                                                                <Switch
                                                                    checked={setting.enabled}
                                                                    onClick={(checked, event) => {
                                                                        event.stopPropagation();
                                                                        handleToggleOscarModule(moduleKey, checked);
                                                                    }}
                                                                />
                                                                <Text strong>{setting.label}</Text>
                                                                <Tag>{module.moduleDefinition?.key || moduleKey}</Tag>
                                                            </Space>
                                                        ),
                                                        children: setting.enabled ? (
                                                            <ModuleConfigEditor
                                                                schema={module.moduleDefinition?.configSchema || null}
                                                                defaultValue={getDeviceModuleDefaultConfig(module)}
                                                                value={setting.config}
                                                                footerText="These defaults are copied into each generated DO command. Editing one DO later only changes that point."
                                                                onChange={(config) => handleOscarModuleConfigChange(moduleKey, config)}
                                                            />
                                                        ) : (
                                                            <Text type="secondary">Disabled for this mission</Text>
                                                        ),
                                                    };
                                                })}
                                            />
                                        )}

                                        <Space wrap>
                                            <Button onClick={() => recalculateOscarModuleCommands(false)}>
                                                Recalculate commands
                                            </Button>
                                            <Button onClick={() => recalculateOscarModuleCommands(true)}>
                                                Reset command configs
                                            </Button>
                                            <Button type="primary" onClick={handleConfirmOscarModuleSettings}>
                                                Confirm modules
                                            </Button>
                                        </Space>
                                    </Space>
                                ) : (
                                    <div className="operations-workflow-confirmed">
                                        <div>
                                            <Text strong>{enabledOscarModuleSettings.length} module{enabledOscarModuleSettings.length === 1 ? '' : 's'} enabled</Text>
                                            <br />
                                            <Text type="secondary">
                                                Execution order: {oscarModuleExecutionMode === 'parallel' ? 'Parallel - modules run together' : 'Series - modules run one after another'}
                                            </Text>
                                        </div>
                                        <Tag color="geekblue">Confirmed</Tag>
                                    </div>
                                )}
                            </Card>
                        ) : null}

                        {isDeviceConfirmed && isModuleConfigurationConfirmed && !isSlamScan ? (
                            <Card
                                className="operations-editor-card"
                                title={patternStepTitle}
                                extra={isPatternSelectionConfirmed ? (
                                    <Button type="link" onClick={() => setExpandedWorkflowStep('pattern')}>
                                        {workflowChangeLabel}
                                    </Button>
                                ) : null}
                            >
                                {expandedWorkflowStep === 'pattern' || !isPatternSelectionConfirmed ? (
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        <Text className="operations-editor-help-text">
                                            Choose a pattern for this area, or continue with a manual route. Each card shows the pattern projected onto the GPS satellite view of this area. Click a card to confirm it.
                                        </Text>
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} sm={12} lg={8}>
                                                <button
                                                    type="button"
                                                    className={`operations-pattern-option${isPatternSelectionConfirmed && !selectedPattern ? ' operations-pattern-option-active' : ''}`}
                                                    onClick={handleCreateManualRoute}
                                                >
                                                    <div className="operations-pattern-option-empty">
                                                        <Text strong>Manual route</Text>
                                                        <Text type="secondary">Draw a route, save it as a reusable pattern by default, or keep it only in this mission.</Text>
                                                    </div>
                                                </button>
                                            </Col>

                                            {patterns.map((pattern) => {
                                                const isSelected = editorPatternId === pattern.id && isPatternSelectionConfirmed;
                                                const previewSourcePoints = patternPreviewPointsById.get(pattern.id) || [];
                                                const gpsData = patternGpsPointsById.get(pattern.id);
                                                const unresolvedPreviewCount = isIndoorOrMixedMission
                                                    ? Math.max(0, previewSourcePoints.length - (gpsData?.points.length || 0))
                                                    : 0;

                                                return (
                                                    <Col xs={24} sm={12} lg={8} key={pattern.id}>
                                                        <div className={`operations-pattern-option${isSelected ? ' operations-pattern-option-active' : ''}`}>
                                                            <div className="operations-pattern-card-map">
                                                                <PatternPointMapCard
                                                                    areaName={selectedAreaLabel || undefined}
                                                                    areaGeometry={(operationMapGeometry as any) || null}
                                                                    patternCoordSystem="gps"
                                                                    patternPoints={gpsData?.points || []}
                                                                    initialBearing={gpsData?.bearing ?? 0}
                                                                    templateImageUrl={areaTemplateUrl || null}
                                                                    templateCorners={areaTemplateCorners}
                                                                />
                                                            </div>
                                                            <div className="operations-pattern-option-body">
                                                                <Text strong>{pattern.name}</Text>
                                                                <Text type="secondary">
                                                                    {pattern.patternType || 'Pattern'}
                                                                </Text>
                                                                <Space wrap size={8}>
                                                                    <Tag>{pattern.numPoints ?? previewSourcePoints.length} pts</Tag>
                                                                    {pattern.isGlobal ? <Tag color="gold">Global</Tag> : null}
                                                                    {unresolvedPreviewCount > 0 ? (
                                                                        <Tag color="red">{unresolvedPreviewCount} unresolved</Tag>
                                                                    ) : null}
                                                                </Space>
                                                                {unresolvedPreviewCount > 0 ? (
                                                                    <Text type="danger" style={{ fontSize: 12 }}>
                                                                        Local projection needs this area's 4-corner local geometry and SLAM placement.
                                                                    </Text>
                                                                ) : null}
                                                                <Button
                                                                    type={isSelected ? 'default' : 'primary'}
                                                                    size="small"
                                                                    block
                                                                    onClick={() => handleConfirmPatternSelection(pattern)}
                                                                >
                                                                    {isSelected ? '✓ Selected' : 'Select pattern'}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>

                                        {!patternsLoading && patterns.length === 0 ? (
                                            <div className="operations-workflow-selection-meta">
                                                <Empty description="No saved patterns are available for this area yet." />
                                                <Button onClick={handleCreateManualRoute}>
                                                    Create manual route
                                                </Button>
                                            </div>
                                        ) : null}
                                    </Space>
                                ) : (
                                    <div className="operations-workflow-confirmed">
                                        <div>
                                            <Text strong>{selectedPattern?.name || 'Manual route'}</Text>
                                            <br />
                                            <Text type="secondary">
                                                {selectedPattern
                                                    ? `${selectedPattern.numPoints ?? patternPoints.length} points available`
                                                    : 'No pattern attached to this mission'}
                                            </Text>
                                        </div>
                                        <Tag color={selectedPattern ? 'purple' : 'default'}>
                                            {selectedPattern ? 'Pattern selected' : 'Manual'}
                                        </Tag>
                                    </div>
                                )}
                            </Card>
                        ) : null}

                        {showMissionDraftSections ? (
                            <Card
                                className="operations-editor-card"
                                title={missionDetailsStepTitle}
                            >
                                <div className="operations-workflow-confirmed">
                                    <div>
                                        <Text strong>{generatedMissionMetadata.name}</Text>
                                        <br />
                                        <Text type="secondary">{generatedMissionMetadata.description}</Text>
                                    </div>
                                    <Tag color="blue">Auto-filled</Tag>
                                </div>
                            </Card>
                        ) : null}

                        {/* Scan mission: replace route builder with scanning instructions */}
                        {showScanMissionInstructions ? (
                            <Card
                                className="operations-editor-card operations-scan-instructions-card"
                                title={scanInstructionsStepTitle}
                            >
                                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    <Alert
                                        type="warning"
                                        showIcon
                                        message="This is a map scanning mission — no route to build"
                                        description="The robot builds a 2D SLAM map. The field operator can guide it manually or let it explore autonomously."
                                    />

                                    <div className="operations-scan-compact-grid">
                                        <div>
                                            <Text className="operations-editor-field-label">Mission summary</Text>
                                            <div className="operations-editor-chip-row operations-scan-chip-row">
                                                <Tag color="blue">{selectedAreaLabel || 'Area'}</Tag>
                                                <Tag color="geekblue">{selectedDevice?.label || 'Robot'}</Tag>
                                                <Tag color="orange">Map scanning (SLAM)</Tag>
                                                <Tag color={scanMode === 'manual' ? 'purple' : 'green'}>
                                                    {scanMode === 'manual' ? 'Manual drive' : 'Autonomous exploration'}
                                                </Tag>
                                            </div>
                                        </div>

                                        <div>
                                            <Text className="operations-editor-field-label">Mapping mode</Text>
                                            <Select
                                                value={scanMode}
                                                options={SCAN_MODE_OPTIONS}
                                                onChange={(value) => setScanMode(value as ScanMode)}
                                                style={{ width: '100%', marginTop: 6 }}
                                            />
                                        </div>

                                        <div>
                                            <Text className="operations-editor-field-label">Field steps</Text>
                                            <ol className="operations-scan-step-list">
                                                <li>Deploy the robot at the start position.</li>
                                                <li>{scanMode === 'manual' ? 'Drive manually while watching frontier progress.' : 'Let the robot explore autonomously.'}</li>
                                                <li>Cover walls, corridors, rooms, and boundaries.</li>
                                                <li>{scanMode === 'manual' ? 'Press Finish Scan on the robot console when the map is complete.' : 'The robot finishes when frontier coverage is complete and the map is saved.'}</li>
                                                <li>The generated map is uploaded and linked as a SLAM map for this area.</li>
                                            </ol>
                                        </div>
                                    </div>

                                    <Alert
                                        type="info"
                                        showIcon
                                        message="AprilTag anchors improve localisation accuracy"
                                        description="After saving, download the marker PDF from the mission detail page, print it at 100% scale, and mount it flat at robot camera height before scanning."
                                    />

                                    <div className="operations-scan-save-row">
                                        <Button onClick={onCancel}>Cancel</Button>
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined />}
                                            onClick={() => void handleSave()}
                                            loading={saving}
                                        >
                                            Save mission
                                        </Button>
                                    </div>
                                </Space>
                            </Card>
                        ) : null}

                        {showMissionDraftSections && !isSlamScan ? (
                            <Card
                                className="operations-editor-card operations-editor-planner-card"
                                title="6. Build route"
                            >
                                <div className="operations-editor-route-setup">
                                    {/* Environment-aware banners and SLAM map selector */}
                                    {editorOperationEnvironment === 'indoor' && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            message="Indoor mission — local navigation only"
                                            description="GPS is not available for this mission. The robot navigates using SLAM maps and local coordinates. The satellite map is shown for reference only — mission points use local X/Y coordinates relative to the selected SLAM map's origin."
                                            style={{ marginBottom: 16 }}
                                        />
                                    )}
                                    {editorOperationEnvironment === 'mixed' && (
                                        <Alert
                                            type="info"
                                            showIcon
                                            message="Mixed environment — indoor + outdoor segments"
                                            description="This mission crosses both GPS-covered and indoor areas. Attach geo-anchored SLAM maps for the indoor segments so they can be overlaid on the satellite view."
                                            style={{ marginBottom: 16 }}
                                        />
                                    )}

                                    {/* SLAM map selector (indoor and mixed only) */}
                                    {(editorOperationEnvironment === 'indoor' || editorOperationEnvironment === 'mixed') && (
                                        <Card
                                            size="small"
                                            title="SLAM maps for this mission"
	                                            style={{ marginBottom: 16 }}
	                                            extra={
	                                                areaSlamMapsLoading ? <Spin size="small" /> : (
	                                                    <Tag color={confirmedSlamMapIds.length > 0 ? 'blue' : 'default'}>
	                                                        {confirmedSlamMapIds.length} selected
	                                                    </Tag>
	                                                )
	                                            }
	                                        >
	                                            {areaSlamMaps.length === 0 && !areaSlamMapsLoading ? (
	                                                <Alert
	                                                    type="info"
	                                                    showIcon
	                                                    message="No SLAM map is attached to this area"
	                                                    description={`Attach a SLAM map directly to "${selectedAreaLabel || 'this area'}" before using it for an indoor or mixed mission.`}
	                                                />
	                                            ) : (
	                                                <Row gutter={[10, 10]}>
	                                                    {areaSlamMaps.map((slamMap) => {
	                                                        const isSelected = confirmedSlamMapIds.includes(slamMap.id);
	                                                        return (
	                                                            <Col xs={24} sm={12} lg={8} key={slamMap.id}>
	                                                                <button
                                                                    type="button"
	                                                                    className={`operations-workflow-select-card${isSelected ? ' operations-workflow-select-card-active' : ''}`}
	                                                                    style={{ width: '100%', textAlign: 'left' }}
	                                                                    onClick={() => {
	                                                                        setConfirmedSlamMapIds((prev) =>
	                                                                            isSelected
	                                                                                ? prev.filter((id) => id !== slamMap.id)
	                                                                                : [...prev, slamMap.id]
                                                                        );
                                                                        if (slamMap.imageStoragePath && !slamMapImageUrls[slamMap.id]) {
                                                                            void loadSlamMapImageUrl(slamMap.id);
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="operations-workflow-select-card-header">
                                                                        <Text strong ellipsis>{slamMap.name}</Text>
                                                                        {isSelected && <Tag color="blue">✓</Tag>}
                                                                    </div>
                                                                    <div className="operations-workflow-select-card-meta">
                                                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                                                            {slamMap.mapType} · {slamMap.metersPerPixel != null ? `${slamMap.metersPerPixel} m/px` : 'No scale'}
                                                                        </Text>
                                                                        {slamMap.geoAnchor && editorOperationEnvironment === 'mixed' ? (
                                                                            <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>Geo-anchored</Tag>
                                                                        ) : null}
                                                                        {slamMap.imageStoragePath && slamMapImageUrls[slamMap.id] ? (
                                                                            <img
                                                                                src={slamMapImageUrls[slamMap.id]}
                                                                                alt={slamMap.name}
                                                                                style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain', marginTop: 4, borderRadius: 4 }}
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                </button>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            )}
                                        </Card>
                                    )}

                                    <div className="operations-editor-route-setup-header">
                                        <div>
                                            <Text className="operations-editor-field-label">Route setup</Text>
                                            <Title level={5} className="operations-editor-route-setup-title">
                                                Build and review the path first
                                            </Title>
                                            <Text className="operations-editor-help-text">
                                                Choose the route behaviour, then review and adjust the path directly on the map.
                                            </Text>
                                        </div>
                                        <Button
                                            icon={<UndoOutlined />}
                                            onClick={handleUndoRouteEdit}
                                            disabled={routeUndoDepth === 0}
                                        >
                                            Undo last edit
                                        </Button>
                                    </div>

                                    <div className="operations-editor-route-settings-list">
                                            <div className="operations-editor-route-setting-row">
                                                <div className="operations-editor-route-setting-copy">
                                                    <Text strong>Navigation strategy</Text>
                                                    <Text type="secondary">Choose whether the robot follows the complete route or plans to each waypoint.</Text>
                                                </div>
                                                <Form.Item name="navigationMode" noStyle>
                                                    <Select
                                                        options={NAVIGATION_MODE_OPTIONS}
                                                        className="operations-editor-route-setting-control"
                                                    />
                                                </Form.Item>
                                            </div>

                                            <div className="operations-editor-route-setting-row">
                                                <div className="operations-editor-route-setting-copy">
                                                    <Text strong>Waypoint heading strategy</Text>
                                                    <Text type="secondary">
                                                        {patternGuidedDirections
                                                            ? 'Use the detected pattern corridor heading for each pattern waypoint.'
                                                            : 'First faces the next point, middle points average the incoming and outgoing directions, and last follows the arrival direction.'}
                                                    </Text>
                                                </div>
                                                <Select
                                                    value={patternGuidedDirections ? 'pattern' : 'automatic'}
                                                    onChange={(value) => setPatternGuidedDirections(value === 'pattern')}
                                                    disabled={draftPointSummary.patternCount === 0}
                                                    options={[
                                                        { value: 'pattern', label: 'Use pattern corridor heading' },
                                                        { value: 'automatic', label: 'Average path direction at waypoints' },
                                                    ]}
                                                    className="operations-editor-route-setting-control"
                                                />
                                            </div>

                                            <div className="operations-editor-route-setting-row">
                                                <div className="operations-editor-route-setting-copy">
                                                    <Text strong>Obstacle policy</Text>
                                                    <Text type="secondary">
                                                        {OBSTACLE_POLICY_OPTIONS.find((option) => option.value === obstaclePolicyMode)?.description}
                                                    </Text>
                                                </div>
                                                <Select
                                                    value={obstaclePolicyMode}
                                                    onChange={(value) => setObstaclePolicyMode(value as ObstaclePolicyMode)}
                                                    options={OBSTACLE_POLICY_OPTIONS}
                                                    optionRender={(option) => (
                                                        <div className="operations-editor-policy-option">
                                                            <Text strong>{option.label}</Text>
                                                            <Text type="secondary">
                                                                {(option.data as (typeof OBSTACLE_POLICY_OPTIONS)[number]).description}
                                                            </Text>
                                                        </div>
                                                    )}
                                                    className="operations-editor-route-setting-control"
                                                />
                                            </div>
                                    </div>

                                    {editorNavigationMode === 'waypoint_goals' && (
                                        <Alert
                                            type="info"
                                            showIcon
                                            message="Waypoint pose-goal mission"
                                            description="The robot will plan independently to each waypoint. Preview connectors show ordering only and will not be saved as an executable route."
                                            style={{ marginBottom: 16 }}
                                        />
                                    )}

                                    <div className="operations-editor-route-points-card">
                                        <div className="operations-editor-route-point-actions">
                                            <div className="operations-editor-route-point-actions-copy">
                                                <Text strong>Route points</Text>
                                                <Text type="secondary">Add the required start and end, plus any checkpoints. New points appear at the current map centre and can be dragged into position.</Text>
                                            </div>
                                            <div className="operations-editor-route-actions">
                                                <Button
                                                    icon={<PlusOutlined />}
                                                    onClick={() => handleAddCustomPoint('start')}
                                                    disabled={!editorAreaId || hasStartPoint}
                                                >
                                                    Start
                                                </Button>
                                                <Button
                                                    icon={<PlusOutlined />}
                                                    onClick={() => handleAddCustomPoint('custom')}
                                                    disabled={!editorAreaId}
                                                >
                                                    Checkpoint
                                                </Button>
                                                <Button
                                                    icon={<PlusOutlined />}
                                                    onClick={() => handleAddCustomPoint('end')}
                                                    disabled={!editorAreaId || hasEndPoint}
                                                >
                                                    End
                                                </Button>
                                            </div>
                                        </div>

                                        <div className="operations-editor-route-validation-list">
                                            {!hasStartPoint ? (
                                                <Alert
                                                    type="error"
                                                    showIcon
                                                    message="No START point created"
                                                    description="Every mission route must have one explicit starting point. Mission saving is disabled until it is added."
                                                    action={(
                                                        <Button size="small" danger onClick={() => handleAddCustomPoint('start')} disabled={!editorAreaId}>
                                                            Add START
                                                        </Button>
                                                    )}
                                                />
                                            ) : null}

                                            {!hasEndPoint ? (
                                                <Alert
                                                    type="error"
                                                    showIcon
                                                    message="No END point created"
                                                    description="Every mission route must have one explicit ending point. Mission saving is disabled until it is added."
                                                    action={(
                                                        <Button size="small" danger onClick={() => handleAddCustomPoint('end')} disabled={!editorAreaId}>
                                                            Add END
                                                        </Button>
                                                    )}
                                                />
                                            ) : null}

                                            {routeFallbackWarningCount > 0 ? (
                                                <Alert
                                                    type="warning"
                                                    showIcon
                                                    message={`${routeFallbackWarningCount} route segment${routeFallbackWarningCount === 1 ? '' : 's'} fell back to straight lines`}
                                                    description="Adjust the affected route points if the straight fallback does not match the intended path."
                                                />
                                            ) : null}

                                            {invalidAreaPathIndexes.length > 0 ? (
                                                <Alert
                                                    type="error"
                                                    showIcon
                                                    message={`${invalidAreaPathIndexes.length} route segment${invalidAreaPathIndexes.length === 1 ? '' : 's'} leave the selected area`}
                                                    description="The affected paths are shown in red. Move the route points until every segment stays inside the boundary; mission saving is disabled until then."
                                                />
                                            ) : null}
                                        </div>
                                    </div>

                                </div>

                                <Row gutter={[20, 20]} className="operations-editor-planner-layout">
                                    <Col xs={24} xl={15}>
                                        <div className="operations-editor-planner-panel operations-editor-planner-panel-map">
                                            <div className="operations-editor-planner-pane-header">
                                                <div>
                                                    <Text className="operations-editor-field-label">
                                                        Route preview
                                                    </Text>
	                                                    <Text className="operations-editor-help-text">
	                                                        {isIndoorOrMixedMission && routePreviewMode === 'local'
	                                                            ? 'Select a point on the local SLAM map to edit it. Pattern points use local coordinates first.'
	                                                            : 'Select a point on the map to edit it. Custom points can be dragged directly on the map.'}
	                                                    </Text>
                                                </div>
                                            </div>

	                                            <div className="operations-editor-map-toolbar">
	                                                <div className="operations-editor-map-legend">
	                                                    {isIndoorOrMixedMission ? (
	                                                        <Select
	                                                            size="small"
	                                                            value={routePreviewMode}
	                                                            style={{ minWidth: 170 }}
	                                                            options={[
	                                                                { value: 'local', label: 'Local Coordinates' },
	                                                                { value: 'mapbox', label: 'Mapbox Map', disabled: !canProjectLocalMissionToGps },
	                                                            ]}
	                                                            onChange={(value: MissionRoutePreviewMode) => setRoutePreviewMode(value)}
	                                                        />
	                                                    ) : null}
	                                                    <Tag color="blue" style={{ margin: 0 }}>Radius-compliant path</Tag>
	                                                    <Tag color="red" style={{ margin: 0 }}>Warning / outside area</Tag>
		                                                    {areaTemplateUrl ? (
		                                                        <Space size={6} align="center" style={{ marginLeft: 8 }}>
		                                                            <Switch
		                                                                size="small"
                                                                checked={showRoutePreviewTemplate}
                                                                onChange={setShowRoutePreviewTemplate}
                                                            />
		                                                            <Text style={{ fontSize: 12 }}>Template overlay</Text>
		                                                        </Space>
		                                                    ) : null}
                                                </div>
                                                <Text type="secondary" className="operations-editor-map-toolbar-note">
                                                    {draftPointSummary.total > 0
                                                        ? `${draftPointSummary.mappedCount} of ${draftPointSummary.total} point${draftPointSummary.total === 1 ? '' : 's'} placed on the map`
                                                        : 'Import a pattern or add a start and end point to begin'}
                                                </Text>
	                                            </div>

	                                            <div className="operations-map-wrapper operations-map-wrapper-editor">
	                                                {isIndoorOrMixedMission && routePreviewMode === 'local' ? (
	                                                    localRoutePreviewImageUrl ? (
	                                                        <LocalRoutePreview
	                                                            imageUrl={localRoutePreviewImageUrl}
	                                                            imageName={selectedAreaSlamMap?.name || 'SLAM map'}
			                                                            imageWidth={toFiniteNumber(selectedAreaSlamMap?.imageWidth) || 800}
			                                                            imageHeight={toFiniteNumber(selectedAreaSlamMap?.imageHeight) || 600}
			                                                            points={localRoutePreviewPoints}
		                                                            paths={areaValidatedLocalRoutePreviewPaths}
			                                                            templateImageUrl={showRoutePreviewTemplate ? areaTemplateUrl : null}
			                                                            templateQuadPixels={showRoutePreviewTemplate ? localRouteTemplateQuadPixels : null}
			                                                            focusTemplate={showRoutePreviewTemplate}
			                                                            draggablePointIds={draftPoints.filter((point) => !point.patternPointId).map((point) => point.key)}
			                                                            onPointClick={handleMapPointClick}
			                                                            onPointDragEnd={(pointId, location) => {
			                                                                const local = slamPixelToLocalMeters(location.pixelX, location.pixelY, selectedAreaSlamMap);
			                                                                if (local) {
			                                                                    handleDraftPointLocalLocationChange(String(pointId), local);
			                                                                }
			                                                            }}
			                                                            onViewCenterChange={(location) => {
			                                                                const local = slamPixelToLocalMeters(location.pixelX, location.pixelY, selectedAreaSlamMap);
			                                                                if (local) {
			                                                                    setLocalPreviewCenter((current) => (
			                                                                        current
			                                                                        && Math.abs(current.x - local.x) < 0.000001
			                                                                        && Math.abs(current.y - local.y) < 0.000001
			                                                                            ? current
			                                                                            : local
			                                                                    ));
			                                                                }
			                                                            }}
			                                                        />
	                                                    ) : (
	                                                        <Alert
	                                                            type="info"
	                                                            showIcon
	                                                            message="No SLAM map image available"
	                                                            description="Attach a SLAM map with an image to preview the mission in local coordinates."
	                                                        />
	                                                    )
	                                                ) : (
	                                                    <>
	                                                        {isIndoorOrMixedMission && !canProjectLocalMissionToGps ? (
	                                                            <Alert
	                                                                type="warning"
	                                                                showIcon
	                                                                style={{ marginBottom: 12 }}
	                                                                message="Mapbox preview needs SLAM placement"
	                                                                description="Set the SLAM map placement and rotation for this area before previewing local mission points on GPS."
	                                                            />
	                                                        ) : null}
	                                                        <AreaMap
	                                                            mapKey={editorAreaId || 'mission-editor-area'}
	                                                            areaGeometry={operationMapGeometry}
	                                                            areaName={selectedAreaForEditor ? areaPathMap.get(selectedAreaForEditor.id) || selectedAreaForEditor.name : null}
	                                                            allAreas={operationMapAreas}
	                                                            points={editorPreviewPoints}
	                                                            paths={areaValidatedEditorPreviewPaths}
	                                                            onPointClick={handleMapPointClick}
	                                                            onViewStateChange={setPreviewMapCenter}
	                                                            draggablePointIds={draftPoints.filter((point) => !point.patternPointId).map((point) => point.key)}
	                                                            onPointDragEnd={(pointId, location) => {
	                                                                handleDraftPointLocationChange(String(pointId), location);
	                                                            }}
	                                                            alwaysShowMap
	                                                            emptyOverlayMessage="Select a field or add mission points to preview the route on the map."
	                                                            templateImageUrl={showRoutePreviewTemplate ? (areaTemplateUrl || null) : null}
	                                                            initialBearing={editorPatternId ? (patternGpsPointsById.get(editorPatternId)?.bearing ?? null) : null}
	                                                        />
	                                                    </>
	                                                )}
	                                            </div>
                                        </div>
                                    </Col>

                                    <Col xs={24} xl={9}>
                                        <div className="operations-editor-planner-panel operations-editor-planner-panel-sidebar">
                                            <div className="operations-editor-planner-pane-header">
                                                <div>
                                                    <Text className="operations-editor-field-label">
                                                        {activePoint
                                                            ? `Selected point: ${getMissionPointMarkerLabel(draftPoints, activePoint, activePointIndex)}`
                                                            : 'Selected point'}
                                                    </Text>
                                                    <Text className="operations-editor-help-text">
                                                        Edit the point label, coordinates, direction, and whether it is required. Configure its behaviour in Commands below the map.
                                                    </Text>
                                                </div>
                                                {activePoint ? (
                                                    <Button type="link" onClick={clearActivePoint}>
                                                        Clear selection
                                                    </Button>
                                                ) : null}
                                            </div>

                                            {activePoint ? (
                                                <>
                                                    <div className="operations-editor-chip-row">
                                                        <Tag color={activePoint.patternPointId ? 'blue' : 'default'}>
                                                            {activePoint.patternPointId ? `Pattern #${activePoint.patternPointId}` : 'Custom point'}
                                                        </Tag>
                                                        <Tag color={activePoint.isRequired ? 'green' : 'default'}>
                                                            {activePoint.isRequired ? 'Required' : 'Optional'}
                                                        </Tag>
                                                    </div>

                                                    <Space wrap>
                                                        <Button
                                                            icon={<PlusOutlined />}
                                                            onClick={() => handleInsertCheckpointRelative(activePointIndex, 'before')}
                                                        >
                                                            Add before
                                                        </Button>
                                                        <Button
                                                            icon={<PlusOutlined />}
                                                            onClick={() => handleInsertCheckpointRelative(activePointIndex, 'after')}
                                                        >
                                                            Add after
                                                        </Button>
                                                    </Space>

                                                    <Row gutter={[12, 12]} className="operations-editor-inspector-grid">
                                                        <Col xs={24}>
                                                            <div className="operations-editor-field">
                                                                <Text className="operations-editor-field-label">Label</Text>
                                                                <Input
                                                                    value={activePoint.label}
                                                                    onChange={(event) => handleDraftPointChange(activePointIndex, { label: event.target.value })}
                                                                    placeholder={`Step ${activePointIndex + 1}`}
                                                                />
                                                            </div>
                                                        </Col>

                                                        <Col xs={24} md={12} xl={12}>
                                                            <div className="operations-editor-field">
                                                                <Text className="operations-editor-field-label">
                                                                    {isIndoorOrMixedMission ? 'Local X (m)' : 'Latitude'}
                                                                </Text>
                                                                <InputNumber
                                                                    value={isIndoorOrMixedMission ? activePointLocal?.x : activePoint.lat ?? undefined}
                                                                    onChange={(value) => {
                                                                        if (isIndoorOrMixedMission) {
                                                                            handleDraftPointLocalLocationChange(activePoint.key, {
                                                                                x: Number(value ?? activePointLocal?.x ?? 0),
                                                                                y: activePointLocal?.y ?? 0,
                                                                            });
                                                                            return;
                                                                        }
                                                                        handleDraftPointChange(activePointIndex, { lat: value ?? null });
                                                                    }}
                                                                    style={{ width: '100%' }}
                                                                    precision={6}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col xs={24} md={12} xl={12}>
                                                            <div className="operations-editor-field">
                                                                <Text className="operations-editor-field-label">
                                                                    {isIndoorOrMixedMission ? 'Local Y (m)' : 'Longitude'}
                                                                </Text>
                                                                <InputNumber
                                                                    value={isIndoorOrMixedMission ? activePointLocal?.y : activePoint.lng ?? undefined}
                                                                    onChange={(value) => {
                                                                        if (isIndoorOrMixedMission) {
                                                                            handleDraftPointLocalLocationChange(activePoint.key, {
                                                                                x: activePointLocal?.x ?? 0,
                                                                                y: Number(value ?? activePointLocal?.y ?? 0),
                                                                            });
                                                                            return;
                                                                        }
                                                                        handleDraftPointChange(activePointIndex, { lng: value ?? null });
                                                                    }}
                                                                    style={{ width: '100%' }}
                                                                    precision={6}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col xs={24} md={12} xl={12}>
                                                            <div className="operations-editor-field">
                                                                <Text className="operations-editor-field-label">Wait (s)</Text>
                                                                <InputNumber
                                                                    min={0}
                                                                    value={activePoint.waitSeconds ?? undefined}
                                                                    onChange={(value) => handleDraftPointChange(activePointIndex, { waitSeconds: value ?? null })}
                                                                    style={{ width: '100%' }}
                                                                />
                                                            </div>
                                                        </Col>
                                                        <Col xs={24} md={12} xl={12}>
                                                            <div className="operations-editor-field">
                                                                <Text className="operations-editor-field-label">Speed</Text>
                                                                <InputNumber
                                                                    min={0}
                                                                    step={0.1}
                                                                    value={activePoint.speedOverride ?? undefined}
                                                                    onChange={(value) => handleDraftPointChange(activePointIndex, { speedOverride: value ?? null })}
                                                                    style={{ width: '100%' }}
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <div className="operations-editor-inspector-footer">
                                                        <Space>
                                                            <Text className="operations-editor-field-label">Required</Text>
                                                            <Switch
                                                                checked={activePoint.isRequired}
                                                                onChange={(checked) => handleDraftPointChange(activePointIndex, { isRequired: checked })}
                                                            />
                                                        </Space>
                                                        <Button
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => handleRemoveDraftPoint(activePointIndex)}
                                                        >
                                                            Remove point
                                                        </Button>
                                                    </div>

                                                    <Collapse
                                                        ghost
                                                        className="operations-editor-advanced-panel"
                                                        items={[
                                                            {
                                                                key: 'point-routing',
                                                                label: 'Advanced point routing',
                                                                children: (
                                                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                                                        <div className="operations-editor-field">
                                                                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                                                                <div>
                                                                                    <Text className="operations-editor-field-label">Hold outgoing segment straight</Text>
                                                                                    <div>
                                                                                        <Text className="operations-editor-help-text">
                                                                                            Aligns at this point, estimates raw-IMU gyro bias, then makes small corrections around the calibrated steering neutral until the straight section ends.
                                                                                        </Text>
                                                                                    </div>
                                                                                </div>
                                                                                <Switch
                                                                                    checked={activePoint.params?.straightSteeringLocked === true}
                                                                                    disabled={
                                                                                        editorNavigationMode === 'waypoint_goals'
                                                                                        || activePointIndex >= resolvedDraftPoints.length - 1
                                                                                    }
                                                                                    onChange={(checked) => handleDraftPointChange(activePointIndex, {
                                                                                        params: {
                                                                                            ...(activePoint.params || {}),
                                                                                            straightSteeringLocked: checked,
                                                                                        },
                                                                                    })}
                                                                                />
                                                                            </Space>
                                                                        </div>

                                                                        <div className="operations-editor-field">
                                                                            <Text className="operations-editor-field-label">Direction mode</Text>
                                                                            <Select
                                                                                value={activePointDirectionMode ?? undefined}
                                                                                options={activePointDirectionOptions}
                                                                                onChange={(value) => handleDraftPointDirectionModeChange(activePointIndex, value as PointDirectionMode)}
                                                                                style={{ width: '100%' }}
                                                                            />
                                                                            <Text className="operations-editor-help-text">
                                                                                {activePointDirectionMode === 'pattern'
                                                                                    ? 'Uses the detected pattern corridor heading for this point.'
                                                                                    : activePointDirectionMode === 'manual'
                                                                                        ? 'Manual heading overrides automatic routing for this point.'
                                                                                        : activePointIndex === 0
                                                                                            ? 'Auto heading faces the next mission point.'
                                                                                            : activePointIndex === resolvedDraftPoints.length - 1
                                                                                                ? 'Auto heading follows the arrival direction from the previous mission point.'
                                                                                                : 'Auto heading averages the incoming and outgoing path directions.'}
                                                                            </Text>
                                                                        </div>

                                                                        <div className="operations-editor-field">
                                                                            <Text className="operations-editor-field-label">Angle</Text>
                                                                            <InputNumber
                                                                                min={0}
                                                                                max={359.9}
                                                                                step={1}
                                                                                precision={1}
                                                                                value={activePointHeadingDegrees ?? undefined}
                                                                                onChange={(value) => handleDraftPointHeadingChange(activePointIndex, value)}
                                                                                style={{ width: '100%' }}
                                                                                disabled={activePointDirectionMode !== 'manual'}
                                                                                formatter={(value) => (
                                                                                    value === undefined || value === null
                                                                                        ? ''
                                                                                        : `${value}°`
                                                                                )}
                                                                                parser={(value) => Number(String(value ?? '').replace(/[^\d.-]/g, ''))}
                                                                            />
                                                                            <Slider
                                                                                min={0}
                                                                                max={359}
                                                                                step={1}
                                                                                value={Math.round(activePointHeadingDegrees ?? 0)}
                                                                                onChange={(value) => handleDraftPointHeadingChange(activePointIndex, Number(value))}
                                                                                disabled={activePointDirectionMode !== 'manual'}
                                                                                tooltip={{ formatter: (value) => `${value}°` }}
                                                                            />
                                                                            <Text className="operations-editor-help-text">
                                                                                Manual angle constrains how the route enters and leaves this point.
                                                                            </Text>
                                                                        </div>
                                                                    </Space>
                                                                ),
                                                            },
                                                        ]}
                                                    />
                                                </>
                                            ) : (
                                                <div className="operations-editor-empty">
                                                    <Empty
                                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        description="Select a point from the map to edit its action, coordinates, and requirement."
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>

                                {/* Full-width command execution grid — shown below map when a point is selected */}
                                {activePoint ? (
                                    <div className="operations-editor-commands-section">
	                                        <div className="operations-editor-commands-header">
	                                            <div>
	                                                <Text className="operations-editor-field-label">
	                                                    Commands — {getMissionPointMarkerLabel(draftPoints, activePoint, activePointIndex)}
	                                                </Text>
	                                                <Text className="operations-editor-help-text">
	                                                    {isMavlinkCommandMode
	                                                        ? 'MAVLink-compatible devices execute commands as a flat ordered mission list.'
	                                                        : 'Commands on the same row execute simultaneously. Drag cards to reorder or group them.'}
	                                                </Text>
	                                            </div>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {activePointCommands.length} command{activePointCommands.length !== 1 ? 's' : ''}
	                                            </Text>
	                                        </div>
	                                        {isMavlinkCommandMode && activePointCommands.some((command) => {
	                                            const definition = resolveCommand(command.commandName);
	                                            return !definition || definition.mavlinkCode === null || command.commandName === 'nav_pause' || command.commandName === 'nav_continue';
	                                        }) ? (
	                                            <Alert
	                                                type="warning"
	                                                showIcon
	                                                style={{ marginBottom: 12 }}
	                                                message="This point contains OSCAR-only commands. They are not native MAVLink mission items and will export as a lossy projection."
	                                            />
	                                        ) : null}
	                                        <CommandExecutionGrid
                                            pointCommands={activePointCommands}
                                            selectedCommandKey={activeCommandKey}
                                            onCommandSelect={setActiveCommandKey}
                                            onCommandsChange={handlePointCommandsChange}
                                            onAddStep={handleAddCommandForActivePoint}
                                            onAddToRow={handleAddCommandToGroup}
                                            onRemoveCommand={handleRemoveDraftCommand}
	                                            onCommandChange={handleDraftCommandChange}
	                                            commandProtocol={commandProtocol}
	                                            moduleConfigsByKey={commandModuleConfigsByKey}
	                                        />
                                    </div>
                                ) : null}
                            </Card>
                        ) : null}

                        {showMissionDraftSections && !isSlamScan ? (
                            <Card
                                className="operations-editor-card"
                                title="7. Mission overview"
                            >
                                <Row gutter={[20, 20]}>
                                    <Col xs={24} xl={10}>
                                        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                            <div>
                                                <Text className="operations-editor-field-label">Mission</Text>
                                                <Title level={5} style={{ marginBottom: 4 }}>
                                                    {String(editorMissionName || '').trim() || 'Untitled mission'}
                                                </Title>
                                                <Text type="secondary">
                                                    {String(editorMissionDescription || '').trim() || 'Add a short description to document the mission intent and operator notes.'}
                                                </Text>
                                            </div>

                                            <div className="operations-editor-chip-row">
                                                <Tag color={selectedAreaLabel ? 'blue' : 'default'}>
                                                    {selectedAreaLabel || 'Choose a field'}
                                                </Tag>
                                                <Tag color={selectedDevice ? 'geekblue' : 'default'}>
                                                    {selectedDevice?.label || 'Choose a robot'}
                                                </Tag>
                                                <Tag color={selectedPattern ? 'purple' : 'default'}>
                                                    {selectedPattern?.name || 'Manual route'}
                                                </Tag>
                                                <Tag color={editorOperationEnvironment === 'outdoor' ? 'green' : editorOperationEnvironment === 'indoor' ? 'orange' : 'blue'}>
                                                    {OPERATION_ENVIRONMENT_OPTIONS.find((o) => o.value === editorOperationEnvironment)?.label || 'Outdoor (GPS)'}
                                                </Tag>
                                            </div>
                                        </Space>
                                    </Col>

                                    <Col xs={24} xl={14}>
                                        <div className="operations-editor-summary-grid">
                                            <div className="operations-editor-summary-item">
                                                <Text type="secondary">Points</Text>
                                                <Text strong className="operations-editor-summary-value">{draftPointSummary.total}</Text>
                                            </div>
                                            <div className="operations-editor-summary-item">
                                                <Text type="secondary">Mapped</Text>
                                                <Text strong className="operations-editor-summary-value">{draftPointSummary.mappedCount}</Text>
                                            </div>
                                            <div className="operations-editor-summary-item">
                                                <Text type="secondary">Pattern</Text>
                                                <Text strong className="operations-editor-summary-value">{draftPointSummary.patternCount}</Text>
                                            </div>
                                            <div className="operations-editor-summary-item">
                                                <Text type="secondary">Custom</Text>
                                                <Text strong className="operations-editor-summary-value">{draftPointSummary.customCount}</Text>
                                            </div>
                                            <div className="operations-editor-summary-item">
                                                <Text type="secondary">Commands</Text>
                                                <Text strong className="operations-editor-summary-value">{draftPointSummary.commandCount}</Text>
                                            </div>
                                        </div>

                                    </Col>
                                </Row>
                            </Card>
                        ) : null}

                        {showMissionDraftSections && !isSlamScan ? (
                            <Collapse
                                className="operations-editor-step-collapse"
                                items={[
                                    {
                                        key: 'mission-points',
                                        label: `8. Mission points (${draftPointSummary.total})`,
                                        children: (
                                            <Table<MissionPointDraft>
                                                rowKey="key"
                                                className="operations-table"
                                                columns={draftPointColumns}
                                                dataSource={draftPoints}
                                                pagination={false}
                                                size="small"
                                                scroll={{ x: 1180 }}
                                                rowClassName={(record) => (record.key === activePointKey ? 'operations-selected-row' : '')}
                                                onRow={(record) => ({
                                                    onClick: () => setActivePointKey(record.key),
                                                })}
                                                locale={{
                                                    emptyText: (
                                                        <Empty description="No mission points yet. Add start, end, or checkpoint points in step 6." />
                                                    ),
                                                }}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        ) : null}

                        {showMissionDraftSections && !isSlamScan ? (
                            <Card
                                className="operations-editor-card operations-editor-save-card"
                                title="9. Mission details and save"
                            >
                                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Text type="secondary">
                                        Review the final mission metadata. These values can be changed without rebuilding the route.
                                    </Text>

                                    <Row gutter={[16, 0]}>
                                        <Col xs={24} lg={12}>
                                            <Form.Item
                                                label="Mission name"
                                                name="name"
                                                rules={[{ required: true, message: 'Enter a mission name' }]}
                                            >
                                                <Input placeholder="Mission name" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <Form.Item label="Mission type" name="missionType" rules={[{ required: true }]}>
                                                <Select options={MISSION_TYPE_OPTIONS} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} lg={6}>
                                            <Form.Item label="Status" name="status" rules={[{ required: true }]}>
                                                <Select options={MISSION_STATUS_OPTIONS} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item label="Description" name="description">
                                                <Input.TextArea rows={3} placeholder="Mission purpose and operator notes" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Form.Item label="Scheduled start" name="scheduledStartAt">
                                                <DatePicker showTime style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Form.Item label="Turn radius (m)" name="turnRadiusMeters">
                                                <InputNumber min={0.1} step={0.1} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} md={8}>
                                            <Form.Item label="Default speed (m/s)" name="defaultSpeedMps">
                                                <InputNumber min={0.1} step={0.1} style={{ width: '100%' }} placeholder="Device default" />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {hasBlockingRouteValidation ? (
                                        <Alert
                                            type="error"
                                            showIcon
                                            message="The mission route is not ready to save"
                                            description="Return to step 6 and resolve the missing START/END point or any path segment outside the selected area."
                                        />
                                    ) : (
                                        <Alert
                                            type="success"
                                            showIcon
                                            message="Mission ready to save"
                                            description="The route has a START and END point and stays inside the selected area."
                                        />
                                    )}

                                    <div className="operations-editor-final-actions">
                                        <Button onClick={onCancel}>Cancel</Button>
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined />}
                                            onClick={() => void handleSave()}
                                            loading={saving}
                                            disabled={hasBlockingRouteValidation}
                                        >
                                            {mode === 'edit' ? 'Save changes' : 'Save mission'}
                                        </Button>
                                    </div>
                                </Space>
                            </Card>
                        ) : null}
                    </Space>
                </Form>
            </Spin>

            <input
                ref={arduPilotFileInputRef}
                type="file"
                accept=".plan,.waypoints,.txt,.wpl,.json,application/json,text/plain"
                style={{ display: 'none' }}
                onChange={(event) => {
                    void handleArduPilotFileSelected(event);
                }}
            />

            <Modal
                title={patternPreviewPattern?.name || 'Pattern preview'}
                className="operations-pattern-preview-modal"
                open={patternPreviewVisible}
                onCancel={() => {
                    setPatternPreviewVisible(false);
                    setPatternPreviewPatternId(null);
                }}
                onOk={() => {
                    if (patternPreviewPattern) {
                        handleConfirmPatternSelection(patternPreviewPattern);
                    }
                }}
                okText="Use pattern"
                width={1120}
                styles={{ body: { paddingTop: 12 } }}
                destroyOnClose={false}
            >
                {patternPreviewPattern ? (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Text type="secondary" className="operations-pattern-preview-subtitle">
                            Review the selected pattern before attaching it to this mission.
                        </Text>

                        <div className="operations-pattern-preview-summary">
                            <div className="operations-pattern-preview-summary-item">
                                <Text className="operations-pattern-preview-summary-label">Type</Text>
                                <Text strong>{patternPreviewPattern.patternType || 'Pattern'}</Text>
                            </div>
                            <div className="operations-pattern-preview-summary-item">
                                <Text className="operations-pattern-preview-summary-label">Coordinate system</Text>
                                <Text strong>{patternPreviewPattern.coordSystem || 'Unknown'}</Text>
                            </div>
                            <div className="operations-pattern-preview-summary-item">
                                <Text className="operations-pattern-preview-summary-label">Points</Text>
                                <Text strong>{patternPreviewPattern.numPoints ?? patternPreviewPoints.length}</Text>
                            </div>
                            <div className="operations-pattern-preview-summary-item">
                                <Text className="operations-pattern-preview-summary-label">Scope</Text>
                                <Text strong>{patternPreviewPattern.isGlobal ? 'Global' : 'Area'}</Text>
                            </div>
                        </div>

                        {areaTemplateUrl ? (
                            <PatternTemplatePreview
                                templateUrl={areaTemplateUrl}
                                points={patternPreviewGraphicPoints}
                                loading={areaTemplateLoading}
                                expanded
                            />
                        ) : patternPreviewPoints.length > 0 ? (
                            <div className="operations-pattern-preview-map">
                                <PatternPointMapCard
                                    areaName={selectedAreaLabel || undefined}
                                    areaGeometry={(operationMapGeometry as any) || null}
                                    patternCoordSystem={patternPreviewPattern.coordSystem}
                                    patternPoints={patternPreviewPoints}
                                />
                            </div>
                        ) : (
                            <PatternTemplatePreview
                                templateUrl={null}
                                points={[]}
                                loading={areaTemplateLoading}
                                emptyText="No template image is available for this area type."
                                expanded
                            />
                        )}
                    </Space>
                ) : (
                    <Spin />
                )}
            </Modal>

            {/* Inline SLAM map upload modal */}
            <Modal
                title="Upload SLAM map for this area"
                open={slamMapUploadVisible}
                onCancel={() => setSlamMapUploadVisible(false)}
                onOk={() => void handleInlineUploadSlamMap(slamUploadName, slamUploadMapType, slamUploadScale, slamUploadFile)}
                okText="Upload"
                okButtonProps={{ disabled: !slamUploadName.trim(), loading: slamMapUploadSaving }}
                destroyOnClose
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text type="secondary">
                        Upload a SLAM map image and link it to <strong>{selectedAreaLabel || 'this area'}</strong>. This map will then be available for selection in indoor/mixed missions.
                    </Text>
                    <div>
                        <Text className="operations-editor-field-label">Map name *</Text>
                        <Input
                            value={slamUploadName}
                            onChange={(e) => setSlamUploadName(e.target.value)}
                            placeholder="e.g. Ground floor east wing"
                        />
                    </div>
                    <div>
                        <Text className="operations-editor-field-label">Map type</Text>
                        <Select
                            value={slamUploadMapType}
                            onChange={setSlamUploadMapType}
                            style={{ width: '100%' }}
                            options={[
                                { value: 'slam_2d', label: 'SLAM 2D (occupancy grid)' },
                                { value: 'floor_plan', label: 'Floor plan (architectural)' },
                                { value: 'cad_overlay', label: 'CAD overlay' },
                            ]}
                        />
                    </div>
                    <div>
                        <Text className="operations-editor-field-label">Scale (metres per pixel)</Text>
                        <InputNumber
                            value={slamUploadScale ?? undefined}
                            onChange={(v) => setSlamUploadScale(v ?? null)}
                            min={0.001}
                            step={0.01}
                            precision={4}
                            style={{ width: '100%' }}
                            placeholder="e.g. 0.05 for 5 cm/px"
                        />
                    </div>
                    <div>
                        <Text className="operations-editor-field-label">Map image (PNG/JPEG/PGM)</Text>
                        <div style={{ marginTop: 8 }}>
                            <label htmlFor="slam-upload-file" style={{ cursor: 'pointer' }}>
                                <Button icon={<UploadOutlined />} onClick={() => document.getElementById('slam-upload-file')?.click()}>
                                    {slamUploadFile ? slamUploadFile.name : 'Choose image file…'}
                                </Button>
                            </label>
                            <input
                                id="slam-upload-file"
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp,.pgm,.ppm"
                                style={{ display: 'none' }}
                                onChange={(e) => setSlamUploadFile(e.target.files?.[0] ?? null)}
                            />
                        </div>
                    </div>
                </Space>
            </Modal>

            <Modal
                open={arduPilotPreviewVisible}
                onCancel={() => setArduPilotPreviewVisible(false)}
                onOk={() => {
                    if (arduPilotPreview) {
                        applyArduPilotPreview(arduPilotPreview);
                    }
                }}
                okText="Apply import"
                width={880}
                destroyOnClose={false}
            >
                {arduPilotPreview ? (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <div>
                            <Title level={4} style={{ marginBottom: 4 }}>Import ArduPilot mission</Title>
                            <Text type="secondary">
                                Review the parsed mission before replacing the current draft in the editor.
                            </Text>
                        </div>

                        {draftPoints.length > 0 || draftCommands.length > 0 ? (
                            <Alert
                                type="warning"
                                showIcon
                                message="Applying this import will replace the current draft points and commands in the editor."
                            />
                        ) : null}

                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Format">
                                {arduPilotPreview.format === 'qgc_plan' ? 'QGroundControl .plan' : 'QGC WPL 110'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mission name">
                                {arduPilotPreview.missionName || 'Not provided'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mission items">
                                {arduPilotPreview.summary.itemCount}
                            </Descriptions.Item>
                            <Descriptions.Item label="Imported points">
                                {arduPilotPreview.summary.pointCount}
                            </Descriptions.Item>
                            <Descriptions.Item label="Imported commands">
                                {arduPilotPreview.summary.commandCount}
                            </Descriptions.Item>
                            <Descriptions.Item label="Warnings">
                                {arduPilotPreview.summary.warningCount}
                            </Descriptions.Item>
                        </Descriptions>

                        {arduPilotPreview.warnings.length > 0 ? (
                            <Alert
                                type="info"
                                showIcon
                                message="Import warnings"
                                description={(
                                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                                        {arduPilotPreview.warnings.slice(0, 8).map((warning, index) => (
                                            <li key={`${warning}-${index}`}>{warning}</li>
                                        ))}
                                    </ul>
                                )}
                            />
                        ) : null}

                        {arduPilotPreview.unsupportedItems.length > 0 ? (
                            <Table
                                size="small"
                                pagination={false}
                                rowKey={(item) => `${item.seq}-${item.commandName}`}
                                columns={[
                                    { title: '#', dataIndex: 'seq', key: 'seq', width: 72 },
                                    { title: 'Item', dataIndex: 'commandName', key: 'commandName' },
                                    { title: 'Reason', dataIndex: 'reason', key: 'reason' },
                                ]}
                                dataSource={arduPilotPreview.unsupportedItems}
                            />
                        ) : null}
                    </Space>
                ) : (
                    <Spin />
                )}
            </Modal>
        </Space>
    );
};

export default MissionEditorView;
